import React, { useMemo, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Square, Diamond, StickyNote, Trash2, Spline, MoveHorizontal, MoveVertical, Eraser, RotateCcw, ClipboardList, ArrowRight, Radio, Layers, Truck, Package, X as XIcon, LayoutGrid, Crosshair, Maximize2 } from 'lucide-react';
import { Card } from './Card';
import { PostItCard } from './PostIt';
import { CardData, PostIt, PostItType, NodeType, Position, IProjectMetrics, ProcessedConnection, Action, PageConnector, Connection } from '../types';
import { getDiamondConnectionPoint } from '../utils/vsmGeometry';
import { useProjectStore } from '../store/useProjectStore';
import { useTheme } from '../context/ThemeContext';
import TimeGrid from './tools/TimeGrid';
import LineGrid from './LineGrid';
import { PageConnectorNode } from './PageConnectorNode';
import ComparisonView from './ComparisonView';
import { GroupingToolbar } from './GroupingToolbar';
import { GroupContainer } from './GroupContainer';
import { groupCardsIntoSubProcess, ungroupSubProcess, isGroupedCard } from '../utils/nestedFlowCalculations';
import type { VsmCanvasProps } from './VsmCanvas/types';
import { useSimulationSafe } from '../context/SimulationContext';
import { getCardDimensions } from './Card/cardUtils';
import { FlowStockBadge, type FlowStockBadgeSeverity } from './FlowStockBadge';
import { VsmTransportEdge, type VsmEdgeType } from './VsmTransportEdge';
import SwimLaneOverlay from './SwimLaneOverlay';
import { isCancelledProgramInfoCard } from '../utils/vsmCommunicationCleanup';
// LeadTimeLadder removed — footer is rendered by MapaPage via LeadTimeLadderFooter

const STOCK_BADGE_MIN_DISTANCE = 12;
const STOCK_BADGE_LINE_OFFSET = 12;
const STOCK_BADGE_MERGE_ENTRY_OFFSET = 10;
const STOCK_BADGE_WARNING_THRESHOLD = 150;
const STOCK_BADGE_CRITICAL_THRESHOLD = 200;

function getCanvasHitBox(card: CardData, isTimeScaleActive: boolean): { width: number; height: number; area: number } {
  const isDecision = card.tipo === 'decisao';
  const { width, height } = getCardDimensions(card, isDecision, isTimeScaleActive);
  const effectiveWidth = isDecision
    ? 160
    : (card.tipo === 'card_inicial' || (card.tipo === 'card_final' && card.layoutType === 'classic'))
    ? 220
    : width;
  const effectiveHeight = isDecision ? 160 : height;
  return { width: effectiveWidth, height: effectiveHeight, area: effectiveWidth * effectiveHeight };
}

function toVsmCycleSeconds(value: unknown, unit: unknown, fallbackUnit: 's' | 'min' | 'h' = 's'): number {
  const numericValue = Number(value) || 0;
  const resolvedUnit = typeof unit === 'string' && unit.length > 0 ? unit : fallbackUnit;
  if (['horas', 'hora', 'h'].includes(resolvedUnit)) return numericValue * 3600;
  if (['minutos', 'minuto', 'min'].includes(resolvedUnit)) return numericValue * 60;
  return numericValue;
}

function getProcessCycleSeconds(card: CardData): number {
  const vsmCT = Number((card as any).vsmCT) || 0;
  if (vsmCT > 0) return toVsmCycleSeconds(vsmCT, (card as any).vsmCTUnit, 's');
  return toVsmCycleSeconds((card as any).tp, (card as any).tpUnit, 'min');
}

function getProcessWorkContentSeconds(card: CardData): number {
  const cycleSeconds = getProcessCycleSeconds(card);
  const availability = Number((card as any).vsmDisp);
  if (Number.isFinite(availability) && availability > 0 && availability < 100) {
    return cycleSeconds / (availability / 100);
  }
  return cycleSeconds;
}

type VsmKaizenMetricKey = 'cycleTime' | 'setup' | 'availability' | 'quality';

type ContinuousFlowCellSummary = {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  rawCycleSeconds: number;
  cycleSeconds: number;
  minCycleSeconds: number;
  displayCycleSeconds: number;
  projectedCycleSeconds?: number;
  leadTimeReductionPercent?: number;
  processCount: number;
  hasLayoutKaizen: boolean;
};

const FLOW_CELL_HEADER_SLOT_COUNT = 7;
const KAIZEN_MARKER_SIZE = 84;

function getCellHeaderAnchorPoint(cell: ContinuousFlowCellSummary, point?: { x: number; y: number } | null) {
  const slotWidth = cell.width / (FLOW_CELL_HEADER_SLOT_COUNT + 1);
  const rawSlot = point
    ? Math.round((point.x - cell.x) / slotWidth)
    : Math.ceil(FLOW_CELL_HEADER_SLOT_COUNT / 2);
  const slot = Math.min(FLOW_CELL_HEADER_SLOT_COUNT, Math.max(1, rawSlot));
  return {
    slot,
    x: cell.x + slotWidth * slot,
    y: cell.y,
  };
}

function getKaizenMetrics(card: CardData): Record<VsmKaizenMetricKey, any> {
  return ((card as any).vsmKaizenProjection?.metrics ?? {}) as Record<VsmKaizenMetricKey, any>;
}

function getProjectedCycleSeconds(card: CardData, override?: any): number {
  const projection = (card as any).vsmKaizenProjection;
  if (projection?.scope === 'flowCell') {
    return Number(
      override?.timeOverrideSeconds
      ?? override?.timeOverride
      ?? override?.projectedValueSeconds
      ?? 0,
    ) || 0;
  }

  return Number(
    override?.timeOverrideSeconds
    ?? override?.timeOverride
    ?? override?.projectedValueSeconds
    ?? getKaizenMetrics(card).cycleTime?.projectedSeconds
    ?? (card as any).vsmKaizenProjection?.projectedSeconds
    ?? 0,
  ) || 0;
}

function getEffectiveProcessCycleSeconds(card: CardData, override?: any): number {
  const baseSeconds = getProcessCycleSeconds(card);
  const projectedSeconds = getProjectedCycleSeconds(card, override);
  if (projectedSeconds > 0) return projectedSeconds;
  const reductionPercent = Math.max(0, Math.min(100, Number(
    override?.ctReduction
    ?? override?.cycleReduction
    ?? override?.rework
    ?? override?.reworkReduction
    ?? (override?.targetMetric !== 'leadTime' ? override?.gainPercent : 0)
    ?? 0,
  ) || 0));
  return baseSeconds * (1 - reductionPercent / 100);
}

function isContinuousFlowProcess(card: CardData): boolean {
  return card.tipo === 'atividade' && (card as any).vsmIcon === 'processo';
}

function isFactoryProcessTarget(card: CardData | null | undefined): boolean {
  if (!card) return false;
  const icon = String((card as any).vsmIcon ?? '');
  const label = String((card as any).atividade ?? (card as any).vsmNome ?? (card as any).titulo ?? (card as any).title ?? '');
  return card.tipo === 'atividade' && (icon === 'processo' || /solda\s*(i|ii)\b|estamparia|montagem|expedi[cç][aã]o/i.test(label));
}

function findCardAtCanvasPoint(cards: CardData[], x: number, y: number, isTimeScaleActive: boolean, excludeId?: string): CardData | null {
  const hitPadding = 28;
  return cards
    .filter(card => card.id !== excludeId && card.status !== 'deletado')
    .map(card => {
      const box = getCanvasHitBox(card, isTimeScaleActive);
      const exactHit = x >= card.posicao.x &&
        x <= card.posicao.x + box.width &&
        y >= card.posicao.y &&
        y <= card.posicao.y + box.height;
      const paddedHit = x >= card.posicao.x - hitPadding &&
        x <= card.posicao.x + box.width + hitPadding &&
        y >= card.posicao.y - hitPadding &&
        y <= card.posicao.y + box.height + hitPadding;
      const centerX = card.posicao.x + box.width / 2;
      const centerY = card.posicao.y + box.height / 2;
      const distance = Math.hypot(x - centerX, y - centerY);
      return { card, box, exactHit, paddedHit, distance };
    })
    .filter(candidate => candidate.paddedHit)
    .sort((a, b) => {
      const processDelta = Number(isFactoryProcessTarget(b.card)) - Number(isFactoryProcessTarget(a.card));
      if (processDelta !== 0) return processDelta;
      const exactDelta = Number(b.exactHit) - Number(a.exactHit);
      if (exactDelta !== 0) return exactDelta;
      return a.distance - b.distance;
    })[0]?.card ?? null;
}

function findProcessCardByViewportPoint(cards: CardData[], clientX: number, clientY: number, excludeId?: string): CardData | null {
  const hitPadding = 42;
  return cards
    .filter(card => card.id !== excludeId && card.status !== 'deletado' && isFactoryProcessTarget(card))
    .map(card => {
      const el = document.querySelector(`[data-card-id="${CSS.escape(card.id)}"]`) as HTMLElement | null;
      if (!el) return null;
      const rect = el.getBoundingClientRect();
      const exactHit = clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
      const paddedHit = clientX >= rect.left - hitPadding && clientX <= rect.right + hitPadding && clientY >= rect.top - hitPadding && clientY <= rect.bottom + hitPadding;
      const distance = Math.hypot(clientX - (rect.left + rect.width / 2), clientY - (rect.top + rect.height / 2));
      return { card, exactHit, paddedHit, distance };
    })
    .filter((candidate): candidate is { card: CardData; exactHit: boolean; paddedHit: boolean; distance: number } => !!candidate && candidate.paddedHit)
    .sort((a, b) => {
      const exactDelta = Number(b.exactHit) - Number(a.exactHit);
      if (exactDelta !== 0) return exactDelta;
      return a.distance - b.distance;
    })[0]?.card ?? null;
}

function getPathMidpoint(
  d: string,
  fallback: { x: number; y: number }
): { x: number; y: number } {
  const segments: Array<{ start: { x: number; y: number }; end: { x: number; y: number }; length: number }> = [];
  const commandRegex = /([ML])\s*(-?\d*\.?\d+)\s*,?\s*(-?\d*\.?\d+)/gi;
  let current: { x: number; y: number } | null = null;
  let match: RegExpExecArray | null;

  while ((match = commandRegex.exec(d)) !== null) {
    const command = match[1];
    const point = { x: Number(match[2]), y: Number(match[3]) };

    if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) continue;

    if (command === 'M' || current === null) {
      current = point;
      continue;
    }

    const dx = point.x - current.x;
    const dy = point.y - current.y;
    const length = Math.hypot(dx, dy);

    if (length > 0) {
      segments.push({ start: current, end: point, length });
    }

    current = point;
  }

  if (segments.length === 0) return fallback;

  const totalLength = segments.reduce((sum, segment) => sum + segment.length, 0);
  if (totalLength <= 0) return fallback;

  const halfway = totalLength / 2;
  let accumulated = 0;

  for (const segment of segments) {
    if (accumulated + segment.length >= halfway) {
      const t = (halfway - accumulated) / segment.length;
      return {
        x: segment.start.x + (segment.end.x - segment.start.x) * t,
        y: segment.start.y + (segment.end.y - segment.start.y) * t,
      };
    }
    accumulated += segment.length;
  }

  return { ...segments[segments.length - 1].end };
}

function pushPointAwayFromAnchor(
  point: { x: number; y: number },
  anchor: { x: number; y: number },
  minDistance: number
): { x: number; y: number } {
  const dx = point.x - anchor.x;
  const dy = point.y - anchor.y;
  const distance = Math.hypot(dx, dy);

  if (distance >= minDistance) {
    return point;
  }

  if (distance === 0) {
    return { x: anchor.x + minDistance, y: anchor.y };
  }

  const scale = minDistance / distance;
  return {
    x: anchor.x + dx * scale,
    y: anchor.y + dy * scale,
  };
}

function getStockBadgePosition(conn: ProcessedConnection): { x: number; y: number } {
  const fallbackMidpoint = {
    x: (conn.sourcePoint.x + conn.targetPoint.x) / 2,
    y: (conn.sourcePoint.y + conn.targetPoint.y) / 2,
  };

  // Centro geom�trico entre origem e destino para evitar badge colado em cantos de segmentos ortogonais
  const midpoint = fallbackMidpoint;
  const pathDx = conn.targetPoint.x - conn.sourcePoint.x;
  const pathDy = conn.targetPoint.y - conn.sourcePoint.y;
  const pathLength = Math.hypot(pathDx, pathDy) || 1;

  // Deslocar badge para fora da linha (preferindo dire��o "acima" no canvas)
  let normalX = -pathDy / pathLength;
  let normalY = pathDx / pathLength;
  if (normalY > 0) {
    normalX = -normalX;
    normalY = -normalY;
  }

  const offsetMidpoint = {
    x: midpoint.x + normalX * STOCK_BADGE_LINE_OFFSET,
    y: midpoint.y + normalY * STOCK_BADGE_LINE_OFFSET,
  };

  const adjustedFromTarget = pushPointAwayFromAnchor(offsetMidpoint, conn.targetPoint, STOCK_BADGE_MIN_DISTANCE);
  return pushPointAwayFromAnchor(adjustedFromTarget, conn.sourcePoint, STOCK_BADGE_MIN_DISTANCE);
}

function getMergeEntryStockBadgePosition(
  conn: ProcessedConnection,
  incomingConnections: ProcessedConnection[]
): { x: number; y: number } {
  const target = conn.targetPoint;
  let directionX = conn.targetPoint.x - conn.sourcePoint.x;
  let directionY = conn.targetPoint.y - conn.sourcePoint.y;

  if (incomingConnections.length > 0) {
    let sumX = 0;
    let sumY = 0;
    let directionCount = 0;

    incomingConnections.forEach((incomingConn) => {
      const dx = incomingConn.targetPoint.x - incomingConn.sourcePoint.x;
      const dy = incomingConn.targetPoint.y - incomingConn.sourcePoint.y;
      const length = Math.hypot(dx, dy);

      if (length > 0) {
        sumX += dx / length;
        sumY += dy / length;
        directionCount += 1;
      }
    });

    if (directionCount > 0) {
      directionX = sumX / directionCount;
      directionY = sumY / directionCount;
    }
  }

  const directionLength = Math.hypot(directionX, directionY) || 1;
  const normalizedX = directionX / directionLength;
  const normalizedY = directionY / directionLength;

  const positionedNearEntry = {
    x: target.x - normalizedX * STOCK_BADGE_MERGE_ENTRY_OFFSET,
    y: target.y - normalizedY * STOCK_BADGE_MERGE_ENTRY_OFFSET,
  };

  return pushPointAwayFromAnchor(positionedNearEntry, target, 5);
}

function buildPushArrowShape(pathD: string, thickness: number): string {
  const match = pathD.match(/M\s*(-?\d+(?:\.\d+)?)\s+(-?\d+(?:\.\d+)?)\s+L\s*(-?\d+(?:\.\d+)?)\s+(-?\d+(?:\.\d+)?)/);
  if (!match) return pathD;

  const startX = Number(match[1]);
  const startY = Number(match[2]);
  const endX = Number(match[3]);
  const endY = Number(match[4]);
  const dx = endX - startX;
  const dy = endY - startY;
  const length = Math.hypot(dx, dy);
  if (length < 1) return pathD;

  const ux = dx / length;
  const uy = dy / length;
  const px = -uy;
  const py = ux;
  const headLength = Math.min(18, Math.max(10, length * 0.42));
  const headHalf = thickness * 0.82;
  const shaftHalf = thickness * 0.38;
  const baseX = endX - ux * headLength;
  const baseY = endY - uy * headLength;

  const points = [
    [startX + px * shaftHalf, startY + py * shaftHalf],
    [baseX + px * shaftHalf, baseY + py * shaftHalf],
    [baseX + px * headHalf, baseY + py * headHalf],
    [endX, endY],
    [baseX - px * headHalf, baseY - py * headHalf],
    [baseX - px * shaftHalf, baseY - py * shaftHalf],
    [startX - px * shaftHalf, startY - py * shaftHalf],
  ];

  return `M ${points.map(([x, y]) => `${x.toFixed(1)} ${y.toFixed(1)}`).join(' L ')} Z`;
}

function getStraightPathEndpoints(pathD: string): { startX: number; startY: number; endX: number; endY: number } | null {
  const match = pathD.match(/M\s*(-?\d+(?:\.\d+)?)\s+(-?\d+(?:\.\d+)?)\s+L\s*(-?\d+(?:\.\d+)?)\s+(-?\d+(?:\.\d+)?)/);
  if (!match) return null;
  return {
    startX: Number(match[1]),
    startY: Number(match[2]),
    endX: Number(match[3]),
    endY: Number(match[4]),
  };
}

function trimStraightPathEnd(pathD: string, trimLength: number): string {
  const endpoints = getStraightPathEndpoints(pathD);
  if (!endpoints) return pathD;
  const { startX, startY, endX, endY } = endpoints;
  const dx = endX - startX;
  const dy = endY - startY;
  const length = Math.hypot(dx, dy);
  if (length <= trimLength + 1) return pathD;

  const ux = dx / length;
  const uy = dy / length;
  const trimmedEndX = endX - ux * trimLength;
  const trimmedEndY = endY - uy * trimLength;
  return `M ${startX.toFixed(1)} ${startY.toFixed(1)} L ${trimmedEndX.toFixed(1)} ${trimmedEndY.toFixed(1)}`;
}

function trimStraightPath(pathD: string, startTrim: number, endTrim: number): string {
  const endpoints = getStraightPathEndpoints(pathD);
  if (!endpoints) return pathD;
  const { startX, startY, endX, endY } = endpoints;
  const dx = endX - startX;
  const dy = endY - startY;
  const length = Math.hypot(dx, dy);
  if (length <= startTrim + endTrim + 1) return pathD;

  const ux = dx / length;
  const uy = dy / length;
  const nextStartX = startX + ux * startTrim;
  const nextStartY = startY + uy * startTrim;
  const nextEndX = endX - ux * endTrim;
  const nextEndY = endY - uy * endTrim;
  return `M ${nextStartX.toFixed(1)} ${nextStartY.toFixed(1)} L ${nextEndX.toFixed(1)} ${nextEndY.toFixed(1)}`;
}

function extendStraightPathEnd(pathD: string, extensionLength: number): string {
  const endpoints = getStraightPathEndpoints(pathD);
  if (!endpoints || extensionLength <= 0) return pathD;
  const { startX, startY, endX, endY } = endpoints;
  const dx = endX - startX;
  const dy = endY - startY;
  const length = Math.hypot(dx, dy);
  if (length <= 1) return pathD;

  const ux = dx / length;
  const uy = dy / length;
  const extendedEndX = endX + ux * extensionLength;
  const extendedEndY = endY + uy * extensionLength;
  return `M ${startX.toFixed(1)} ${startY.toFixed(1)} L ${extendedEndX.toFixed(1)} ${extendedEndY.toFixed(1)}`;
}

function buildMaterialArrowHead(pathD: string, headLength: number, headHalf: number): string | null {
  const endpoints = getStraightPathEndpoints(pathD);
  if (!endpoints) return null;
  const { startX, startY, endX, endY } = endpoints;
  const dx = endX - startX;
  const dy = endY - startY;
  const length = Math.hypot(dx, dy);
  if (length <= headLength + 1) return null;

  const ux = dx / length;
  const uy = dy / length;
  const px = -uy;
  const py = ux;
  const baseX = endX - ux * headLength;
  const baseY = endY - uy * headLength;
  const points = [
    [baseX + px * headHalf, baseY + py * headHalf],
    [endX, endY],
    [baseX - px * headHalf, baseY - py * headHalf],
  ];

  return `M ${points.map(([x, y]) => `${x.toFixed(1)} ${y.toFixed(1)}`).join(' L ')} Z`;
}

function splitStraightPathAroundIntermediateStock(
  pathD: string,
  cards: CardData[],
  sourceId: string | undefined,
  targetId: string | undefined,
  arrowLength: number,
  rightExtensionLength = 0,
): string | null {
  const endpoints = getStraightPathEndpoints(pathD);
  if (!endpoints) return null;
  const { startX, startY, endX, endY } = endpoints;
  const dx = endX - startX;
  const dy = endY - startY;
  if (Math.abs(dx) < 1 || Math.abs(dy) > 2) return null;

  const flowsRight = dx > 0;
  const minX = Math.min(startX, endX);
  const maxX = Math.max(startX, endX);
  const STOCK_W = 104;
  const STOCK_H = 80;
  const STOCK_LEFT_RATIO = 0.25;
  const STOCK_RIGHT_RATIO = 0.75;
  const GAP = 10;
  const ENTRY_SEGMENT_LENGTH = 26;
  const SOURCE_EXIT_GAP = 34;
  const RIGHT_VISIBLE_SHAFT_EXTENSION = 44;

  const stock = cards.find((card) => {
    if (card.id === sourceId || card.id === targetId) return false;
    if (card.tipo !== 'inventory' || (card.vsmIcon ?? '') !== 'estoque') return false;
    const centerX = card.posicao.x + STOCK_W / 2;
    const centerY = card.posicao.y + STOCK_H / 2;
    return centerX > minX && centerX < maxX && Math.abs(centerY - startY) <= 220;
  });
  if (!stock) return null;

  const stockLeft = stock.posicao.x + STOCK_W * STOCK_LEFT_RATIO;
  const stockRight = stock.posicao.x + STOCK_W * STOCK_RIGHT_RATIO;

  if (flowsRight) {
    const leftEnd = stockLeft - GAP;
    const rightStart = stockRight + GAP - RIGHT_VISIBLE_SHAFT_EXTENSION;
    const rightEnd = endX + rightExtensionLength - arrowLength;
    const rightLength = Math.max(0, rightEnd - rightStart);
    const leftStart = Math.max(startX + SOURCE_EXIT_GAP, leftEnd - ENTRY_SEGMENT_LENGTH);
    if (leftEnd <= leftStart || rightEnd <= rightStart) return null;
    return `M ${leftStart.toFixed(1)} ${startY.toFixed(1)} L ${leftEnd.toFixed(1)} ${startY.toFixed(1)} M ${rightStart.toFixed(1)} ${startY.toFixed(1)} L ${rightEnd.toFixed(1)} ${startY.toFixed(1)}`;
  }

  const leftStart = stockLeft - GAP;
  const rightEnd = stockRight + GAP;
  const leftEnd = endX + arrowLength;
  const leftLength = Math.max(0, leftStart - leftEnd);
  const rightStart = Math.min(startX, rightEnd + leftLength);
  if (leftStart <= leftEnd || rightStart <= rightEnd) return null;
  return `M ${rightStart.toFixed(1)} ${startY.toFixed(1)} L ${rightEnd.toFixed(1)} ${startY.toFixed(1)} M ${leftStart.toFixed(1)} ${startY.toFixed(1)} L ${leftEnd.toFixed(1)} ${startY.toFixed(1)}`;
}

function getIntermediateStockRightExtension(
  pathD: string,
  cards: CardData[],
  sourceId: string | undefined,
  targetId: string | undefined,
  arrowLength: number,
): number {
  const endpoints = getStraightPathEndpoints(pathD);
  if (!endpoints) return 0;
  const { startX, startY, endX, endY } = endpoints;
  const dx = endX - startX;
  const dy = endY - startY;
  if (dx <= 0 || Math.abs(dy) > 2) return 0;

  const minX = Math.min(startX, endX);
  const maxX = Math.max(startX, endX);
  const STOCK_W = 104;
  const STOCK_H = 80;
  const STOCK_RIGHT_RATIO = 0.75;
  const GAP = 10;
  const stock = cards.find((card) => {
    if (card.id === sourceId || card.id === targetId) return false;
    if (card.tipo !== 'inventory' || (card.vsmIcon ?? '') !== 'estoque') return false;
    const centerX = card.posicao.x + STOCK_W / 2;
    const centerY = card.posicao.y + STOCK_H / 2;
    return centerX > minX && centerX < maxX && Math.abs(centerY - startY) <= 220;
  });
  if (!stock) return 0;

  const rightStart = stock.posicao.x + STOCK_W * STOCK_RIGHT_RATIO + GAP;
  const rightEnd = endX - arrowLength;
  const rightLength = Math.max(0, rightEnd - rightStart);
  return Math.max(rightLength * 1.5, 44);
}

function toFactoryMinutes(value: unknown, unit: unknown, fallbackUnit: 's' | 'min' | 'h' = 's'): number {
  const numericValue = Number(value) || 0;
  const resolvedUnit = typeof unit === 'string' && unit.length > 0 ? unit : fallbackUnit;
  if (['dias', 'dia', 'd'].includes(resolvedUnit)) return numericValue * 1440;
  if (['horas', 'hora', 'h'].includes(resolvedUnit)) return numericValue * 60;
  if (['minutos', 'minuto', 'min'].includes(resolvedUnit)) return numericValue;
  return numericValue / 60;
}

function getProcessCapacityPerDay(card: CardData | undefined, shiftMinutes: number): number {
  if (!card) return 0;
  const cycleMinutes = toFactoryMinutes((card as any).vsmCT ?? card.tp, (card as any).vsmCTUnit ?? card.tpUnit, 's');
  const operators = Math.max(1, Number((card as any).vsmOp ?? card.pessoasSimultaneas ?? 1));
  return cycleMinutes > 0 ? (shiftMinutes * operators) / cycleMinutes : 0;
}

function isFifoSwapInventoryCard(card: CardData | undefined): boolean {
  return !!card && card.tipo === 'inventory' && ['estoque', 'supermercado', 'fifo'].includes(card.vsmIcon ?? '');
}

function getFifoStockQty(
  connection: { fifoCurrentWip?: number } | undefined,
  sourceCard: CardData | undefined,
  targetCard: CardData | undefined,
): number {
  const stockCard = isFifoSwapInventoryCard(targetCard) ? targetCard : isFifoSwapInventoryCard(sourceCard) ? sourceCard : undefined;
  const cardQty = stockCard
    ? Number((stockCard as any).vsmFifoCurrentWip ?? (stockCard as any).inventory ?? (stockCard as any).entrada ?? 0)
    : 0;
  return Math.max(0, Number(connection?.fifoCurrentWip ?? cardQty) || 0);
}

function getFifoCapacity(
  connection: { fifoMaxCapacity?: number } | undefined,
  sourceCard: CardData | undefined,
  targetCard: CardData | undefined,
): number {
  const stockCard = isFifoSwapInventoryCard(targetCard) ? targetCard : isFifoSwapInventoryCard(sourceCard) ? sourceCard : undefined;
  return Math.max(1, Number(connection?.fifoMaxCapacity ?? (stockCard as any)?.vsmFifoMaxCapacity ?? 50) || 50);
}

function getCardCenter(card: CardData): Position {
  const dimensions = getCardDimensions(card);
  return {
    x: card.posicao.x + dimensions.width / 2,
    y: card.posicao.y + dimensions.height / 2,
  };
}

function isCardBetweenProcesses(card: CardData, sourceProcess: CardData, targetProcess: CardData): boolean {
  const cardCenter = getCardCenter(card);
  const sourceCenter = getCardCenter(sourceProcess);
  const targetCenter = getCardCenter(targetProcess);
  const minX = Math.min(sourceCenter.x, targetCenter.x);
  const maxX = Math.max(sourceCenter.x, targetCenter.x);
  const expectedY = sourceCenter.y + ((targetCenter.y - sourceCenter.y) * ((cardCenter.x - sourceCenter.x) / Math.max(1, targetCenter.x - sourceCenter.x)));
  return cardCenter.x > minX && cardCenter.x < maxX && Math.abs(cardCenter.y - expectedY) <= 180;
}

function getFifoRuntimeState(
  connection: { fifoMaxCapacity?: number; fifoCurrentWip?: number; kanbanLoopEnabled?: boolean; kanbanSupermarketId?: string } | undefined,
  sourceCard: CardData | undefined,
  targetCard: CardData | undefined,
  cards: CardData[],
  shiftMinutes: number,
): { isBlocked: boolean; maxCapacity?: number } {
  const maxCapacity = getFifoCapacity(connection, sourceCard, targetCard);
  const currentWip = getFifoStockQty(connection, sourceCard, targetCard);
  const outputCapacity = getProcessCapacityPerDay(sourceCard, shiftMinutes);
  const consumeCapacity = getProcessCapacityPerDay(targetCard, shiftMinutes);
  const kanbanSupermarket = connection?.kanbanSupermarketId
    ? cards.find(card => card.id === connection.kanbanSupermarketId)
    : undefined;
  const supermarketStock = Number((kanbanSupermarket as any)?.inventory ?? 0);
  const kanbanBlocksProduction = Boolean(connection?.kanbanLoopEnabled && kanbanSupermarket && supermarketStock > 0);
  const projectedWip = currentWip + Math.max(0, outputCapacity - consumeCapacity);
  return {
    maxCapacity,
    isBlocked: currentWip > maxCapacity || kanbanBlocksProduction || (outputCapacity > consumeCapacity && projectedWip > maxCapacity),
  };
}

export const VsmCanvas: React.FC<VsmCanvasProps> = ({
  activeCards, postIts, readOnly = false, scale, setScale, pan, setPan, selectedPostItIds, setSelectedPostItIds, 
  selectedCardIds, setSelectedCardIds, highlightedPath,
  analysisMode, storeMetrics, isCreatingPostIt, creatingPostItType, manualArrowMode, manualArrowSource,
  manualArrowWaypoints, connectingSource, selectedConnection, boxSelection, setBoxSelection,
  processedConnections, mergeNodes, handleCardSelect, onClearSelection, handleDeleteTotal, handleMarkX,
  handleDuplicateCard, handleAddNext, handleMove, handleCardDragStart, handleCardDragEnd, handleStartConnection,
  handleFinishConnection, handleCreatePostItOnCard, handleUpdateCardData,
  handlePostItClick, handlePostItStartDrag, handlePostItDrag, handlePostItEndDrag,
  handlePostItTextChange, handleTogglePostItMinimize, openContextMenu,
  handleOpenInMemo, getActionsByNode, setRightPanelMode, setSelectedConnection,
  setEditingPostItId, canvasRef, currentLayout, draggingPostIt, postItSnapTargetId,
  editingPostItId, brainstormMode = false, setPostItSnapTargetId, isTimeScaleActive = false,
  heatmapMode = false,
  executiveMode = false,
  executiveFocusIds = null,
  executiveStageSummaries = [],
  executiveBottleneckId = null,
  showGrid = true, gridType = 'lines', gridColor = 'rgba(255,255,255,0.05)', gridIntensity = 18, gridScale = 10,
  canvasBackgroundColor = '#FAFBFD', canvasGridType, canvasGridColor, defaultArrowColor = '#64748B',
  interactionMode = 'select', pageMode = { enabled: false, format: 'a4-landscape' }, 
  setManualArrowWaypoints, setManualArrowMode, setManualArrowSource,
  onCancelManualArrowCreation,
  contentScale = 12, pageConnectors = [], onUpdateConnectorPosition, onUpdateConnectorLabel, onDeleteConnector,
  snapToGridEnabled = true, onMouseMove, onOpenImpactPanel, onDropCardOnTarget, onDropCardOnCanvas, onDropCardBeforeTarget,
  showPercentBadge = true,
  showAS = true,
  showFTE = true,
  insertionPreview = null,
  swimLanes = [],
  swimLanesVisible = false,
  onSwimlanesApplied,
  liveMetricsMap,
  engineeringTool = null,
  onEngineeringToolChange,
  consolidateFutureNonce = 0,
}) => {
  const { isDark } = useTheme();
  const simulation = useSimulationSafe();
  const simulationShiftMinutes =
    simulation?.shiftPreset === '2_shifts'
      ? 960
      : simulation?.shiftPreset === 'plus_2h'
      ? 600
      : 480;
  const selectedCardId = useProjectStore((state) => state.selectedCardId);
  const isKaizenMode = useProjectStore((state) => state.isKaizenMode);
  const viewMode = useProjectStore((state) => state.viewMode);
  const factoryShifts = useProjectStore((state) => state.factoryShifts);
  const factoryMinPerShift = useProjectStore((state) => state.factoryMinPerShift);
  const factoryWorkDays = useProjectStore((state) => state.factoryWorkDays);
  const vsmDesignStyle = useProjectStore((state) => state.vsmDesignStyle);
  const vsmCardColor = useProjectStore((state) => state.vsmCardColor);
  const isOptimizationMode = viewMode === 'kaizen_work' || isKaizenMode;
  const canEditCanvas = !readOnly;
  const [hoveredFlowCellId, setHoveredFlowCellId] = React.useState<string | null>(null);
  const [activeFlowCellId, setActiveFlowCellId] = React.useState<string | null>(null);
  const kaizenMarkerDragRef = React.useRef<{
    cardId: string;
    startClientX: number;
    startClientY: number;
    startOffsetX: number;
    startOffsetY: number;
    moved: boolean;
  } | null>(null);

  // Drag ref para ícones kanban arrastáveis ao longo das setas (retirada / produção / posto)
  const kanbanIconDragRef = React.useRef<{
    cardId: string;
    offsetField: 'vsmKanbanRetIconOffset' | 'vsmKanbanProdIconOffset' | 'vsmKanbanPostoIconOffset' | 'vsmKanbanRetArrowOffset' | 'vsmKanbanProdArrowOffset' | 'vsmKanbanPcpmIconOffset';
    yOnly: boolean;
    startClientX: number;
    startClientY: number;
    startOffsetX: number;
    startOffsetY: number;
  } | null>(null);

  // Drag ref para waypoints das setas kanban (Fase 2)
  const kanbanWaypointDragRef = React.useRef<{
    cardId: string;
    field: 'vsmKanbanRetWaypoints' | 'vsmKanbanProdWaypoints';
    index: number;
    startClientX: number;
    startClientY: number;
    startWpX: number;
    startWpY: number;
  } | null>(null);

  // Hover key para mostrar/ocultar bolinhas de waypoint (formato: `${cardId}::${field}`)
  const [hoveredKanbanArrow, setHoveredKanbanArrow] = React.useState<string | null>(null);

  // Drag ref para ícone de sinal de puxada (pull signal) nas setas vsm_pull_signal
  const pullSignalIconDragRef = React.useRef<{
    srcCardId: string;
    connId: string;
    startClientX: number;
    startClientY: number;
    startOffsetX: number;
    startOffsetY: number;
  } | null>(null);
  // Drag ref para deslocamento vertical do conjunto seta+caminhão+texto (vsm_transport)
  const transportEdgeYDragRef = React.useRef<{
    srcCardId: string;
    connId: string;
    startClientY: number;
    startOffsetY: number;
    moved: boolean;
  } | null>(null);
  const lastConsolidateNonceRef = React.useRef(consolidateFutureNonce);
  const cancelManualArrowCreation = React.useCallback(() => {
    if (onCancelManualArrowCreation) {
      onCancelManualArrowCreation();
      return;
    }
    if (setManualArrowMode) setManualArrowMode(false);
    if (setManualArrowSource) setManualArrowSource(null);
    if (setManualArrowWaypoints) setManualArrowWaypoints([]);
  }, [onCancelManualArrowCreation, setManualArrowMode, setManualArrowSource, setManualArrowWaypoints]);
  const isFutureMode = viewMode === 'future_simulated';
  const getFutureMetrics = useProjectStore((state) => state.getFutureMetrics);
  const setCards = useProjectStore((state) => state.setCards);
  // Cursor none durante modo cria��o de post-it (cursor visual � o ghost div abaixo)
  const postItCursor = React.useMemo(() => {
    return isCreatingPostIt ? 'none' : 'default';
  }, [isCreatingPostIt]);

  const [isPanning, setIsPanning] = React.useState(false);
  // Posi��o do cursor em coordenadas de canvas para renderizar o ghost post-it
  const [cursorCanvasPos, setCursorCanvasPos] = React.useState<{ x: number; y: number } | null>(null);
  const [isSelecting, setIsSelecting] = React.useState(false);
  const [showComparisonModal, setShowComparisonModal] = useState(false);
  const [contextMenu, setContextMenu] = React.useState<{ x: number; y: number; type: 'canvas' | 'edge' } | null>(null);
  const panStartRef = React.useRef<{ x: number; y: number } | null>(null);
  const selectionStartRef = React.useRef<{ x: number; y: number } | null>(null);
  const [dropTargetCardId, setDropTargetCardId] = React.useState<string | null>(null);
  const [dropPosition, setDropPosition] = React.useState<'yes' | 'no' | null>(null);
  const [draggingCardType, setDraggingCardType] = React.useState<string | null>(null);
  const [isMultiSelectMode, setIsMultiSelectMode] = React.useState(false);

  React.useEffect(() => {
    if (!readOnly) return;
    if (manualArrowMode || manualArrowSource || connectingSource || isCreatingPostIt) {
      cancelManualArrowCreation();
    }
  }, [cancelManualArrowCreation, connectingSource, isCreatingPostIt, manualArrowMode, manualArrowSource, readOnly]);

  const wipByTargetId = useMemo(() => {
    if (!simulation?.isActive) return new Map<string, { loadPercent: number; overflowPerDay: number }>();

    const map = new Map<string, { loadPercent: number; overflowPerDay: number }>();
    const demand = Math.max(0, Number(simulation.demand ?? 0));

    activeCards.forEach((card) => {
      if (['card_inicial', 'card_final', 'decisao'].includes(card.tipo)) return;

      const override = simulation.kaizenOverrides[card.id] ?? {};
      const extraFte = Math.max(0, Number(override.fte ?? override.extraFTE ?? 0));
      const reworkReduction = Math.max(0, Math.min(100, Number(override.rework ?? override.reworkReduction ?? 0)));
      const baseFte = Math.max(1, Number(card.pessoasSimultaneas ?? 1));
      const baseCycleMinutes = Math.max(0, Number(card.tt ?? 0));
      const adjustedCycleMinutes = baseCycleMinutes * (1 - reworkReduction / 100);
      const capacity =
        adjustedCycleMinutes > 0 ? (simulationShiftMinutes * (baseFte + extraFte)) / adjustedCycleMinutes : 0;
      const loadPercent = capacity > 0 ? (demand / capacity) * 100 : 999;
      const overflowPerDay = Math.max(0, Math.round(demand - capacity));

      map.set(card.id, { loadPercent, overflowPerDay });
    });

    return map;
  }, [activeCards, simulation?.demand, simulation?.isActive, simulation?.kaizenOverrides, simulationShiftMinutes]);

  // V201 — cards ocultos no Estado Futuro (vsmFlowCellBlocked + vsmFuturoHide)
  // V206: vsmLocked NÃO oculta o card — o PCPM permanece visível com slash vermelho (isPcpmLocked2)
  const futuroHiddenSet = React.useMemo(() => {
    if (viewMode !== 'future_simulated') return new Set<string>();
    const set = new Set<string>();
    activeCards.forEach(c => {
      if ((c as any).vsmFlowCellBlocked || (c as any).vsmFuturoHide) set.add(c.id);
    });
    return set;
  }, [activeCards, viewMode]);

  const continuousFlowCells = React.useMemo(() => {
    if (!isOptimizationMode && viewMode !== 'future_simulated') return [];

    const cells = new Map<string, ContinuousFlowCellSummary>();

    activeCards.forEach(card => {
      const cellId = (card as any).vsmFlowCellId;
      if (!cellId) return;
      const x = Number((card as any).vsmFlowCellX);
      const y = Number((card as any).vsmFlowCellY);
      const width = Number((card as any).vsmFlowCellW);
      const height = Number((card as any).vsmFlowCellH);
      if (![x, y, width, height].every(Number.isFinite) || width <= 0 || height <= 0) return;

      const projectedCycleSeconds = Number((card as any).vsmFlowCellProjectedCycleSeconds);
      const leadTimeReductionPercent = Number((card as any).vsmFlowCellLeadTimeReductionPercent);
      const existing = cells.get(cellId) ?? {
        id: cellId,
        x,
        y,
        width,
        height,
        rawCycleSeconds: 0,
        cycleSeconds: 0,
        minCycleSeconds: Number.POSITIVE_INFINITY,
        displayCycleSeconds: 0,
        projectedCycleSeconds: undefined,
        leadTimeReductionPercent: undefined,
        processCount: 0,
        hasLayoutKaizen: false,
      };
      if (Number.isFinite(projectedCycleSeconds) && projectedCycleSeconds > 0) {
        existing.projectedCycleSeconds = projectedCycleSeconds;
      }
      if (Number.isFinite(leadTimeReductionPercent) && leadTimeReductionPercent > 0) {
        existing.leadTimeReductionPercent = Math.max(existing.leadTimeReductionPercent ?? 0, Math.min(100, leadTimeReductionPercent));
      }
      if ((card as any).vsmFlowCellKaizenLayout) {
        existing.hasLayoutKaizen = true;
      }
      if (isContinuousFlowProcess(card)) {
        const effectiveCycleSeconds = getEffectiveProcessCycleSeconds(card, simulation?.kaizenOverrides?.[card.id]);
        existing.rawCycleSeconds += getProcessWorkContentSeconds(card);
        existing.cycleSeconds = Math.max(existing.cycleSeconds, effectiveCycleSeconds);
        if (effectiveCycleSeconds > 0) {
          existing.minCycleSeconds = Math.min(existing.minCycleSeconds, effectiveCycleSeconds);
        }
        existing.processCount += 1;
      }
      cells.set(cellId, existing);
    });

    return Array.from(cells.values())
      .filter(cell => cell.processCount >= 2)
      .map(cell => ({
        ...cell,
        displayCycleSeconds: Number.isFinite(cell.projectedCycleSeconds) && Number(cell.projectedCycleSeconds) > 0
          ? Number(cell.projectedCycleSeconds)
          : Number.isFinite(cell.minCycleSeconds)
          ? cell.minCycleSeconds
          : cell.cycleSeconds,
      }));
  }, [activeCards, isOptimizationMode, simulation?.kaizenOverrides]);

  const openKaizenProjection = React.useCallback((card: CardData) => {
    const previousProjection = (card as any).vsmKaizenProjection ?? {};
    const currentCycleSeconds = getProcessCycleSeconds(card);
    const nextProjection = {
      ...previousProjection,
      title: previousProjection.title ?? 'KAIZEN',
      type: previousProjection.type ?? 'multiMetric',
      currentValue: previousProjection.currentValue ?? currentCycleSeconds,
      metrics: previousProjection.metrics ?? {},
    };

    if (!(card as any).vsmKaizenProjection) {
      setCards(activeCards.map(currentCard => currentCard.id === card.id
        ? { ...currentCard, vsmKaizenProjection: nextProjection } as CardData
        : currentCard));
    }

    useProjectStore.getState().setSelectedCardId(card.id);
    setSelectedCardIds(new Set([card.id]));
    if (setRightPanelMode) setRightPanelMode('properties');
    if (onEngineeringToolChange) onEngineeringToolChange(null);
  }, [activeCards, onEngineeringToolChange, setCards, setRightPanelMode, setSelectedCardIds]);

  const getCanvasPointFromEvent = React.useCallback((event: React.MouseEvent | React.PointerEvent) => {
    if (!canvasRef.current) return null;
    const rect = canvasRef.current.getBoundingClientRect();
    const effectiveScale = scale * (contentScale / 12);
    return {
      x: (event.clientX - rect.left - pan.x) / effectiveScale,
      y: (event.clientY - rect.top - pan.y) / effectiveScale,
    };
  }, [canvasRef, contentScale, pan.x, pan.y, scale]);

  const anchorFlowCellLayoutKaizen = React.useCallback((cell: ContinuousFlowCellSummary, point?: { x: number; y: number } | null) => {
    if (!canEditCanvas) return;
    const cellProcesses = activeCards
      .filter(card => (card as any).vsmFlowCellId === cell.id && isContinuousFlowProcess(card))
      .sort((a, b) => a.posicao.x - b.posicao.x);
    const clickPoint = point ?? { x: cell.x + cell.width / 2, y: cell.y };
    const availableProcesses = cellProcesses.filter(card => !(card as any).vsmKaizenProjection);
    const anchorCandidates = availableProcesses.length > 0 ? availableProcesses : cellProcesses;
    const anchorCard = anchorCandidates
      .map(card => {
        const { width, height } = getCanvasHitBox(card, isTimeScaleActive);
        const centerX = card.posicao.x + width / 2;
        const centerY = card.posicao.y + height / 2;
        return { card, distance: Math.hypot(clickPoint.x - centerX, clickPoint.y - centerY) };
      })
      .sort((a, b) => a.distance - b.distance)[0]?.card;
    if (!anchorCard) return;

    const headerAnchor = getCellHeaderAnchorPoint(cell, clickPoint);
    const markerLeft = headerAnchor.x - KAIZEN_MARKER_SIZE / 2;
    const markerTop = headerAnchor.y - KAIZEN_MARKER_SIZE / 2;

    setCards(activeCards.map(card => {
      if ((card as any).vsmFlowCellId !== cell.id) return card;
      if (card.id !== anchorCard.id) {
        return {
          ...card,
          vsmFlowCellKaizenLayout: true,
          vsmFlowCellBaselineCycleSeconds: (card as any).vsmFlowCellBaselineCycleSeconds ?? cell.rawCycleSeconds,
        } as CardData;
      }

      const previousProjection = (card as any).vsmKaizenProjection ?? {};
      return {
        ...card,
        vsmFlowCellKaizenLayout: true,
        vsmFlowCellBaselineCycleSeconds: (card as any).vsmFlowCellBaselineCycleSeconds ?? cell.rawCycleSeconds,
        vsmKaizenProjection: {
          ...previousProjection,
          title: previousProjection.title ?? 'LAYOUT',
          type: 'layout',
          improvementType: previousProjection.improvementType ?? 'layout',
          scope: 'flowCell',
          flowCellId: cell.id,
          anchorType: 'cellHeader',
          cellAnchorSlot: headerAnchor.slot,
          flowCellCurrentCycleSeconds: previousProjection.flowCellCurrentCycleSeconds ?? cell.rawCycleSeconds,
          currentValue: previousProjection.currentValue ?? getProcessCycleSeconds(card),
          markerOffsetX: previousProjection.markerOffsetX ?? markerLeft - card.posicao.x,
          markerOffsetY: previousProjection.markerOffsetY ?? markerTop - card.posicao.y,
          metrics: previousProjection.metrics ?? {},
        },
      } as CardData;
    }));
    useProjectStore.getState().setSelectedCardId(anchorCard.id);
    setSelectedCardIds(new Set());
    if (setRightPanelMode) setRightPanelMode('properties');
    setActiveFlowCellId(cell.id);
    if (onEngineeringToolChange) onEngineeringToolChange(null);
  }, [activeCards, canEditCanvas, contentScale, isTimeScaleActive, onEngineeringToolChange, pan.x, pan.y, scale, setCards, setRightPanelMode, setSelectedCardIds]);

  const handleFlowCellKaizenClick = React.useCallback((cell: ContinuousFlowCellSummary, event: React.MouseEvent) => {
    event.stopPropagation();
    if (!canEditCanvas || engineeringTool !== 'kaizen') {
      setActiveFlowCellId(current => current === cell.id ? null : cell.id);
      if (setRightPanelMode) setRightPanelMode('none');
      useProjectStore.getState().setSelectedCardId(null);
      if (onClearSelection) onClearSelection();
      return;
    }

    anchorFlowCellLayoutKaizen(cell, getCanvasPointFromEvent(event));
  }, [anchorFlowCellLayoutKaizen, canEditCanvas, engineeringTool, getCanvasPointFromEvent, onClearSelection, setRightPanelMode]);

  React.useEffect(() => {
    const cellsNeedingMigration = continuousFlowCells.filter(cell => {
      if (!cell.hasLayoutKaizen) return false;
      return !activeCards.some(card =>
        (card as any).vsmFlowCellId === cell.id
        && (card as any).vsmKaizenProjection?.scope === 'flowCell'
      );
    });
    if (cellsNeedingMigration.length === 0) return;

    let changed = false;
    const updatedCards = activeCards.map(card => {
      const cell = cellsNeedingMigration.find(candidate => (card as any).vsmFlowCellId === candidate.id);
      if (!cell || !isContinuousFlowProcess(card) || (card as any).vsmKaizenProjection) return card;

      const cellProcesses = activeCards
        .filter(candidate => (candidate as any).vsmFlowCellId === cell.id && isContinuousFlowProcess(candidate))
        .sort((a, b) => a.posicao.x - b.posicao.x);
      const anchorCard = cellProcesses.find(candidate => !(candidate as any).vsmKaizenProjection) ?? cellProcesses[Math.floor(cellProcesses.length / 2)];
      if (!anchorCard || anchorCard.id !== card.id) return card;

      const headerAnchor = getCellHeaderAnchorPoint(cell);

      changed = true;
      return {
        ...card,
        vsmFlowCellBaselineCycleSeconds: (card as any).vsmFlowCellBaselineCycleSeconds ?? cell.rawCycleSeconds,
        vsmKaizenProjection: {
          title: 'LAYOUT',
          type: 'layout',
          improvementType: 'layout',
          scope: 'flowCell',
          flowCellId: cell.id,
          anchorType: 'cellHeader',
          cellAnchorSlot: headerAnchor.slot,
          flowCellCurrentCycleSeconds: cell.rawCycleSeconds,
          currentValue: getProcessCycleSeconds(card),
          markerOffsetX: headerAnchor.x - KAIZEN_MARKER_SIZE / 2 - card.posicao.x,
          markerOffsetY: headerAnchor.y - KAIZEN_MARKER_SIZE / 2 - card.posicao.y,
          metrics: {},
        },
      } as CardData;
    });

    if (changed) setCards(updatedCards);
  }, [activeCards, continuousFlowCells, setCards]);

  React.useEffect(() => {
    let changed = false;
    const updatedCards = activeCards.map(card => {
      const projection = (card as any).vsmKaizenProjection;
      const cellId = (card as any).vsmFlowCellId;
      if (!cellId || !projection || !(card as any).vsmFlowCellKaizenLayout) return card;
      const cell = continuousFlowCells.find(candidate => candidate.id === cellId);
      if (!cell) return card;
      const migratedProjectedSeconds = Number(
        projection.flowCellProjectedCycleSeconds
        ?? projection.metrics?.cycleTime?.projectedSeconds
        ?? projection.projectedSeconds
        ?? 0,
      ) || 0;
      const existingCellProjectedSeconds = Number((card as any).vsmFlowCellProjectedCycleSeconds) || 0;
      const shouldUpdateScope = projection.scope !== 'flowCell' || projection.flowCellId !== cellId;
      const shouldUpdateBaseline = !(Number((card as any).vsmFlowCellBaselineCycleSeconds) > 0) || !(Number(projection.flowCellCurrentCycleSeconds) > 0);
      const shouldPromoteProjection = migratedProjectedSeconds > 0 && existingCellProjectedSeconds <= 0;
      if (!shouldUpdateScope && !shouldUpdateBaseline && !shouldPromoteProjection) return card;
      changed = true;
      return {
        ...card,
        vsmFlowCellBaselineCycleSeconds: (card as any).vsmFlowCellBaselineCycleSeconds ?? cell.rawCycleSeconds,
        vsmFlowCellProjectedCycleSeconds: existingCellProjectedSeconds > 0
          ? existingCellProjectedSeconds
          : (migratedProjectedSeconds || (card as any).vsmFlowCellProjectedCycleSeconds),
        vsmKaizenProjection: {
          ...projection,
          scope: 'flowCell',
          flowCellId: cellId,
          flowCellCurrentCycleSeconds: projection.flowCellCurrentCycleSeconds ?? cell.rawCycleSeconds,
          flowCellProjectedCycleSeconds: projection.flowCellProjectedCycleSeconds ?? (migratedProjectedSeconds || undefined),
        },
      } as CardData;
    });
    if (changed) setCards(updatedCards);
  }, [activeCards, continuousFlowCells, setCards]);

  const blockInformationConnection = React.useCallback((sourceId: string, connectionId: string) => {
    const updatedCards = activeCards.map(card => {
      if (card.id !== sourceId) return card;
      return {
        ...card,
        conexoes_saida: (card.conexoes_saida ?? []).map(conn => conn.id === connectionId
          ? { ...conn, vsmInfoBlocked: true, vsmInfoBlockedAt: new Date().toISOString() } as Connection
          : conn),
      } as CardData;
    });
    setCards(updatedCards);
  }, [activeCards, setCards]);

  const moveKaizenMarker = React.useCallback((cardId: string, offsetX: number, offsetY: number) => {
    const updatedCards = activeCards.map(card => {
      if (card.id !== cardId) return card;
      const previousProjection = (card as any).vsmKaizenProjection ?? {};
      if (previousProjection.scope === 'flowCell' || previousProjection.flowCellId) {
        const cell = continuousFlowCells.find(candidate => candidate.id === (previousProjection.flowCellId ?? (card as any).vsmFlowCellId));
        if (cell) {
          const markerCenter = {
            x: card.posicao.x + offsetX + KAIZEN_MARKER_SIZE / 2,
            y: card.posicao.y + offsetY + KAIZEN_MARKER_SIZE / 2,
          };
          const headerAnchor = getCellHeaderAnchorPoint(cell, markerCenter);
          return {
            ...card,
            vsmKaizenProjection: {
              ...previousProjection,
              anchorType: 'cellHeader',
              cellAnchorSlot: headerAnchor.slot,
              markerOffsetX: headerAnchor.x - KAIZEN_MARKER_SIZE / 2 - card.posicao.x,
              markerOffsetY: headerAnchor.y - KAIZEN_MARKER_SIZE / 2 - card.posicao.y,
            },
          } as CardData;
        }
      }
      return {
        ...card,
        vsmKaizenProjection: {
          ...previousProjection,
          markerOffsetX: offsetX,
          markerOffsetY: offsetY,
        },
      } as CardData;
    });
    setCards(updatedCards);
  }, [activeCards, continuousFlowCells, setCards]);

  // Mover ícone kanban (offset 2D) — usa setCards direto igual ao kaizen marker para não quebrar pointer capture
  const moveKanbanIcon = React.useCallback((cardId: string, field: string, x: number, y: number) => {
    const updatedCards = activeCards.map(card => {
      if (card.id !== cardId) return card;
      return { ...card, [field]: { x, y } } as CardData;
    });
    setCards(updatedCards);
  }, [activeCards, setCards]);

  // ── Waypoints kanban (Fase 2: rotas com curvas Bezier suaves) ──
  const addKanbanWaypoint = React.useCallback((cardId: string, field: 'vsmKanbanRetWaypoints' | 'vsmKanbanProdWaypoints', x: number, y: number) => {
    const updatedCards = activeCards.map(card => {
      if (card.id !== cardId) return card;
      const wps = (((card as any)[field] ?? []) as Array<{ x: number; y: number }>).map(w => ({ x: Number(w.x), y: Number(w.y) }));
      wps.push({ x: Math.round(x), y: Math.round(y) });
      return { ...card, [field]: wps } as CardData;
    });
    setCards(updatedCards);
  }, [activeCards, setCards]);

  const moveKanbanWaypoint = React.useCallback((cardId: string, field: 'vsmKanbanRetWaypoints' | 'vsmKanbanProdWaypoints', index: number, x: number, y: number) => {
    const updatedCards = activeCards.map(card => {
      if (card.id !== cardId) return card;
      const wps = (((card as any)[field] ?? []) as Array<{ x: number; y: number }>).map((w, i) =>
        i === index ? { x: Math.round(x), y: Math.round(y) } : { x: Number(w.x), y: Number(w.y) }
      );
      return { ...card, [field]: wps } as CardData;
    });
    setCards(updatedCards);
  }, [activeCards, setCards]);

  const removeKanbanWaypoint = React.useCallback((cardId: string, field: 'vsmKanbanRetWaypoints' | 'vsmKanbanProdWaypoints', index: number) => {
    const updatedCards = activeCards.map(card => {
      if (card.id !== cardId) return card;
      const wps = (((card as any)[field] ?? []) as Array<{ x: number; y: number }>).filter((_, i) => i !== index);
      return { ...card, [field]: wps } as CardData;
    });
    setCards(updatedCards);
  }, [activeCards, setCards]);

  const movePullSignalIcon = React.useCallback((srcCardId: string, connId: string, x: number, y: number) => {
    const updatedCards = activeCards.map(card => {
      if (card.id !== srcCardId) return card;
      const newConexoes = (card.conexoes_saida ?? []).map((c: any) =>
        c.id === connId ? { ...c, vsmPullSignalIconOffset: { x, y } } : c
      );
      return { ...card, conexoes_saida: newConexoes } as CardData;
    });
    setCards(updatedCards);
  }, [activeCards, setCards]);

  const moveVsmTransportEdgeY = React.useCallback((srcCardId: string, connId: string, offsetY: number) => {
    const updatedCards = activeCards.map(card => {
      if (card.id !== srcCardId) return card;
      const newConexoes = (card.conexoes_saida ?? []).map((c: any) =>
        c.id === connId ? { ...c, vsmTransportOffsetY: offsetY } : c
      );
      return { ...card, conexoes_saida: newConexoes } as CardData;
    });
    setCards(updatedCards);
  }, [activeCards, setCards]);

  const openKaizenMarkerProperties = React.useCallback((card: CardData, event: React.PointerEvent<HTMLDivElement>) => {
    event.stopPropagation();
    const isFlowCellKaizen = Boolean((card as any).vsmKaizenProjection?.scope === 'flowCell' || (card as any).vsmKaizenProjection?.flowCellId);
    if (isFlowCellKaizen) {
      useProjectStore.getState().setSelectedCardId(card.id);
      setSelectedCardIds(new Set());
      setActiveFlowCellId((card as any).vsmKaizenProjection?.flowCellId ?? (card as any).vsmFlowCellId ?? null);
    } else {
      handleCardSelect(card.id, event as any);
    }
    if (setRightPanelMode) setRightPanelMode('properties');
  }, [handleCardSelect, setRightPanelMode, setSelectedCardIds]);

  React.useEffect(() => {
    const soldaI = activeCards.find(card => {
      const label = String((card as any).atividade ?? (card as any).vsmNome ?? (card as any).titulo ?? '');
      return isContinuousFlowProcess(card) && /^solda\s*i$/i.test(label.trim());
    });
    if (!soldaI) return;

    const setupProjection = getKaizenMetrics(soldaI).setup;
    if (setupProjection) return;

    const updatedCards = activeCards.map(card => {
      if (card.id !== soldaI.id) return card;
      const previousProjection = (card as any).vsmKaizenProjection ?? {};
      const metrics = {
        ...(previousProjection.metrics ?? {}),
        setup: {
          currentValue: 10,
          projectedValue: 0,
          unit: 'min',
          projectedSeconds: 0,
        },
      };
      return {
        ...card,
        vsmCO: 10,
        vsmCT: 17,
        vsmCTUnit: 's',
        vsmKaizenProjection: {
          ...previousProjection,
          title: previousProjection.title ?? 'SMED',
          type: previousProjection.type ?? 'multiMetric',
          metrics,
        },
      } as CardData;
    });

    setCards(updatedCards);
  }, [activeCards, setCards]);

  React.useEffect(() => {
    if (consolidateFutureNonce === lastConsolidateNonceRef.current) return;
    lastConsolidateNonceRef.current = consolidateFutureNonce;

    const overrides = simulation?.kaizenOverrides ?? {};
    const updatedCards = activeCards.map(card => {
      const override = overrides[card.id] as any;
      const metrics = {
        ...getKaizenMetrics(card),
        ...(override?.metrics ?? {}),
      } as Record<VsmKaizenMetricKey, any>;
      const projectedSeconds = getProjectedCycleSeconds(card, override);
      const nextConnections = (card.conexoes_saida ?? []).map(conn => {
        const { vsmInfoBlocked, vsmInfoBlockedAt, ...rest } = conn as any;
        return rest as Connection;
      });
      const { vsmKaizenProjection, ...cleanCard } = card as any;
      const nextCard: any = { ...cleanCard, conexoes_saida: nextConnections };

      if (isContinuousFlowProcess(card) && projectedSeconds > 0) {
        const currentUnit = (card as any).vsmCTUnit ?? 's';
        const officialValue = currentUnit === 'min' ? projectedSeconds / 60 : projectedSeconds;
        nextCard.vsmCT = Number(officialValue.toFixed(currentUnit === 'min' ? 2 : 0));
        nextCard.vsmCTUnit = currentUnit;
      }

      if (metrics.setup) nextCard.vsmCO = Number(metrics.setup.projectedValue ?? nextCard.vsmCO ?? 0);
      if (metrics.availability) nextCard.vsmDisp = Number(metrics.availability.projectedValue ?? nextCard.vsmDisp ?? 100);
      if (metrics.quality) nextCard.percentLivreErros = Number(metrics.quality.projectedValue ?? nextCard.percentLivreErros ?? 100);

      return nextCard as CardData;
    });

    setCards(updatedCards);
    simulation?.clearKaizenOverrides?.();
    if (onEngineeringToolChange) onEngineeringToolChange(null);
  }, [activeCards, consolidateFutureNonce, onEngineeringToolChange, setCards, simulation]);

  const globalTaktTimeSeconds = React.useMemo(() => {
    // V204: calcular também no modo future_simulated para exibir Pitch e staffing no card célula
    if (!isOptimizationMode && viewMode !== 'future_simulated') return null;

    const clientCard = activeCards.find(card => {
      const extType = String((card as any).extType ?? '').toUpperCase();
      return card.tipo === 'card_final' || extType === 'CLIENTE' || (card as any).vsmIcon === 'cliente';
    });
    if (!clientCard) return null;

    const toDailyDemand = (card: CardData): number => {
      const rawDemand = Number((card as any).vsmDemanda ?? (card as any).entrada ?? 0);
      const demandUnit = String((card as any).unidadeDemanda ?? (card as any).vsmDemandaUnidade ?? '/dia').toLowerCase();
      const workDays = Math.max(0, Number((card as any).vsmDiasMes ?? factoryWorkDays) || 0);

      if (rawDemand > 0) {
        if (demandUnit.includes('mês') || demandUnit.includes('mes') || demandUnit.includes('/ms')) {
          return workDays > 0 ? rawDemand / workDays : 0;
        }
        if (demandUnit.includes('hora')) return rawDemand * 8;
        return rawDemand;
      }

      const mix = Array.isArray((card as any).vsmDemandMix) ? (card as any).vsmDemandMix : [];
      const totalMonthly = mix.reduce((sum: number, item: any) => sum + (Number(item?.qty) || 0), 0);
      return totalMonthly > 0 && workDays > 0 ? totalMonthly / workDays : 0;
    };

    const dailyDemand = toDailyDemand(clientCard);
    const availableSecPerDay = Math.max(0, Number(factoryShifts) || 0) * Math.max(0, Number(factoryMinPerShift) || 0) * 60;
    return dailyDemand > 0 && availableSecPerDay > 0 ? availableSecPerDay / dailyDemand : null;
  }, [activeCards, factoryMinPerShift, factoryShifts, factoryWorkDays, isOptimizationMode]);

  const selectedContinuousFlowProcesses = React.useMemo(() => {
    return activeCards.filter(card => selectedCardIds.has(card.id) && isContinuousFlowProcess(card));
  }, [activeCards, selectedCardIds]);

  const canCreateContinuousFlowCell = isOptimizationMode && canEditCanvas && selectedContinuousFlowProcesses.length >= 2;

  const createContinuousFlowCell = React.useCallback(() => {
    if (!canCreateContinuousFlowCell) return;

    const padding = 24;
    const processBoxes = selectedContinuousFlowProcesses.map(card => {
      const { width, height } = getCanvasHitBox(card, isTimeScaleActive);
      return {
        id: card.id,
        x: card.posicao.x,
        y: card.posicao.y,
        width,
        height,
      };
    });
    const minX = Math.min(...processBoxes.map(box => box.x)) - padding;
    const maxX = Math.max(...processBoxes.map(box => box.x + box.width)) + padding;
    const minY = Math.min(...processBoxes.map(box => box.y)) - padding;
    const maxY = Math.max(...processBoxes.map(box => box.y + box.height)) + padding;
    const processCenterSpreadX = Math.max(...processBoxes.map(box => box.x + box.width / 2)) - Math.min(...processBoxes.map(box => box.x + box.width / 2));
    const processCenterSpreadY = Math.max(...processBoxes.map(box => box.y + box.height / 2)) - Math.min(...processBoxes.map(box => box.y + box.height / 2));
    const primaryAxis = processCenterSpreadX >= processCenterSpreadY ? 'x' : 'y';
    const minProcessCenter = Math.min(...processBoxes.map(box => primaryAxis === 'x' ? box.x + box.width / 2 : box.y + box.height / 2));
    const maxProcessCenter = Math.max(...processBoxes.map(box => primaryAxis === 'x' ? box.x + box.width / 2 : box.y + box.height / 2));
    const cellId = `vsm-flow-cell-${Date.now().toString(36)}`;

    const updatedCards = activeCards.map(card => {
      const { width, height } = getCanvasHitBox(card, isTimeScaleActive);
      const x = card.posicao.x;
      const y = card.posicao.y;
      const intersectsCell = x + width > minX && x < maxX && y + height > minY && y < maxY;
      const isSelectedProcess = selectedCardIds.has(card.id) && isContinuousFlowProcess(card);
      const cardCenter = primaryAxis === 'x' ? x + width / 2 : y + height / 2;
      const isInternalCellStock = cardCenter > minProcessCenter && cardCenter < maxProcessCenter;
      const isBlockedStock = intersectsCell && isInternalCellStock && card.tipo === 'inventory' && (card as any).vsmIcon === 'estoque';

      if (!isSelectedProcess && !isBlockedStock) return card;

      return {
        ...card,
        vsmFlowCellId: cellId,
        vsmFlowCellX: minX,
        vsmFlowCellY: minY,
        vsmFlowCellW: maxX - minX,
        vsmFlowCellH: maxY - minY,
        vsmFlowCellBlocked: isBlockedStock ? true : (card as any).vsmFlowCellBlocked,
      } as CardData;
    });

    setCards(updatedCards);
    setSelectedCardIds(new Set(selectedContinuousFlowProcesses.map(card => card.id)));
    setSelectedPostItIds(new Set());
    if (setRightPanelMode) setRightPanelMode('none');
    setContextMenu(null);
  }, [activeCards, canCreateContinuousFlowCell, isTimeScaleActive, selectedCardIds, selectedContinuousFlowProcesses, setCards, setRightPanelMode, setSelectedCardIds, setSelectedPostItIds]);

  const ungroupContinuousFlowCell = React.useCallback((cellId: string) => {
    if (!canEditCanvas) return;

    const updatedCards = activeCards.map(card => {
      if ((card as any).vsmFlowCellId !== cellId) return card;
      return {
        ...card,
        vsmFlowCellId: undefined,
        vsmFlowCellX: undefined,
        vsmFlowCellY: undefined,
        vsmFlowCellW: undefined,
        vsmFlowCellH: undefined,
        vsmFlowCellBlocked: card.tipo === 'inventory' && (card as any).vsmIcon === 'estoque'
          ? false
          : (card as any).vsmFlowCellBlocked,
      } as CardData;
    });

    setCards(updatedCards);
  }, [activeCards, canEditCanvas, setCards]);

  const cardById = useMemo(() => {
    const map = new Map<string, CardData>();
    activeCards.forEach((card) => map.set(card.id, card));
    return map;
  }, [activeCards]);

  const fifoBlockedSourceIds = useMemo(() => {
    const blocked = new Set<string>();
    activeCards.forEach((sourceCard) => {
      (sourceCard.conexoes_saida ?? []).forEach((rawConn) => {
        if (rawConn.connectionType === 'vsm_fifo') {
          const targetCard = cardById.get(rawConn.targetId);
          const fifoState = getFifoRuntimeState(rawConn, sourceCard, targetCard, activeCards, simulationShiftMinutes);
          if (fifoState.isBlocked) blocked.add(sourceCard.id);
        }
        if (rawConn.connectionType === 'vsm_info' && rawConn.kanbanLoopEnabled && rawConn.vsmInfoTransmission === 'Eletrônico') {
          const supermarket = rawConn.kanbanSupermarketId ? cardById.get(rawConn.kanbanSupermarketId) : undefined;
          if (supermarket && Number((supermarket as any).inventory ?? 0) > 0) blocked.add(sourceCard.id);
        }
      });
    });
    return blocked;
  }, [activeCards, cardById, simulationShiftMinutes]);

  const hiddenFifoSymbolCardIds = useMemo(() => {
    const hidden = new Set<string>();
    activeCards.forEach((sourceCard) => {
      (sourceCard.conexoes_saida ?? []).forEach((rawConn) => {
        if (rawConn.connectionType !== 'vsm_fifo') return;
        const targetCard = cardById.get(rawConn.targetId);
        if (isFifoSwapInventoryCard(sourceCard)) hidden.add(sourceCard.id);
        if (isFifoSwapInventoryCard(targetCard)) hidden.add(targetCard!.id);
      });
    });
    return hidden;
  }, [activeCards, cardById]);

  // V210 — Conjunto COMPLETO de cards não-interativos (espelha o filtro de renderedCards).
  // Usado por hit-testing, marquee selection e drag-drop targets para impedir que cards
  // visualmente ocultos (eliminados na Otimização, ocultos no Futuro) capturem cliques/seleção.
  const nonInteractableCardIds = React.useMemo(() => {
    const set = new Set<string>();
    if (viewMode === 'future_simulated') {
      const pcpmIds = new Set(
        activeCards.filter((c: any) => (c as any).vsmIcon === 'pcpm').map(c => c.id)
      );
      const removedConnIds = new Set<string>();
      activeCards.forEach((c: any) => {
        if (!pcpmIds.has(c.id)) return;
        (c.conexoes_saida ?? []).forEach((rc: any) => {
          if ((rc as any).connectionType !== 'vsm_pull_signal') {
            removedConnIds.add(rc.id as string);
          }
        });
      });
      const cellMemberIdSet = new Set(
        activeCards.filter(c => (c as any).vsmFlowCellId && !(c as any).vsmFlowCellBlocked).map(c => c.id)
      );
      const isAdjacentToCell = (c: CardData): boolean => {
        if (c.tipo !== 'inventory' || (c as any).vsmIcon !== 'estoque') return false;
        if ((c as any).vsmConvertedToIcon) return false;
        if ((c.conexoes_saida ?? []).some((rc: any) => cellMemberIdSet.has(rc.targetId))) return true;
        return activeCards.some(src => cellMemberIdSet.has(src.id) &&
          (src.conexoes_saida ?? []).some((rc: any) => rc.targetId === c.id));
      };
      activeCards.forEach(c => {
        if (
          c.status === 'removido' ||
          (c as any).vsmFlowCellBlocked ||
          (c as any).vsmFuturoHide ||
          ((c as any).vsmFlowCellId && !(c as any).vsmFlowCellBlocked) ||
          (c.tipo === 'inventory' && (c as any).vsmFlowCellId) ||
          isAdjacentToCell(c) ||
          ((c as any).vsmIcon === 'comunicacao' && (
            removedConnIds.has((c as any).vsmBoundConnection?.connectionId ?? '') ||
            pcpmIds.has((c as any).vsmBoundConnection?.sourceId ?? '')
          ))
        ) {
          set.add(c.id);
        }
      });
    } else if (viewMode === 'current_clean') {
      activeCards.forEach(c => { if (c.status === 'removido') set.add(c.id); });
    }
    activeCards.forEach(c => {
      if (isCancelledProgramInfoCard(c)) set.add(c.id);
    });
    hiddenFifoSymbolCardIds.forEach(id => set.add(id));
    return set;
  }, [activeCards, viewMode, hiddenFifoSymbolCardIds]);

  const interactableCards = React.useMemo(
    () => activeCards.filter(c => !nonInteractableCardIds.has(c.id)),
    [activeCards, nonInteractableCardIds]
  );

  // Limpa seleções de cards que se tornaram não-interativos (ex: ao entrar no modo Futuro,
  // um card antes selecionado pode ser ocultado — evita handles "fantasma" e cliques acidentais).
  React.useEffect(() => {
    if (nonInteractableCardIds.size === 0) return;
    const currentSelectedId = useProjectStore.getState().selectedCardId;
    if (currentSelectedId && nonInteractableCardIds.has(currentSelectedId)) {
      useProjectStore.getState().setSelectedCardId(null);
    }
    if (selectedCardIds && selectedCardIds.size > 0) {
      let mutated = false;
      const next = new Set<string>();
      selectedCardIds.forEach(id => {
        if (nonInteractableCardIds.has(id)) {
          mutated = true;
        } else {
          next.add(id);
        }
      });
      if (mutated) setSelectedCardIds(next);
    }
  }, [nonInteractableCardIds, selectedCardIds, setSelectedCardIds]);

  const selectedCardIsFlowCellScoped = React.useMemo(() => {
    const selectedCard = selectedCardId ? activeCards.find(card => card.id === selectedCardId) : null;
    return Boolean(
      (selectedCard as any)?.vsmKaizenProjection?.scope === 'flowCell'
      || (selectedCard as any)?.vsmKaizenProjection?.flowCellId,
    );
  }, [activeCards, selectedCardId]);

  const convertConnectionToFifoAtomically = useCallback((sourceId: string, connectionId: string, patch: Partial<Connection>) => {
    if (!canEditCanvas) return;

    const currentSourceCard = activeCards.find(card => card.id === sourceId);
    const currentConnection = currentSourceCard?.conexoes_saida?.find(conn => conn.id === connectionId);
    if (!currentSourceCard || !currentConnection) return;

    const currentTargetCard = cardById.get(currentConnection.targetId);
    const incomingProcessFor = (cardId: string) => activeCards.find(card =>
      isFactoryProcessTarget(card) && (card.conexoes_saida ?? []).some(conn => conn.targetId === cardId)
    );
    const outgoingProcessFrom = (card: CardData | undefined) => {
      if (!card) return undefined;
      return (card.conexoes_saida ?? [])
        .map(conn => cardById.get(conn.targetId))
        .find(candidate => isFactoryProcessTarget(candidate));
    };
    const nearestProcessToRight = (card: CardData | undefined) => {
      if (!card) return undefined;
      return activeCards
        .filter(candidate => isFactoryProcessTarget(candidate) && candidate.posicao.x > card.posicao.x)
        .sort((a, b) => a.posicao.x - b.posicao.x)[0];
    };
    const nearestProcessToLeft = (card: CardData | undefined) => {
      if (!card) return undefined;
      return activeCards
        .filter(candidate => isFactoryProcessTarget(candidate) && candidate.posicao.x < card.posicao.x)
        .sort((a, b) => b.posicao.x - a.posicao.x)[0];
    };

    const sourceProcess = isFactoryProcessTarget(currentSourceCard)
      ? currentSourceCard
      : incomingProcessFor(currentSourceCard.id) ?? nearestProcessToLeft(currentSourceCard);
    const targetProcess = isFactoryProcessTarget(currentTargetCard)
      ? currentTargetCard
      : outgoingProcessFrom(currentTargetCard) ?? nearestProcessToRight(currentTargetCard);

    if (!sourceProcess || !targetProcess || sourceProcess.id === targetProcess.id) {
      const updatedConexoes = (currentSourceCard.conexoes_saida ?? []).map(conn =>
        conn.id === connectionId
          ? { ...conn, ...patch, connectionType: 'vsm_fifo' as const, waypoints: undefined, manualJumps: undefined, mergeToConnection: undefined, forceRadial: undefined }
          : conn
      );
      handleUpdateCardData(currentSourceCard.id, { conexoes_saida: updatedConexoes });
      setContextMenu(null);
      return;
    }

    const intermediateInventory = activeCards.filter(card =>
      card.tipo === 'inventory' && (
        card.id === currentSourceCard.id ||
        card.id === currentTargetCard?.id ||
        isCardBetweenProcesses(card, sourceProcess, targetProcess)
      )
    );
    const intermediateIds = new Set(intermediateInventory.map(card => card.id));
    const capturedWip = Math.max(
      Number(currentConnection.fifoCurrentWip ?? 0) || 0,
      ...intermediateInventory.map(card => Number((card as any).vsmFifoCurrentWip ?? (card as any).inventory ?? (card as any).entrada ?? 0) || 0),
    );
    const capturedCapacity = Math.max(1, Number(
      patch.fifoMaxCapacity ??
      currentConnection.fifoMaxCapacity ??
      intermediateInventory.find(card => Number((card as any).vsmFifoMaxCapacity) > 0)?.vsmFifoMaxCapacity ??
      50
    ) || 50);

    const fifoConnection: Connection = {
      ...currentConnection,
      ...patch,
      id: connectionId,
      targetId: targetProcess.id,
      sourceHandle: 'right',
      targetHandle: 'left',
      connectionType: 'vsm_fifo',
      manualCommand: true,
      waypoints: undefined,
      manualJumps: undefined,
      mergeToConnection: undefined,
      forceRadial: undefined,
      fifoMaxCapacity: capturedCapacity,
      fifoCurrentWip: capturedWip,
    };

    const nextCards = activeCards
      .filter(card => !intermediateIds.has(card.id))
      .map(card => {
        const cleanedConnections = (card.conexoes_saida ?? []).filter(conn => {
          if (conn.id === connectionId) return false;
          if (intermediateIds.has(conn.targetId)) return false;
          if (intermediateIds.has(card.id)) return false;
          if (card.id === sourceProcess.id && conn.targetId === targetProcess.id) return false;
          return true;
        });

        if (card.id !== sourceProcess.id) {
          return { ...card, conexoes_saida: cleanedConnections };
        }

        return {
          ...card,
          conexoes_saida: [...cleanedConnections, fifoConnection],
        };
      });

    setCards(nextCards);
    setSelectedConnection({ sourceId: sourceProcess.id, connectionId });
    setContextMenu(null);
  }, [activeCards, canEditCanvas, cardById, handleUpdateCardData, setCards, setSelectedConnection]);

  const getStockBadgeTopPosition = useCallback(
    (targetId: string, conn: ProcessedConnection): { x: number; y: number } => {
      const targetCard = cardById.get(targetId);

      if (!targetCard) {
        return {
          x: Math.round(conn.targetPoint.x),
          y: Math.round(conn.targetPoint.y - 24),
        };
      }

      const cardWidth = targetCard.tipo === 'decisao' ? 160 : (targetCard.calculatedWidth || 240);
      const visualTopY = targetCard.tipo === 'decisao' ? targetCard.posicao.y + 10 : targetCard.posicao.y;
      const badgeX = targetCard.posicao.x + cardWidth / 2;
      const badgeY = visualTopY - 14;

      return {
        x: Math.round(badgeX),
        y: Math.max(8, Math.round(badgeY)),
      };
    },
    [cardById]
  );

  const incomingConnectionsByTargetId = useMemo(() => {
    const map = new Map<string, ProcessedConnection[]>();

    processedConnections
      .filter((conn) => conn && conn.id && conn.targetId)
      .forEach((conn) => {
        const targetId = conn.targetId as string;
        const existing = map.get(targetId);

        if (existing) {
          existing.push(conn);
        } else {
          map.set(targetId, [conn]);
        }
      });

    return map;
  }, [processedConnections]);

  const badgeAnchorConnectionIdByTargetId = useMemo(() => {
    const map = new Map<string, string>();

    incomingConnectionsByTargetId.forEach((incomingConnections, targetId) => {
      if (incomingConnections.length === 0) return;

      const anchorConnection = [...incomingConnections].sort((a, b) => {
        if (a.isReturn !== b.isReturn) return a.isReturn ? 1 : -1;

        const aDistance = Math.hypot(a.targetPoint.x - a.sourcePoint.x, a.targetPoint.y - a.sourcePoint.y);
        const bDistance = Math.hypot(b.targetPoint.x - b.sourcePoint.x, b.targetPoint.y - b.sourcePoint.y);

        if (aDistance !== bDistance) return aDistance - bDistance;
        return (a.id || '').localeCompare(b.id || '');
      })[0];

      if (anchorConnection?.id) {
        map.set(targetId, anchorConnection.id);
      }
    });

    return map;
  }, [incomingConnectionsByTargetId]);

  // Estado para drag de card sobre outro card (inser��o autom�tica no fluxo)
  const [draggingCardId, setDraggingCardId] = React.useState<string | null>(null);
  const [dragHighlightPos, setDragHighlightPos] = React.useState<{ x: number; y: number } | null>(null);
  const [cardDropTargetId, setCardDropTargetId] = React.useState<string | null>(null);
  // 'before' = inserir no lugar do target (empurrando), 'after' = inserir depois do target
  const [cardDropSide, setCardDropSide] = React.useState<'before' | 'after'>('after');
  // Refs para leitura s�ncrona dentro de onDragEnd (evita stale-closure)
  const draggingCardIdRef = React.useRef<string | null>(null);
  const cardDropTargetIdRef = React.useRef<string | null>(null);
  const cardDropSideRef = React.useRef<'before' | 'after'>('after');
  // Lado de inser��o para drag da sidebar (left=before, right=after)
  const [sidebarDropSide, setSidebarDropSide] = React.useState<'before' | 'after'>('after');
  const sidebarDropSideRef = React.useRef<'before' | 'after'>('after');
  
  // Estado para edi+�+�o de waypoints de conex+�o
  const [editingWaypoints, setEditingWaypoints] = React.useState<boolean>(false);
  const [draggingWaypointIndex, setDraggingWaypointIndex] = React.useState<number | null>(null);
  const draggingWaypointIndexRef = React.useRef<number | null>(null);
  
  // Estado para preview de hover no menu lateral do card
  const [hoverPreview, setHoverPreview] = React.useState<{
    sourceId: string;
    type: 'atividade' | 'decisao';
    isYesPath?: boolean;
  } | null>(null);
  
  // Cache para cards agrupados (permitir desagrupar)
  const groupedCardsCacheRef = React.useRef<Map<string, CardData[]>>(new Map());
  
  // ��Ļ GRUPOS EXPANDIDOS - Usar store global para integra+�+�o com useEdgeCalculations
  const expandedGroups = useProjectStore(state => state.expandedGroups);
  const expandGroup = useProjectStore(state => state.expandGroup);
  const collapseGroup = useProjectStore(state => state.collapseGroup);
  const updateExpandedGroup = useProjectStore(state => state.expandGroup); // Re-usar para atualizar

  // ��Ļ CARDS DIN+�MICOS DO GRUPO - Calcula baseado em parentGroupId (inclui novos cards adicionados)
  const dynamicExpandedGroups = useMemo(() => {
    const dynamicGroups = new Map<string, CardData[]>();
    
    expandedGroups.forEach((_, subProcessId) => {
      // Buscar TODOS os cards que pertencem a este grupo (usando parentGroupId)
      const groupCards = activeCards.filter(c => c.parentGroupId === subProcessId);
      
      if (groupCards.length > 0) {
        dynamicGroups.set(subProcessId, groupCards);
      } else {
        // Fallback: usar os cards do store se n+�o houver parentGroupId ainda
        const storeCards = expandedGroups.get(subProcessId);
        if (storeCards) {
          dynamicGroups.set(subProcessId, storeCards);
        }
      }
    });
    
    return dynamicGroups;
  }, [expandedGroups, activeCards]);

  // Calcular cards selecionados para agrupamento
  // ���� TRAVA: Nunca permitir agrupamento de cards marcadores (In+�cio/Fim)
  const MARKER_TYPES = ['card_inicial', 'card_final'];
  const selectedCardsForGrouping = useMemo(() => {
    return activeCards.filter(c => 
      selectedCardIds.has(c.id) && 
      !MARKER_TYPES.includes(c.tipo)
    );
  }, [activeCards, selectedCardIds]);
  
  const canGroupCards = selectedCardsForGrouping.length >= 2;

  // Handler para agrupar cards selecionados em sub-processo
  const handleGroupCards = useCallback((name?: string) => {
    if (!canEditCanvas) return;
    if (!canGroupCards) return;
    
    const result = groupCardsIntoSubProcess(
      selectedCardsForGrouping,
      activeCards,
      name || `Sub-processo (${selectedCardsForGrouping.length} atividades)`
    );
    
    if (result) {
      // Cachear cards originais para permitir desagrupamento
      groupedCardsCacheRef.current.set(result.newSubProcess.id, selectedCardsForGrouping.map(c => ({ ...c })));
      setCards(result.updatedCards);
      setSelectedCardIds(new Set([result.newSubProcess.id]));
    }
  }, [canEditCanvas, canGroupCards, selectedCardsForGrouping, activeCards, setCards, setSelectedCardIds]);

  // Handler para expandir um sub-processo (mostrar cards originais)
  const handleExpandGroup = useCallback((cardId: string) => {
    if (!canEditCanvas) return;

    const card = activeCards.find(c => c.id === cardId);
    if (!card || !isGroupedCard(card)) {
      console.warn('[VsmCanvas] Card n+�o +� um sub-processo agrupado:', cardId);
      return;
    }

    // Recuperar cards originais do cache
    const originalCards = groupedCardsCacheRef.current.get(cardId);
    if (!originalCards || originalCards.length === 0) {
      console.warn('[VsmCanvas] Cards originais n+�o encontrados no cache para:', cardId);
      return;
    }

    // ��Ļ Marcar cards originais com parentGroupId para rastreamento din+�mico
    const cardsWithParentId = originalCards.map(c => ({
      ...c,
      parentGroupId: cardId
    }));

    // ��Ļ Usar store global para grupos expandidos
    expandGroup(cardId, cardsWithParentId);

    // Atualizar o card para marcar como expandido E adicionar os cards com parentGroupId
    const updatedCards = activeCards.map(c => {
      if (c.id === cardId) {
        return {
          ...c,
          subProcessData: {
            ...c.subProcessData!,
            isExpanded: true,
          }
        };
      }
      // Marcar cards originais com parentGroupId
      const originalCard = originalCards.find(oc => oc.id === c.id);
      if (originalCard) {
        return { ...c, parentGroupId: cardId };
      }
      return c;
    });
    setCards(updatedCards);

    console.log('[VsmCanvas] Grupo expandido:', cardId, '- Cards vis+�veis:', originalCards.length);
  }, [activeCards, canEditCanvas, setCards, expandGroup]);

  // Handler para minimizar/reagrupar um grupo expandido
  const handleMinimizeGroup = useCallback((subProcessId: string) => {
    if (!canEditCanvas) return;

    console.log('[VsmCanvas] Minimizando grupo:', subProcessId);
    
    // ��Ļ Obter todos os cards que pertencem a este grupo (usando parentGroupId)
    const groupCards = activeCards.filter(c => c.parentGroupId === subProcessId);
    
    // Atualizar o cache com os cards atuais (inclui novos cards adicionados)
    if (groupCards.length > 0) {
      groupedCardsCacheRef.current.set(subProcessId, groupCards.map(c => ({ ...c })));
      
      // Atualizar o groupedCardIds no card de sub-processo
      const groupCardIds = groupCards.map(c => c.id);
      
      // Recalcular m+�tricas agregadas
      let totalLT = 0;
      let totalTT = 0;
      for (const card of groupCards) {
        totalLT += (card.lt ?? 0) * 24 * 60; // Converter dias para minutos
        totalTT += (card.tt ?? 0);
      }
      
      // ��Ļ CALCULAR POSI+�+�O ALINHADA: usar o card mais +� esquerda para alinhar o grupo
      // Isso evita "degraus" nas setas - o grupo fica na mesma linha de entrada
      const leftmostCard = groupCards.reduce((left, card) => 
        card.posicao.x < left.posicao.x ? card : left
      , groupCards[0]);
      
      // A posi+�+�o X ser+� a do card mais +� esquerda
      // A posi+�+�o Y ser+� a do card mais +� esquerda (linha de entrada do fluxo)
      const alignedPosition = {
        x: leftmostCard.posicao.x,
        y: leftmostCard.posicao.y
      };
      
      // Atualizar o card para marcar como minimizado e atualizar groupedCardIds
      const updatedCards = activeCards.map(c => {
        if (c.id === subProcessId) {
          return {
            ...c,
            posicao: alignedPosition, // ��Ļ Posi+�+�o alinhada com a entrada
            groupedCardIds: groupCardIds,
            lt: totalLT / (24 * 60), // Converter de volta para dias
            tt: totalTT,
            subProcessData: {
              ...c.subProcessData!,
              isExpanded: false,
              activityCount: groupCards.length,
              totalLeadTime: totalLT,
              totalTouchTime: totalTT,
              efficiency: totalLT > 0 ? (totalTT / totalLT) * 100 : 0,
              lastSync: new Date().toISOString(),
            }
          };
        }
        return c;
      });
      setCards(updatedCards);
    } else {
      // Fallback: usar cache existente se n+�o houver cards com parentGroupId
      const updatedCards = activeCards.map(c => {
        if (c.id === subProcessId) {
          return {
            ...c,
            subProcessData: {
              ...c.subProcessData!,
              isExpanded: false,
            }
          };
        }
        return c;
      });
      setCards(updatedCards);
    }
    
    // ��Ļ Usar store global para remover grupo expandido
    collapseGroup(subProcessId);

    console.log('[VsmCanvas] Grupo minimizado:', subProcessId, '- Cards no grupo:', groupCards.length);
  }, [activeCards, canEditCanvas, setCards, collapseGroup]);

  // Handler para desagrupar um sub-processo
  const handleUngroupCard = useCallback((cardId: string) => {
    if (!canEditCanvas) return;

    const card = activeCards.find(c => c.id === cardId);
    if (!card || !isGroupedCard(card)) {
      console.warn('[VsmCanvas] Card n+�o +� um sub-processo agrupado:', cardId);
      return;
    }

    // Recuperar cards originais do cache
    const originalCards = groupedCardsCacheRef.current.get(cardId);
    if (!originalCards || originalCards.length === 0) {
      console.warn('[VsmCanvas] Cards originais n+�o encontrados no cache para:', cardId);
      return;
    }

    const updatedCards = ungroupSubProcess(card, activeCards, originalCards);
    if (!updatedCards) {
      console.error('[VsmCanvas] Erro ao desagrupar sub-processo');
      return;
    }

    // Limpar cache e estado de expandido
    groupedCardsCacheRef.current.delete(cardId);
    collapseGroup(cardId);

    // Atualizar cards
    setCards(updatedCards);
    
    // Selecionar cards restaurados
    setSelectedCardIds(new Set(originalCards.map(c => c.id)));
    
    console.log('[VsmCanvas] Sub-processo desagrupado com sucesso:', cardId, '- Cards restaurados:', originalCards.length);
  }, [activeCards, canEditCanvas, setCards, setSelectedCardIds, collapseGroup]);

  // Handler para drag over - detecta card alvo e lado de insercao (antes/depois)
  const handleCanvasDragOver = React.useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (!canEditCanvas) return;

    const edgeDragType = e.dataTransfer.getData('application/vsm-edge-drag');
    e.dataTransfer.dropEffect = edgeDragType ? 'link' : 'copy';

    if (edgeDragType) {
      setDropTargetCardId(null);
      setDropPosition(null);
      setSidebarDropSide('after');
      sidebarDropSideRef.current = 'after';
      return;
    }

    const cardTypeFromWindow = (window as any).__draggingCardType;
    if (cardTypeFromWindow && cardTypeFromWindow !== draggingCardType) {
      setDraggingCardType(cardTypeFromWindow);
    }

    if (!canvasRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const mouseX = (e.clientX - rect.left - pan.x) / scale;
    const mouseY = (e.clientY - rect.top - pan.y) / scale;

    let foundCard: CardData | null = null;
    for (const card of interactableCards) {
      const isDecision = card.tipo === 'decisao';
      const dims = getCardDimensions(card as any, isDecision, false);
      const cardWidth = isDecision ? dims.decisionSize : dims.width;
      const cardHeight = isDecision ? dims.decisionSize : dims.height;
      if (mouseX >= card.posicao.x && mouseX <= card.posicao.x + cardWidth &&
          mouseY >= card.posicao.y && mouseY <= card.posicao.y + cardHeight) {
        foundCard = card;
        break;
      }
    }

    if (foundCard) {
      setDropTargetCardId(foundCard.id);
      if (foundCard.tipo === 'decisao') {
        const cx = foundCard.posicao.x + 80;
        const cy = foundCard.posicao.y + 80;
        const dx = mouseX - cx;
        const dy = mouseY - cy;
        setDropPosition(Math.abs(dx) > Math.abs(dy) ? 'yes' : 'no');
        setSidebarDropSide('after');
        sidebarDropSideRef.current = 'after';
      } else if ((foundCard as any).vsmIcon === 'vertical_flow') {
        setDropPosition('yes');
        setSidebarDropSide('after');
        sidebarDropSideRef.current = 'after';
      } else {
        const fw = foundCard.calculatedWidth || 240;
        const side: 'before' | 'after' = mouseX < foundCard.posicao.x + fw / 2 ? 'before' : 'after';
        setDropPosition('yes');
        setSidebarDropSide(side);
        sidebarDropSideRef.current = side;
      }
    } else {
      setDropTargetCardId(null);
      setDropPosition(null);
      setSidebarDropSide('after');
      sidebarDropSideRef.current = 'after';
    }
  }, [activeCards, canEditCanvas, pan, scale, canvasRef, draggingCardType]);

  // Handler para drop - adiciona card conectado
  const handleCanvasDrop = React.useCallback((e: React.DragEvent) => {
    e.preventDefault();

    if (!canEditCanvas) {
      setDropTargetCardId(null);
      setDropPosition(null);
      setSidebarDropSide('after');
      sidebarDropSideRef.current = 'after';
      setDraggingCardType(null);
      (window as any).__draggingCardType = null;
      return;
    }

    const edgeDragType = e.dataTransfer.getData('application/vsm-edge-drag');
    if (edgeDragType) {
      if (setManualArrowMode) setManualArrowMode(true);
      if (setManualArrowSource) setManualArrowSource(null);
      if (setManualArrowWaypoints) setManualArrowWaypoints([]);
      setDropTargetCardId(null);
      setDropPosition(null);
      setSidebarDropSide('after');
      sidebarDropSideRef.current = 'after';
      setDraggingCardType(null);
      (window as any).__draggingCardType = null;
      return;
    }

    const cardType = e.dataTransfer.getData('application/x-card-type') || draggingCardType;
    if (!cardType) return;

    const side = sidebarDropSideRef.current;

    if (dropTargetCardId) {
      if (side === 'before' && onDropCardBeforeTarget) {
        onDropCardBeforeTarget(cardType, dropTargetCardId);
      } else if (onDropCardOnTarget) {
        onDropCardOnTarget(cardType, dropTargetCardId, dropPosition === 'yes');
      }
    } else if (onDropCardOnCanvas && canvasRef.current) {
      const rect = canvasRef.current.getBoundingClientRect();
      const x = (e.clientX - rect.left - pan.x) / scale;
      const y = (e.clientY - rect.top - pan.y) / scale;
      onDropCardOnCanvas(cardType, x, y);
    }

    setDropTargetCardId(null);
    setDropPosition(null);
    setSidebarDropSide('after');
    sidebarDropSideRef.current = 'after';
    setDraggingCardType(null);
  }, [canEditCanvas, dropTargetCardId, dropPosition, onDropCardOnTarget, onDropCardOnCanvas, onDropCardBeforeTarget, pan, scale, canvasRef, draggingCardType]);

  // Handler para drag leave
  const handleCanvasDragLeave = React.useCallback(() => {
    setDropTargetCardId(null);
    setDropPosition(null);
    setDraggingCardType(null);
  }, []);

  // Handler de ESC para cancelar criacao de seta
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && (manualArrowMode || connectingSource)) {
        e.preventDefault();
        e.stopPropagation();
        cancelManualArrowCreation();
      }
      // ESC tamb+�m sai do modo de edi+�+�o de waypoints
      if (e.key === 'Escape' && editingWaypoints) {
        e.preventDefault();
        e.stopPropagation();
        setEditingWaypoints(false);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [manualArrowMode, connectingSource, cancelManualArrowCreation, editingWaypoints]);

  // ============================================================================
  // FUN+�+�ES DE EDI+�+�O DE WAYPOINTS
  // ============================================================================
  
  /**
   * Obt+�m a conex+�o selecionada com seus waypoints
   */
  const getSelectedConnectionData = useCallback(() => {
    if (!selectedConnection) return null;
    const sourceCard = activeCards.find(c => c.id === selectedConnection.sourceId);
    if (!sourceCard?.conexoes_saida) return null;
    const conn = sourceCard.conexoes_saida.find(c => c.id === selectedConnection.connectionId);
    return conn || null;
  }, [selectedConnection, activeCards]);

  /**
   * Adiciona um waypoint +� conex+�o selecionada
   */
  const handleAddWaypointToConnection = useCallback((x: number, y: number) => {
    if (!selectedConnection) return;
    const sourceCard = activeCards.find(c => c.id === selectedConnection.sourceId);
    if (!sourceCard?.conexoes_saida) return;
    
    const connIndex = sourceCard.conexoes_saida.findIndex(c => c.id === selectedConnection.connectionId);
    if (connIndex === -1) return;
    
    const conn = sourceCard.conexoes_saida[connIndex];
    const newWaypoints = [...(conn.waypoints || []), { x, y }];
    
    // Atualizar a conex+�o com os novos waypoints
    const updatedConexoes = [...sourceCard.conexoes_saida];
    updatedConexoes[connIndex] = { ...conn, waypoints: newWaypoints };
    handleUpdateCardData(sourceCard.id, { conexoes_saida: updatedConexoes });
  }, [selectedConnection, activeCards, handleUpdateCardData]);

  /**
   * Move um waypoint da conex+�o selecionada
   */
  const handleMoveWaypoint = useCallback((waypointIndex: number, newX: number, newY: number) => {
    if (!selectedConnection) return;
    const sourceCard = activeCards.find(c => c.id === selectedConnection.sourceId);
    if (!sourceCard?.conexoes_saida) return;
    
    const connIndex = sourceCard.conexoes_saida.findIndex(c => c.id === selectedConnection.connectionId);
    if (connIndex === -1) return;
    
    const conn = sourceCard.conexoes_saida[connIndex];
    if (!conn.waypoints || !conn.waypoints[waypointIndex]) return;
    
    const newWaypoints = [...conn.waypoints];
    newWaypoints[waypointIndex] = { x: newX, y: newY };
    
    const updatedConexoes = [...sourceCard.conexoes_saida];
    updatedConexoes[connIndex] = { ...conn, waypoints: newWaypoints };
    handleUpdateCardData(sourceCard.id, { conexoes_saida: updatedConexoes });
  }, [selectedConnection, activeCards, handleUpdateCardData]);

  /**
   * Remove um waypoint da conex+�o selecionada
   */
  const handleRemoveWaypoint = useCallback((waypointIndex: number) => {
    if (!selectedConnection) return;
    const sourceCard = activeCards.find(c => c.id === selectedConnection.sourceId);
    if (!sourceCard?.conexoes_saida) return;
    
    const connIndex = sourceCard.conexoes_saida.findIndex(c => c.id === selectedConnection.connectionId);
    if (connIndex === -1) return;
    
    const conn = sourceCard.conexoes_saida[connIndex];
    if (!conn.waypoints) return;
    
    const newWaypoints = conn.waypoints.filter((_, idx) => idx !== waypointIndex);
    
    const updatedConexoes = [...sourceCard.conexoes_saida];
    updatedConexoes[connIndex] = { ...conn, waypoints: newWaypoints.length > 0 ? newWaypoints : undefined };
    handleUpdateCardData(sourceCard.id, { conexoes_saida: updatedConexoes });
  }, [selectedConnection, activeCards, handleUpdateCardData]);

  /**
   * Adiciona um jump manual +� conex+�o selecionada (Ctrl+Click)
   */
  const handleAddManualJump = useCallback((x: number, y: number) => {
    if (!selectedConnection) return;
    const sourceCard = activeCards.find(c => c.id === selectedConnection.sourceId);
    if (!sourceCard?.conexoes_saida) return;
    
    const connIndex = sourceCard.conexoes_saida.findIndex(c => c.id === selectedConnection.connectionId);
    if (connIndex === -1) return;
    
    const conn = sourceCard.conexoes_saida[connIndex];
    const newJumps = [...(conn.manualJumps || []), { x, y }];
    
    const updatedConexoes = [...sourceCard.conexoes_saida];
    updatedConexoes[connIndex] = { ...conn, manualJumps: newJumps };
    handleUpdateCardData(sourceCard.id, { conexoes_saida: updatedConexoes });
    console.log('[JUMP] Jump manual adicionado em', { x, y, totalJumps: newJumps.length });
  }, [selectedConnection, activeCards, handleUpdateCardData]);

  /**
   * Remove um jump manual da conex+�o selecionada
   */
  const handleRemoveManualJump = useCallback((jumpIndex: number) => {
    if (!selectedConnection) return;
    const sourceCard = activeCards.find(c => c.id === selectedConnection.sourceId);
    if (!sourceCard?.conexoes_saida) return;
    
    const connIndex = sourceCard.conexoes_saida.findIndex(c => c.id === selectedConnection.connectionId);
    if (connIndex === -1) return;
    
    const conn = sourceCard.conexoes_saida[connIndex];
    if (!conn.manualJumps) return;
    
    const newJumps = conn.manualJumps.filter((_, idx) => idx !== jumpIndex);
    
    const updatedConexoes = [...sourceCard.conexoes_saida];
    updatedConexoes[connIndex] = { ...conn, manualJumps: newJumps.length > 0 ? newJumps : undefined };
    handleUpdateCardData(sourceCard.id, { conexoes_saida: updatedConexoes });
  }, [selectedConnection, activeCards, handleUpdateCardData]);

  /**
   * Remove todos os waypoints e jumps da conex+�o selecionada (volta +� rota autom+�tica)
   */
  const handleResetConnectionRoute = useCallback(() => {
    if (!selectedConnection) return;
    const sourceCard = activeCards.find(c => c.id === selectedConnection.sourceId);
    if (!sourceCard?.conexoes_saida) return;
    
    const connIndex = sourceCard.conexoes_saida.findIndex(c => c.id === selectedConnection.connectionId);
    if (connIndex === -1) return;
    
    const conn = sourceCard.conexoes_saida[connIndex];
    
    const updatedConexoes = [...sourceCard.conexoes_saida];
    updatedConexoes[connIndex] = { ...conn, waypoints: undefined, manualJumps: undefined };
    handleUpdateCardData(sourceCard.id, { conexoes_saida: updatedConexoes });
    setEditingWaypoints(false);
  }, [selectedConnection, activeCards, handleUpdateCardData]);

  // ============================================================================
  // EVENTOS GLOBAIS PARA ARRASTAR WAYPOINTS
  // ============================================================================
  // Manter ref sincronizado com o state
  React.useEffect(() => {
    draggingWaypointIndexRef.current = draggingWaypointIndex;
  }, [draggingWaypointIndex]);
  
  // Calcular fator de escala total (incluindo contentScale)
  const totalScale = scale * (contentScale / 12);
  
  React.useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => {
      const idx = draggingWaypointIndexRef.current;
      if (idx !== null && canvasRef.current) {
        const rect = canvasRef.current.getBoundingClientRect();
        // CR+�TICO: Usar totalScale (scale * scaleFactor) para coordenadas corretas
        const x = (e.clientX - rect.left - pan.x) / totalScale;
        const y = (e.clientY - rect.top - pan.y) / totalScale;
        handleMoveWaypoint(idx, x, y);
      }
    };
    
    const handleGlobalMouseUp = () => {
      if (draggingWaypointIndexRef.current !== null) {
        setDraggingWaypointIndex(null);
      }
    };
    
    window.addEventListener('mousemove', handleGlobalMouseMove);
    window.addEventListener('mouseup', handleGlobalMouseUp);
    
    return () => {
      window.removeEventListener('mousemove', handleGlobalMouseMove);
      window.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [pan.x, pan.y, totalScale, handleMoveWaypoint]);

  const handleCanvasMouseDown = React.useCallback((e: React.MouseEvent) => {
    // N+�o iniciar pan se clicar em um card ou elemento interativo
    const target = e.target as HTMLElement;
    const isAuxiliaryPanButton = e.button === 1 || e.button === 3 || e.button === 4;
    const isPrimaryMovePan = e.button === 0 && !e.shiftKey && !e.ctrlKey && !e.metaKey && interactionMode === 'move';
    const canStartPan = isAuxiliaryPanButton || isPrimaryMovePan;

    // Fechar qualquer post-it em modo edicao ao clicar fora dele
    if (!target.closest('[data-post-it]')) {
      const activeEl = document.activeElement;
      if (activeEl instanceof HTMLTextAreaElement || activeEl instanceof HTMLInputElement) {
        activeEl.blur();
      }
    }

    // PRIORIDADE ABSOLUTA: Se estiver criando post-it e clicar em um card, N+�O processar NADA aqui
    // O card processar+� o evento e criar+� o post-it vinculado
    if (canEditCanvas && isCreatingPostIt && target.closest('[data-card-id]')) {
      // N+�o fazer NADA aqui - deixar o card processar completamente
      // N+�o chamar preventDefault nem stopPropagation para n+�o interferir
      return;
    }
    
    // Exceção: seta vertical de fluxo é arrastável mesmo em modo Pan (ajuste fino do desenho).
    // Bloquear pan apenas para botão primário; auxiliares ainda iniciam pan.
    if (isPrimaryMovePan) {
      const cardEl = target.closest('[data-card-id]') as HTMLElement | null;
      if (cardEl && cardEl.getAttribute('data-drag-handle-flow') === 'true') {
        return;
      }
    }

    // Botao do meio/lateral precisa iniciar pan mesmo sobre setas/cards.
    if (canStartPan && !target.closest('button') && !target.closest('input') && !target.closest('textarea') && !target.closest('[contenteditable="true"]')) {
      setIsPanning(true);
      panStartRef.current = { x: e.clientX - currentPanRef.current.x, y: e.clientY - currentPanRef.current.y };
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    // Se clicar em um card (sem modo cria+�+�o), deixar o card processar normalmente
    if (target.closest('[data-card-id]')) {
      return;
    }
    
    // N+�o processar se clicar em elementos SVG, bot+�es ou post-its
    if (target.closest('svg path') ||
        target.closest('svg g') ||
        target.closest('button') ||
        target.closest('[data-post-it]')) {
      return;
    }

    // Modo Manual Arrow - clique vazio no canvas cancela a criacao da seta
    if (canEditCanvas && manualArrowMode && manualArrowSource && e.button === 0 && canvasRef.current) {
      const rect = canvasRef.current.getBoundingClientRect();
      const effectiveScale = scale * (contentScale / 12);
      const x = (e.clientX - rect.left - pan.x) / effectiveScale;
      const y = (e.clientY - rect.top - pan.y) / effectiveScale;
      
      // Verificar se clicou em algum card (destino)
      const clickedCard = findProcessCardByViewportPoint(interactableCards, e.clientX, e.clientY, manualArrowSource.id) ??
        findCardAtCanvasPoint(interactableCards, x, y, isTimeScaleActive, manualArrowSource.id);
      
      if (clickedCard) {
        // Clicou em um card - finalizar conex+�o
        handleFinishConnection(clickedCard.id);
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      cancelManualArrowCreation();
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    
    // Se estiver criando post-it e clicar no canvas (n+�o em um card), criar post-it livre
    if (canEditCanvas && isCreatingPostIt && e.button === 0) {
      e.preventDefault();
      e.stopPropagation();
      handleCreatePostItOnCard('', e.clientX, e.clientY);
      // Deselecionar ap+�s criar post-it livre no canvas
      if (setRightPanelMode) {
        setRightPanelMode('none');
      }
      // Limpar sele+�+�o usando a Store (fonte +�nica de verdade)
      const { setSelectedCardId } = useProjectStore.getState();
      setSelectedCardId(null);
      if (onClearSelection) {
        onClearSelection();
      }
      return;
    }
    
    // Fechar barra lateral e deselecionar cards/post-its quando clicar no canvas
    // (sempre que n+�o estiver conectando - nesse caso deve finalizar a conex+�o primeiro)
    if (!connectingSource) {
      if (setRightPanelMode) {
        setRightPanelMode('none');
      }
      // Limpar sele+�+�o do card no store (fonte +�nica de verdade)
      const { setSelectedCardId } = useProjectStore.getState();
      setSelectedCardId(null);
      // Limpar sele+�+�es de cards e post-its
      if (onClearSelection) {
        onClearSelection();
      }
      // Deselecionar conex+�o ao clicar no canvas
      setSelectedConnection(null);
    }
    
    // Sele+�+�o por caixa com Bot+�o Esquerdo no modo select
    if (e.button === 0 && interactionMode === 'select' && !draggingCardId) {
      setIsSelecting(true);
      const rect = canvasRef.current!.getBoundingClientRect();
      const x = (e.clientX - rect.left - pan.x) / scale;
      const y = (e.clientY - rect.top - pan.y) / scale;
      selectionStartRef.current = { x, y };
      setBoxSelection({ startX: x, startY: y, endX: x, endY: y });
      e.preventDefault();
      e.stopPropagation();
    }
  }, [pan, scale, contentScale, activeCards, isTimeScaleActive, canEditCanvas, isCreatingPostIt, handleCreatePostItOnCard, handleFinishConnection, connectingSource, setRightPanelMode, onClearSelection, setBoxSelection, interactionMode, manualArrowMode, manualArrowSource, canvasRef, setSelectedConnection, cancelManualArrowCreation]);

  // Refs para scroll/zoom/pan fluido (direto no DOM, sem re-render)
  const currentScaleRef = React.useRef(scale);
  const currentPanRef = React.useRef(pan);
  const rafIdRef = React.useRef<number | null>(null);
  const rubberBandRafRef = React.useRef<number | null>(null);
  const lastScrollTimeRef = React.useRef<number>(0);
  const isOverscrollingRef = React.useRef(false);
  const contentDivRef = React.useRef<HTMLDivElement>(null);
  const scaleFactorRef = React.useRef(contentScale / 12);
  const gridRef = React.useRef<HTMLDivElement>(null);
  const debounceSyncRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingMousePanRef = React.useRef<{ x: number; y: number } | null>(null);
  const mousePanRafRef = React.useRef<number | null>(null);

  // Atualizar DOM direto do grid (backgroundPosition) sem re-render
  const updateGridDOM = React.useCallback((px: number, py: number) => {
    if (gridRef.current) {
      gridRef.current.style.backgroundPosition = `${px}px ${py}px`;
    }
  }, []);

  const applyPanDOM = React.useCallback((nextPan: { x: number; y: number }) => {
    currentPanRef.current = nextPan;
    if (contentDivRef.current) {
      contentDivRef.current.style.transform = `translate3d(${nextPan.x}px, ${nextPan.y}px, 0) scale(${currentScaleRef.current * scaleFactorRef.current})`;
    }
    updateGridDOM(nextPan.x, nextPan.y);
  }, [updateGridDOM]);

  const scheduleMousePanDOM = React.useCallback((nextPan: { x: number; y: number }) => {
    pendingMousePanRef.current = nextPan;
    if (mousePanRafRef.current !== null) return;

    mousePanRafRef.current = requestAnimationFrame(() => {
      mousePanRafRef.current = null;
      const pendingPan = pendingMousePanRef.current;
      if (!pendingPan) return;
      pendingMousePanRef.current = null;
      applyPanDOM(pendingPan);
    });
  }, [applyPanDOM]);

  // Sincronizar estado React com debounce (s� atualiza depois de 150ms sem intera��o)
  const debouncedSyncState = React.useCallback((newPan?: { x: number; y: number }, newScale?: number) => {
    if (debounceSyncRef.current) clearTimeout(debounceSyncRef.current);
    debounceSyncRef.current = setTimeout(() => {
      if (newPan || currentPanRef.current) setPan(newPan || currentPanRef.current);
      if (newScale !== undefined) setScale(newScale);
      else if (currentScaleRef.current !== scale) setScale(currentScaleRef.current);
    }, 150);
  }, [setPan, setScale, scale]);

  // Sincronizar refs com state
  React.useEffect(() => {
    currentScaleRef.current = scale;
  }, [scale]);

  React.useEffect(() => {
    currentPanRef.current = pan;
  }, [pan]);

  React.useEffect(() => {
    scaleFactorRef.current = contentScale / 12;
  }, [contentScale]);

  // Limpar debounce ao desmontar
  React.useEffect(() => {
    return () => {
      if (debounceSyncRef.current) clearTimeout(debounceSyncRef.current);
    };
  }, []);

  const handleCanvasMouseMove = React.useCallback((e: React.MouseEvent) => {
    // Capturar coordenadas do mouse para o StatusBar
    if (canvasRef.current && onMouseMove) {
      const rect = canvasRef.current.getBoundingClientRect();
      const x = Math.round((e.clientX - rect.left - pan.x) / scale);
      const y = Math.round((e.clientY - rect.top - pan.y) / scale);
      onMouseMove(x, y);
    }

    // Highlight de c�lula do grid durante drag de card
    if (draggingCardId !== null && canvasRef.current) {
      const rect = canvasRef.current.getBoundingClientRect();
      const SNAP = 10;
      const wx = (e.clientX - rect.left - pan.x) / scale;
      const wy = (e.clientY - rect.top - pan.y) / scale;
      setDragHighlightPos({
        x: Math.floor(wx / SNAP) * SNAP,
        y: Math.floor(wy / SNAP) * SNAP,
      });
    }

    // Rastrear posi��o do cursor para o ghost post-it em modo cria��o
    if (isCreatingPostIt && canvasRef.current) {
      const rect = canvasRef.current.getBoundingClientRect();
      setCursorCanvasPos({
        x: (e.clientX - rect.left - pan.x) / scale,
        y: (e.clientY - rect.top - pan.y) / scale,
      });
    }

    // Arrastar waypoint
    if (draggingWaypointIndex !== null && canvasRef.current) {
      const rect = canvasRef.current.getBoundingClientRect();
      const x = (e.clientX - rect.left - pan.x) / scale;
      const y = (e.clientY - rect.top - pan.y) / scale;
      handleMoveWaypoint(draggingWaypointIndex, x, y);
    }

    if (isPanning && panStartRef.current) {
      const newPanX = e.clientX - panStartRef.current.x;
      const newPanY = e.clientY - panStartRef.current.y;

      // Pan por mouse: agrupa eventos de alta frequência em um frame.
      scheduleMousePanDOM({ x: newPanX, y: newPanY });
      debouncedSyncState({ x: newPanX, y: newPanY });
    } else if (isSelecting && !draggingCardId && selectionStartRef.current && canvasRef.current) {
      const rect = canvasRef.current.getBoundingClientRect();
      const x = (e.clientX - rect.left - pan.x) / scale;
      const y = (e.clientY - rect.top - pan.y) / scale;
      setBoxSelection({
        ...boxSelection!,
        endX: x,
        endY: y
      });
    }
  }, [isPanning, isSelecting, pan.x, pan.y, scale, setBoxSelection, boxSelection, onMouseMove, isCreatingPostIt, debouncedSyncState, scheduleMousePanDOM, draggingCardId, setDragHighlightPos]);

  // Limpar highlight ao terminar drag
  React.useEffect(() => {
    if (draggingCardId === null) {
      setDragHighlightPos(null);
    } else {
      // Drag de card iniciou: cancelar qualquer caixa de seleção ativa
      setIsSelecting(false);
      setBoxSelection(null);
      selectionStartRef.current = null;
    }
  }, [draggingCardId]);

  const handleCanvasMouseUp = React.useCallback(() => {
    if (isSelecting && boxSelection) {
      // Finalizar sele+�+�o
      const minX = Math.min(boxSelection.startX, boxSelection.endX);
      const maxX = Math.max(boxSelection.startX, boxSelection.endX);
      const minY = Math.min(boxSelection.startY, boxSelection.endY);
      const maxY = Math.max(boxSelection.startY, boxSelection.endY);

      const newlySelectedCards = new Set<string>();
        interactableCards.forEach(card => {
          const x = card.posicao.x;
          const y = card.posicao.y;
          const { width, height } = getCanvasHitBox(card, isTimeScaleActive);

          // Verificar se o card est+� dentro da caixa
          if (x + width > minX && x < maxX && y + height > minY && y < maxY) {
            newlySelectedCards.add(card.id);
          }
        });

        const newlySelectedPostIts = new Set<string>();
        postIts.forEach(p => {
          if (!p.fixedToNodeId) {
            if (p.x + 160 > minX && p.x < maxX && p.y + 160 > minY && p.y < maxY) {
              newlySelectedPostIts.add(p.id);
            }
          }
        });

        if (newlySelectedCards.size > 0 || newlySelectedPostIts.size > 0) {
          setSelectedCardIds(newlySelectedCards);
          setSelectedPostItIds(newlySelectedPostIts);
          
          // Se selecionou apenas um card, abrir propriedades
          if (newlySelectedCards.size === 1 && setRightPanelMode) {
            const cardId = Array.from(newlySelectedCards)[0];
            const { setSelectedCardId } = useProjectStore.getState();
            setSelectedCardId(cardId);
            setRightPanelMode('properties');
          } else if (newlySelectedPostIts.size === 1 && setRightPanelMode) {
            setRightPanelMode('kaizen');
          }
        }
    }

    setIsPanning(false);
    if (mousePanRafRef.current !== null) {
      cancelAnimationFrame(mousePanRafRef.current);
      mousePanRafRef.current = null;
    }
    if (pendingMousePanRef.current) {
      applyPanDOM(pendingMousePanRef.current);
      pendingMousePanRef.current = null;
    }
    setIsSelecting(false);
    panStartRef.current = null;
    selectionStartRef.current = null;
    setBoxSelection(null);
    setDraggingWaypointIndex(null); // Reset dragging waypoint
  }, [isSelecting, boxSelection, activeCards, postIts, setSelectedCardIds, setSelectedPostItIds, setBoxSelection, setRightPanelMode, applyPanDOM, isTimeScaleActive]);

  // Handler para menu de contexto (bot+�o direito)
  const handleContextMenu = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    
    // Verificar se clicou em uma edge (seta/linha)
    const target = e.target as SVGElement;
    const edgeGroup = target.closest('g[data-edge]') as Element | null;
    const isEdge = target.classList.contains('react-flow__edge-path') || 
                   (target.tagName === 'path' && edgeGroup);
    
    // Se clicou em uma edge, deixar o handler da edge processar
    // O handler da edge j+� tem seu pr+�prio onContextMenu que seta selectedConnection
    if (isEdge && edgeGroup) {
      // N+�o fazer nada aqui - o evento vai ser tratado pelo onContextMenu da edge
      // que j+� faz stopPropagation
      return;
    }
    
    const cardElement = target.closest('[data-card-id]') as HTMLElement | null;
    const cardId = cardElement?.dataset.cardId;
    const shouldOpenSelectionMenu = Boolean(cardId && selectedCardIds.has(cardId) && canCreateContinuousFlowCell);

    // Verificar se clicou em card ou post-it (n+�o abrir menu do canvas)
    if ((cardElement && !shouldOpenSelectionMenu) || target.closest('[data-post-it]')) {
      return;
    }

    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      type: 'canvas'
    });
  }, [canCreateContinuousFlowCell, selectedCardIds]);

  const handleSelectedCardContextMenu = React.useCallback((cardId: string, e: React.MouseEvent) => {
    if (!selectedCardIds.has(cardId) || !canCreateContinuousFlowCell) {
      return false;
    }

    e.preventDefault();
    e.stopPropagation();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      type: 'canvas'
    });
    return true;
  }, [canCreateContinuousFlowCell, selectedCardIds]);

  // Fun+�+�o para animar o retorno do rubber band
  // Rubber band desativado ��� pan livre sem limites
  const animateRubberBandReturn = React.useCallback(() => {
    // Noop: canvas sem limites de movimenta+�+�o
    isOverscrollingRef.current = false;
  }, []);

  // Rubber band check desativado ��� pan livre sem limites
  React.useEffect(() => {
    // Noop: sem limites de pan, n+�o precisa verificar retorno
  }, [animateRubberBandReturn]);

  const handleWheel = React.useCallback((e: WheelEvent) => {
    e.preventDefault();
    lastScrollTimeRef.current = Date.now();
    if (rubberBandRafRef.current !== null) {
      cancelAnimationFrame(rubberBandRafRef.current);
      rubberBandRafRef.current = null;
    }
    const currentScale = currentScaleRef.current;
    const currentPan = currentPanRef.current;
    const isTouchpad = e.deltaMode === 0 && Math.abs(e.deltaY) < 50;
    const isPinchZoom = e.ctrlKey || e.metaKey;
    if (!isPinchZoom) {
      // Pan: DOM direto + debounced state sync (zero re-renders durante scroll)
      const panSpeed = isTouchpad ? 1 : 2;
      const newPan = {
        x: currentPan.x - e.deltaX * panSpeed,
        y: currentPan.y - e.deltaY * panSpeed
      };
      currentPanRef.current = newPan;
      if (contentDivRef.current) {
        contentDivRef.current.style.transform = `translate3d(${newPan.x}px, ${newPan.y}px, 0) scale(${currentScale * scaleFactorRef.current})`;
      }
      updateGridDOM(newPan.x, newPan.y);
      debouncedSyncState(newPan);
      return;
    }
    // Zoom centrado no cursor: DOM direto + debounced state sync
    const delta = e.deltaY;
    const zoomIntensity = isTouchpad ? 0.012 : 0.006;
    let newScale = currentScale * Math.exp(-delta * zoomIntensity);
    newScale = Math.max(0.2, Math.min(newScale, 3));
    if (canvasRef.current) {
      const rect = canvasRef.current.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      const dx = (mouseX - currentPan.x) / currentScale;
      const dy = (mouseY - currentPan.y) / currentScale;
      const newPan = {
        x: mouseX - dx * newScale,
        y: mouseY - dy * newScale
      };
      currentScaleRef.current = newScale;
      currentPanRef.current = newPan;
      if (contentDivRef.current) {
        contentDivRef.current.style.transform = `translate3d(${newPan.x}px, ${newPan.y}px, 0) scale(${newScale * scaleFactorRef.current})`;
      }
      updateGridDOM(newPan.x, newPan.y);
      debouncedSyncState(newPan, newScale);
    }
  }, [debouncedSyncState, updateGridDOM]);

  // Registrar wheel listener com passive: false para permitir preventDefault
  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      canvas.removeEventListener('wheel', handleWheel);
      // Limpar RAF pendente
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      // Limpar rubber band RAF pendente
      if (rubberBandRafRef.current !== null) {
        cancelAnimationFrame(rubberBandRafRef.current);
        rubberBandRafRef.current = null;
      }
      if (mousePanRafRef.current !== null) {
        cancelAnimationFrame(mousePanRafRef.current);
        mousePanRafRef.current = null;
      }
    };
  }, [handleWheel]);

  React.useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (isPanning && panStartRef.current) {
        const newPanX = e.clientX - panStartRef.current.x;
        const newPanY = e.clientY - panStartRef.current.y;
        scheduleMousePanDOM({ x: newPanX, y: newPanY });
        debouncedSyncState({ x: newPanX, y: newPanY });
      } else if (isSelecting && selectionStartRef.current && canvasRef.current) {
        const rect = canvasRef.current.getBoundingClientRect();
        const x = (e.clientX - rect.left - pan.x) / scale;
        const y = (e.clientY - rect.top - pan.y) / scale;
        setBoxSelection({
          startX: selectionStartRef.current.x,
          startY: selectionStartRef.current.y,
          endX: x,
          endY: y
        });
      }
    };

    const handleGlobalMouseUp = () => {
      handleCanvasMouseUp();
    };

    const handleGlobalClick = () => {
      // Fechar menu de contexto ao clicar em qualquer lugar
      if (contextMenu) {
        setContextMenu(null);
      }
    };

    if (isPanning || isSelecting) {
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
    }

    if (contextMenu) {
      document.addEventListener('click', handleGlobalClick);
    }

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
      document.removeEventListener('click', handleGlobalClick);
    };
  }, [isPanning, isSelecting, pan.x, pan.y, scale, setBoxSelection, handleCanvasMouseUp, contextMenu, debouncedSyncState, scheduleMousePanDOM]);

  const effectiveFocusIds = highlightedPath ?? (executiveMode ? executiveFocusIds : null);
  const effectiveFocusSet = React.useMemo(() => new Set(effectiveFocusIds ?? []), [effectiveFocusIds]);
  const getExecutiveOpacity = React.useCallback((cardId: string) => {
    if (!effectiveFocusIds || effectiveFocusIds.length === 0) return 1;
    return effectiveFocusSet.has(cardId) ? 1 : executiveMode ? 0.14 : 0.1;
  }, [effectiveFocusIds, effectiveFocusSet, executiveMode]);

  const renderedCards = useMemo(() => {
    if (!activeCards || activeCards.length === 0) return null;
    
    const isKaizenMode = useProjectStore.getState().isKaizenMode;
    const viewMode = useProjectStore.getState().viewMode;
    
    // ?? FILTRAR CARDS BASEADO NO MODO DE VISUALIZA��O
    let visibleCards = activeCards;

    if (viewMode === 'future_simulated') {
      // MODO FUTURO: ocultar stocks bloqueados, cards marcados, e membros de células (V202: substituídos pelo card Célula unificado)
      // V206: vsmLocked NÃO oculta — PCPM permanece visível com slash; estoques internos da célula (vsmFlowCellId em estoque) também são ocultados
      // V207: comunicacao label boxes ancoradas em setas do PCPM também são ocultadas (todas — no futuro PCPM não emite setas)
      if (useProjectStore.getState().viewMode === 'future_simulated') {
        // Somente PCPM silenciado — nivelamento (OXOX) mantém seus sinais de puxada
        const pcpmIds = new Set(
          activeCards
            .filter((c: any) => (c as any).vsmIcon === 'pcpm')
            .map(c => c.id)
        );
        const removedConnIds = new Set<string>();
        activeCards.forEach((c: any) => {
          if (!pcpmIds.has(c.id)) return;
          // V208.1: apenas conexões vsm_info do PCPM são removidas; vsm_pull_signal (→ Expedição) é mantido
          (c.conexoes_saida ?? []).forEach((rc: any) => {
            if ((rc as any).connectionType !== 'vsm_pull_signal') {
              removedConnIds.add(rc.id as string);
            }
          });
        });
        // V208: pre-build cell member IDs to detect stocks adjacent to a cell
        const cellMemberIdSet = new Set(
          activeCards.filter(c => (c as any).vsmFlowCellId && !(c as any).vsmFlowCellBlocked).map(c => c.id)
        );
        // For each estoque card, check if any connected card (in or out) is a cell member
        const isAdjacentToCell = (c: CardData): boolean => {
          if (c.tipo !== 'inventory' || (c as any).vsmIcon !== 'estoque') return false;
          // V208.1: converted stocks (supermercado/pulmão) are future-state objects — keep them visible
          if ((c as any).vsmConvertedToIcon) return false;
          if ((c.conexoes_saida ?? []).some((rc: any) => cellMemberIdSet.has(rc.targetId))) return true;
          return activeCards.some(src => cellMemberIdSet.has(src.id) &&
            (src.conexoes_saida ?? []).some((rc: any) => rc.targetId === c.id));
        };

        const futHidden = new Set(
          activeCards
            .filter(c =>
              (c as any).vsmFlowCellBlocked ||
              (c as any).vsmFuturoHide ||
              // V202: process cards membros de célula são ocultados — frame vira card unificado
              ((c as any).vsmFlowCellId && !(c as any).vsmFlowCellBlocked) ||
              // V206: estoques internos à célula (triângulos intermediários eliminados no fluxo contínuo)
              (c.tipo === 'inventory' && (c as any).vsmFlowCellId) ||
              // V208: estoques estoque adjacentes a membros de célula (entre processo oculto e externo)
              isAdjacentToCell(c) ||
              // V207/V209: labels de comunicação ancoradas em setas PCPM — ocultar por connectionId removido OU por sourceCardId PCPM
              ((c as any).vsmIcon === 'comunicacao' && (
                removedConnIds.has((c as any).vsmBoundConnection?.connectionId ?? '') ||
                pcpmIds.has((c as any).vsmBoundConnection?.sourceId ?? '')
              ))
            )
            .map(c => c.id)
        );
        visibleCards = activeCards.filter(c => c.status !== 'removido' && !futHidden.has(c.id));
      }
    } else if (viewMode === 'current_clean') {
      // MODO ATUAL (Mapa): Ocultar cards removidos (exclus�o f�sica j� ocorre, mas filtrar por seguran�a)
      visibleCards = activeCards.filter(c => c.status !== 'removido');
    }
    // viewMode === 'kaizen_work': Mostrar tudo normalmente (removidos aparecem como fantasma)
    visibleCards = visibleCards.filter(c => !isCancelledProgramInfoCard(c));
    visibleCards = visibleCards.filter(c => !hiddenFifoSymbolCardIds.has(c.id));
    
    // ��Ļ IDENTIFICAR GRUPOS EXPANDIDOS E CARDS QUE PERTENCEM A ELES
    const expandedSubProcessIds = new Set(dynamicExpandedGroups.keys());
    
    // Criar mapa de cardId -> subProcessId (para cards que fazem parte de grupos expandidos)
    const cardToGroupMap = new Map<string, string>();
    dynamicExpandedGroups.forEach((cards, subProcessId) => {
      cards.forEach(card => {
        cardToGroupMap.set(card.id, subProcessId);
      });
    });
    
    // Renderizar cards normais (OCULTAR sub-processos expandidos E cards de grupos)
    const cardElements = visibleCards
      .filter(c => {
        // Ocultar sub-processos que est+�o expandidos (GroupContainer substitui)
        const isSubProcessCard = c.tipo === 'sub_processo' && isGroupedCard(c);
        const isCardExpanded = isSubProcessCard && expandedSubProcessIds.has(c.id);
        
        // ��Ļ Ocultar cards que pertencem a grupos expandidos (ser+�o renderizados em expandedCardElements)
        const belongsToExpandedGroup = c.parentGroupId && expandedSubProcessIds.has(c.parentGroupId);
        
        return !isCardExpanded && !belongsToExpandedGroup; // N+�o renderizar se expandido ou pertence a grupo
      })
      .map((c) => {
      // Defesa final: nunca renderizar card que está marcado como não-interativo
      // (evita handles fantasma e cliques acidentais em cards ocultos no modo Futuro).
      if (nonInteractableCardIds.has(c.id)) return null;
      const linkedPostIts = postIts.filter(p => p.fixedToNodeId === c.id && !p.isMinimized);
      const linkedActions = getActionsByNode ? getActionsByNode(c.id) : [];
      
      // Verificar se este card +� um sub-processo (minimizado)
      const isSubProcessCard = c.tipo === 'sub_processo' && isGroupedCard(c);
      const cachedCards = isSubProcessCard ? groupedCardsCacheRef.current.get(c.id) : undefined;
      const isCardExpanded = false; // Nunca expandido aqui (filtrado acima)
      
      return (
        <React.Fragment key={c.id}>
          <Card
            data={c} selected={(!selectedCardIsFlowCellScoped && selectedCardId === c.id) || selectedCardIds.has(c.id)}
            onSelect={(id, e) => {
              if (canEditCanvas && engineeringTool === 'kaizen' && isContinuousFlowProcess(c)) {
                e?.stopPropagation?.();
                handleCardSelect(id, e);
                if (setRightPanelMode) setRightPanelMode('properties');
                openKaizenProjection(c);
                return;
              }
              // Se modo manual ativo sem source, clicar no card define o source
              if (canEditCanvas && manualArrowMode && !manualArrowSource) {
                handleStartConnection(id, 'right');
                return;
              }
              handleCardSelect(id, e);
              // A abertura da barra de propriedades +� feita no handleCardSelect do App.tsx
            }}
            onCardContextMenu={handleSelectedCardContextMenu}
            onEdit={() => {}} onDeleteTotal={canEditCanvas ? handleDeleteTotal : (() => {})} onMarkX={canEditCanvas ? handleMarkX : (() => {})}
            onDuplicate={canEditCanvas ? handleDuplicateCard : (() => {})} 
            onUngroup={canEditCanvas ? handleUngroupCard : (() => {})}
            onToggleExpand={(cachedCards || isCardExpanded) ? (cardId, expanded) => {
              if (!canEditCanvas) return;
              if (expanded) {
                handleExpandGroup(cardId);
              } else {
                handleMinimizeGroup(cardId);
              }
            } : undefined}
            isExpanded={isCardExpanded}
            onAddNext={canEditCanvas ? handleAddNext : (() => {})}
            onAddVsmNext={canEditCanvas ? onDropCardOnTarget : undefined}
            onHoverAddPreview={(sourceId, type, isYesPath) => {
              if (!canEditCanvas) return;
              if (sourceId && type) {
                setHoverPreview({ sourceId, type, isYesPath });
              } else {
                setHoverPreview(null);
              }
            }}
            onDragStart={(offsetX, offsetY) => {
              if (!canEditCanvas) return;
              handleCardDragStart(offsetX, offsetY, c.id);
              setDraggingCardId(c.id);
              draggingCardIdRef.current = c.id;
            }}
            onDrag={(clientX, clientY) => {
              if (!canEditCanvas) return;
              handleMove(clientX, clientY);
              if (!canvasRef.current) return;
              const isDraggingCommunication = (c as any).vsmIcon === 'comunicacao';
              if (isDraggingCommunication) {
                if (cardDropTargetIdRef.current !== null) {
                  setCardDropTargetId(null);
                  cardDropTargetIdRef.current = null;
                }
                return;
              }
              const rect = canvasRef.current.getBoundingClientRect();
              const sf = contentScale / 12;
              const totalScale = scale * sf;
              const mouseX = (clientX - rect.left - pan.x) / totalScale;
              const mouseY = (clientY - rect.top - pan.y) / totalScale;
              let found: string | null = null;
              let side: 'before' | 'after' = 'after';
              for (const other of interactableCards) {
                if (other.id === c.id) continue;
                if (other.tipo === 'card_inicial' || other.tipo === 'card_final') continue;
                if ((other as any).vsmIcon === 'comunicacao') continue;
                const w = other.tipo === 'decisao' ? 160 : (other.calculatedWidth || 240);
                const h = 160;
                if (mouseX >= other.posicao.x && mouseX <= other.posicao.x + w &&
                    mouseY >= other.posicao.y && mouseY <= other.posicao.y + h) {
                  found = other.id;
                  // Metade esquerda ? inserir ANTES (empurrar); metade direita ? inserir DEPOIS
                  side = mouseX < other.posicao.x + w / 2 ? 'before' : 'after';
                  break;
                }
              }
              // Apenas dispara setState quando o valor muda (evita re-render por frame)
              if (cardDropTargetIdRef.current !== found) {
                setCardDropTargetId(found);
                cardDropTargetIdRef.current = found;
              }
              if (cardDropSideRef.current !== side) {
                setCardDropSide(side);
                cardDropSideRef.current = side;
              }
            }}
            onDragEnd={() => {
              if (!canEditCanvas) return;
              // Ler dos refs para garantir valor atual (evita stale-closure de estado React)
              const dragId = draggingCardIdRef.current;
              const targetId = cardDropTargetIdRef.current;
              const side = cardDropSideRef.current;
              const draggedCard = dragId ? activeCards.find(tc => tc.id === dragId) : null;
              const isCommunicationDrag = (draggedCard as any)?.vsmIcon === 'comunicacao';

              if (!isCommunicationDrag && dragId && targetId && dragId !== targetId) {
                const targetCard = activeCards.find(tc => tc.id === targetId);
                if (targetCard) {
                  const offset = targetCard.tipo === 'decisao' ? 180 : 260;

                  if (side === 'after') {
                    // -- MODO AFTER: A ? C ? B (C inserido � direita do target) -----------
                    const newPos = { x: targetCard.posicao.x + offset, y: targetCard.posicao.y };
                    const existingFwdConn = (targetCard.conexoes_saida || []).find(
                      conn => conn.targetId !== dragId
                    );
                    const nextCardId = existingFwdConn?.targetId ?? null;

                    // BFS: cards downstream de nextCardId ? empurrar � direita
                    const cardsToShift = new Set<string>();
                    if (nextCardId) {
                      const q = [nextCardId];
                      while (q.length > 0) {
                        const cur = q.shift()!;
                        if (!cardsToShift.has(cur)) {
                          cardsToShift.add(cur);
                          const curCard = activeCards.find(a => a.id === cur);
                          if (curCard) (curCard.conexoes_saida || []).forEach(conn => {
                            if (!cardsToShift.has(conn.targetId)) q.push(conn.targetId);
                          });
                        }
                      }
                    }

                    const updatedCards = activeCards.map(ca => {
                      if (ca.id === dragId) {
                        const newConns = [...(ca.conexoes_saida || []).filter(cn => cn.targetId !== nextCardId)];
                        if (nextCardId) newConns.push({
                          id: `conn_${dragId}_${nextCardId}_${Date.now()}`,
                          targetId: nextCardId,
                          tipo: 'fluxo' as const,
                          waypoints: [] as {x: number; y: number}[],
                        });
                        return { ...ca, posicao: newPos, conexoes_saida: newConns };
                      }
                      if (ca.id === targetId) {
                        const filtered = (ca.conexoes_saida || []).filter(
                          cn => cn.targetId !== dragId && cn.targetId !== nextCardId
                        );
                        return { ...ca, conexoes_saida: [...filtered, {
                          id: `conn_${targetId}_${dragId}_${Date.now()}`,
                          targetId: dragId,
                          tipo: 'fluxo' as const,
                          waypoints: [] as {x: number; y: number}[],
                        }]};
                      }
                      if (cardsToShift.has(ca.id))
                        return { ...ca, posicao: { x: ca.posicao.x + offset, y: ca.posicao.y } };
                      if (ca.conexoes_saida)
                        return { ...ca, conexoes_saida: ca.conexoes_saida.filter(cn => cn.targetId !== dragId) };
                      return ca;
                    });
                    setCards(updatedCards);

                  } else {
                    // -- MODO BEFORE: C ocupa o lugar do target, empurrando target + downstream --
                    const cPos = { x: targetCard.posicao.x, y: targetCard.posicao.y };

                    // BFS: target + todos seus downstream ? empurrar � direita
                    const cardsToShift = new Set<string>();
                    const q2 = [targetId];
                    while (q2.length > 0) {
                      const cur = q2.shift()!;
                      if (!cardsToShift.has(cur)) {
                        cardsToShift.add(cur);
                        const curCard = activeCards.find(a => a.id === cur);
                        if (curCard) (curCard.conexoes_saida || []).forEach(conn => {
                          if (!cardsToShift.has(conn.targetId)) q2.push(conn.targetId);
                        });
                      }
                    }

                    const updatedCards = activeCards.map(ca => {
                      if (ca.id === dragId) {
                        // C recebe posi��o do target e aponta para o target
                        const newConns = [
                          ...(ca.conexoes_saida || []).filter(cn => cn.targetId !== targetId),
                          {
                            id: `conn_${dragId}_${targetId}_${Date.now()}`,
                            targetId: targetId,
                            tipo: 'fluxo' as const,
                            waypoints: [] as {x: number; y: number}[],
                          },
                        ];
                        return { ...ca, posicao: cPos, conexoes_saida: newConns };
                      }
                      // Predecessores do target passam a apontar para C
                      if (ca.id !== dragId && ca.conexoes_saida) {
                        const hasConnToTarget = ca.conexoes_saida.some(cn => cn.targetId === targetId);
                        if (hasConnToTarget && !cardsToShift.has(ca.id)) {
                          return {
                            ...ca,
                            conexoes_saida: ca.conexoes_saida.map(cn =>
                              cn.targetId === targetId
                                ? { ...cn, targetId: dragId, id: `conn_${ca.id}_${dragId}_${Date.now()}`, waypoints: [] }
                                : cn
                            ),
                          };
                        }
                      }
                      // Empurrar target e seus downstream para a direita
                      if (cardsToShift.has(ca.id))
                        return { ...ca, posicao: { x: ca.posicao.x + offset, y: ca.posicao.y } };
                      return ca;
                    });
                    setCards(updatedCards);
                  }
                }
              }
              // Limpar tudo (estado + refs)
              setDraggingCardId(null);     draggingCardIdRef.current = null;
              setCardDropTargetId(null);   cardDropTargetIdRef.current = null;
              setCardDropSide('after');    cardDropSideRef.current = 'after';
              if (handleCardDragEnd) handleCardDragEnd();
            }}
            scale={scale} pan={pan} isConnecting={canEditCanvas && (!!connectingSource || (manualArrowMode && !!manualArrowSource))}
            onStartConnection={(sourceId, handle) => {
              if (!canEditCanvas) return;
              if (manualArrowMode && !manualArrowSource && setManualArrowSource) {
                setManualArrowSource({ id: sourceId, handle });
              }
              handleStartConnection(sourceId, handle);
            }}
            onFinishConnection={(targetId) => {
              if (!canEditCanvas) return;
              if (manualArrowMode && manualArrowSource && manualArrowSource.id !== targetId) {
                handleFinishConnection(targetId);
                if (setManualArrowSource) setManualArrowSource(null);
                if (setManualArrowWaypoints) setManualArrowWaypoints([]);
              } else if (connectingSource) {
                handleFinishConnection(targetId);
                if (manualArrowMode && setManualArrowSource) setManualArrowSource(null);
                if (manualArrowMode && setManualArrowWaypoints) setManualArrowWaypoints([]);
              }
            }}
            manualArrowMode={canEditCanvas && manualArrowMode}
            manualArrowSource={canEditCanvas ? manualArrowSource : null}
            opacity={getExecutiveOpacity(c.id)}
            isBottleneck={Boolean((analysisMode && storeMetrics?.realBottleneck?.id === c.id) || fifoBlockedSourceIds.has(c.id))}
            isCreatingPostIt={canEditCanvas && isCreatingPostIt} postItCursor={postItCursor} onCreatePostItOnCard={canEditCanvas ? handleCreatePostItOnCard : (() => {})}
            onUpdateCardData={handleUpdateCardData} analysisMode={analysisMode}
            linkedPostIts={linkedPostIts} onPostItClick={handlePostItClick}
            onPostItContextMenu={openContextMenu} onPostItTextChange={handlePostItTextChange}
            onOpenInMemo={handleOpenInMemo} linkedActions={linkedActions}
            onOpenImpactPanel={canEditCanvas ? onOpenImpactPanel : undefined}
            isTimeScaleActive={isTimeScaleActive}
            dragEnabled={canEditCanvas && (interactionMode === 'select' || (c as any).vsmIcon === 'vertical_flow' || (!!(c as any).transportDockParentId && ['caminhao','transporte'].includes((c as any).vsmIcon ?? '')))}
            isKaizenMode={isKaizenMode}
            viewMode={viewMode}
            heatmapMode={heatmapMode}
            showPercentBadge={showPercentBadge || (simulation?.isSimulationMode ?? false)}
            showAS={showAS}
            showFTE={showFTE}
            liveMetrics={liveMetricsMap?.get(c.atividade || '') ?? null}
          />
        </React.Fragment>
      );
    });
    
    // ��Ļ RENDERIZAR CARDS DE GRUPOS EXPANDIDOS (usando dynamicExpandedGroups)
    const expandedCardElements: React.ReactNode[] = [];
    dynamicExpandedGroups.forEach((cards, subProcessId) => {
      const subProcessCard = activeCards.find(c => c.id === subProcessId);
      const groupName = subProcessCard?.atividade || 'Sub-processo';
      
      cards.forEach(card => {
        const linkedPostIts = postIts.filter(p => p.fixedToNodeId === card.id && !p.isMinimized);
        const linkedActions = getActionsByNode ? getActionsByNode(card.id) : [];
        
        expandedCardElements.push(
          <React.Fragment key={`expanded-${card.id}`}>
            <Card
              data={card} selected={(!selectedCardIsFlowCellScoped && selectedCardId === card.id) || selectedCardIds.has(card.id)}
              onSelect={(id, e) => {
                if (canEditCanvas && engineeringTool === 'kaizen' && isContinuousFlowProcess(card)) {
                  e?.stopPropagation?.();
                  handleCardSelect(id, e);
                  if (setRightPanelMode) setRightPanelMode('properties');
                  openKaizenProjection(card);
                  return;
                }
                if (canEditCanvas && manualArrowMode && !manualArrowSource) {
                  handleStartConnection(id, 'right');
                  return;
                }
                handleCardSelect(id, e);
              }}
              onCardContextMenu={handleSelectedCardContextMenu}
              onEdit={() => {}} onDeleteTotal={canEditCanvas ? handleDeleteTotal : (() => {})} onMarkX={canEditCanvas ? handleMarkX : (() => {})}
              onDuplicate={canEditCanvas ? handleDuplicateCard : (() => {})} 
              onAddNext={canEditCanvas ? handleAddNext : (() => {})}
              onAddVsmNext={canEditCanvas ? onDropCardOnTarget : undefined}
              onHoverAddPreview={(sourceId, type, isYesPath) => {
                if (!canEditCanvas) return;
                if (sourceId && type) {
                  setHoverPreview({ sourceId, type, isYesPath });
                } else {
                  setHoverPreview(null);
                }
              }}
              onDragStart={(offsetX, offsetY) => { if (canEditCanvas) handleCardDragStart(offsetX, offsetY, card.id); }}
              onDrag={canEditCanvas ? handleMove : (() => {})} onDragEnd={canEditCanvas ? (handleCardDragEnd || (() => {})) : (() => {})}
              scale={scale} pan={pan} isConnecting={canEditCanvas && (!!connectingSource || (manualArrowMode && !!manualArrowSource))}
              onStartConnection={(sourceId, handle) => {
                if (!canEditCanvas) return;
                if (manualArrowMode && !manualArrowSource && setManualArrowSource) {
                  setManualArrowSource({ id: sourceId, handle });
                }
                handleStartConnection(sourceId, handle);
              }}
              onFinishConnection={(targetId) => {
                if (!canEditCanvas) return;
                if (manualArrowMode && manualArrowSource && manualArrowSource.id !== targetId) {
                  handleFinishConnection(targetId);
                  if (setManualArrowSource) setManualArrowSource(null);
                  if (setManualArrowWaypoints) setManualArrowWaypoints([]);
                } else if (connectingSource) {
                  handleFinishConnection(targetId);
                  if (manualArrowMode && setManualArrowSource) setManualArrowSource(null);
                  if (manualArrowMode && setManualArrowWaypoints) setManualArrowWaypoints([]);
                }
              }}
              manualArrowMode={canEditCanvas && manualArrowMode}
              manualArrowSource={canEditCanvas ? manualArrowSource : null}
              opacity={getExecutiveOpacity(card.id)}
              isBottleneck={Boolean((analysisMode && storeMetrics?.realBottleneck?.id === card.id) || fifoBlockedSourceIds.has(card.id))}
              isCreatingPostIt={canEditCanvas && isCreatingPostIt} postItCursor={postItCursor} onCreatePostItOnCard={canEditCanvas ? handleCreatePostItOnCard : (() => {})}
              onUpdateCardData={handleUpdateCardData} analysisMode={analysisMode}
              linkedPostIts={linkedPostIts} onPostItClick={handlePostItClick}
              onPostItContextMenu={openContextMenu} onPostItTextChange={handlePostItTextChange}
              onOpenInMemo={handleOpenInMemo} linkedActions={linkedActions}
              onOpenImpactPanel={canEditCanvas ? onOpenImpactPanel : undefined}
              isTimeScaleActive={isTimeScaleActive}
              dragEnabled={canEditCanvas && (interactionMode === 'select' || (card as any).vsmIcon === 'vertical_flow' || (!!(card as any).transportDockParentId && ['caminhao','transporte'].includes((card as any).vsmIcon ?? '')))}
              isKaizenMode={isKaizenMode}
              viewMode={viewMode}
              heatmapMode={heatmapMode}
              showPercentBadge={showPercentBadge || (simulation?.isSimulationMode ?? false)}
              showAS={showAS}
              showFTE={showFTE}
              liveMetrics={liveMetricsMap?.get(card.atividade || '') ?? null}
            />
          </React.Fragment>
        );
      });
    });
    
    // ��Ļ RENDERIZAR GROUP CONTAINERS para grupos expandidos (usando dynamicExpandedGroups)
    const groupContainerElements: React.ReactNode[] = [];
    dynamicExpandedGroups.forEach((cards, subProcessId) => {
      const subProcessCard = activeCards.find(c => c.id === subProcessId);
      const groupName = subProcessCard?.atividade || 'Sub-processo';
      
      groupContainerElements.push(
        <GroupContainer
          key={`group-container-${subProcessId}`}
          groupId={subProcessId}
          groupName={groupName}
          childCards={cards}
          onMinimize={() => handleMinimizeGroup(subProcessId)}
          scale={scale}
          themeColor="#6366f1"
        />
      );
    });
    
    return [...groupContainerElements, ...cardElements, ...expandedCardElements];
  }, [activeCards, postIts, selectedCardId, selectedCardIds, highlightedPath, analysisMode, storeMetrics, isCreatingPostIt, canEditCanvas, getActionsByNode, handleCardSelect, handleSelectedCardContextMenu, handleDeleteTotal, handleMarkX, handleDuplicateCard, handleUngroupCard, handleAddNext, handleCardDragStart, handleMove, handleCardDragEnd, handleStartConnection, handleFinishConnection, handleCreatePostItOnCard, handleUpdateCardData, handlePostItClick, openContextMenu, handlePostItTextChange, handleOpenInMemo, setRightPanelMode, isTimeScaleActive, interactionMode, heatmapMode, showPercentBadge, showAS, showFTE, dynamicExpandedGroups, handleExpandGroup, handleMinimizeGroup, scale, getExecutiveOpacity, manualArrowMode, manualArrowSource, connectingSource, setManualArrowSource, setManualArrowWaypoints, hiddenFifoSymbolCardIds, engineeringTool, openKaizenProjection,
    // V205: viewMode + isKaizenMode devem estar nas deps para re-filtrar cards ao trocar de modo
    viewMode, isKaizenMode, nonInteractableCardIds]);

  // Obter dimens+�es do viewport 
  const [viewportDimensions, setViewportDimensions] = React.useState({ width: 0, height: 0 });
  
  React.useEffect(() => {
    const updateDimensions = () => {
      if (canvasRef.current) {
        setViewportDimensions({
          width: canvasRef.current.clientWidth,
          height: canvasRef.current.clientHeight,
        });
      }
    };
    
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, [canvasRef]);

  // Calcular dimens+�es da p+�gina baseado no formato
  const getPageDimensions = (format: string) => {
    const dpi = 96; // pixels per inch
    const mmToInch = 1 / 25.4;
    switch (format) {
      case 'a4-portrait':
        return { width: Math.round(210 * mmToInch * dpi), height: Math.round(297 * mmToInch * dpi) }; // 794 x 1123
      case 'a4-landscape':
        return { width: Math.round(297 * mmToInch * dpi), height: Math.round(210 * mmToInch * dpi) }; // 1123 x 794
      case 'a3-portrait':
        return { width: Math.round(297 * mmToInch * dpi), height: Math.round(420 * mmToInch * dpi) }; // 1123 x 1587
      case 'a3-landscape':
        return { width: Math.round(420 * mmToInch * dpi), height: Math.round(297 * mmToInch * dpi) }; // 1587 x 1123
      default:
        return { width: 1123, height: 794 };
    }
  };

  const pageDimensions = pageMode?.enabled ? getPageDimensions(pageMode.format) : null;

  // Calcular fator de escala de conte+�do (12 = 100%)
  const scaleFactor = contentScale / 12;

  // Modo p+�gina +� apenas visualiza+�+�o - sem detec+�+�o autom+�tica

  // Cor de fundo do canvas baseada no modo ativo
  const effectiveCanvasBackground = (canvasBackgroundColor && canvasBackgroundColor.trim())
    || (isDark
      ? '#202A35'
      : isKaizenMode
      ? '#fff9e6'
      : isFutureMode
      ? '#f2fbf7'
      : '#FAFBFD');
  const normalizedGridIntensity = Math.min(Math.max(gridIntensity, 0), 100);
  const neutralGridColor = (canvasGridColor || gridColor || '#CBD5E1').trim();
  const effectiveDefaultArrowColor = (defaultArrowColor || (isDark ? '#64748B' : 'rgba(0,0,0,0.6)')).trim();
  // Cor das setas no layout clássico (VSM Factory): adapta para dark mode e estilo blueprint
  const classicArrowColor = isDark
    ? (vsmDesignStyle === 'blueprint' ? '#06b6d4' : vsmDesignStyle === 'technical' ? '#e5e7eb' : '#94a3b8')
    : (vsmDesignStyle === 'technical' ? '#1a1a1a' : '#000000');
  const pushArrowColor = isDark ? classicArrowColor : '#000000';
  const normalizedBackground = (effectiveCanvasBackground || '').trim().toLowerCase();
  const normalizedNeutralGrid = neutralGridColor.toLowerCase();
  const boostGridAlpha = (color: string, multiplier: number) => {
    const rgbaMatch = color.match(/^rgba?\(([^)]+)\)$/i);
    if (!rgbaMatch) return color;

    const parts = rgbaMatch[1].split(',').map((part) => part.trim());
    if (parts.length < 3) return color;

    const [r, g, b, alpha = '1'] = parts;
    const nextAlpha = Math.min(1, Math.max(0, Number.parseFloat(alpha) * multiplier));

    if (Number.isNaN(nextAlpha)) return color;
    return `rgba(${r}, ${g}, ${b}, ${nextAlpha.toFixed(4)})`;
  };
  // Fallback: se cor da grade coincidir com o fundo, usa tom t�cnico vis�vel
  const safeNeutralGridColor =
    normalizedNeutralGrid === normalizedBackground ? '#CBD5E1' : neutralGridColor;
  // Grid discreto para n�o competir com os cards.
  const neutralGridOpacity = 1;
  const modeGridColor = safeNeutralGridColor;
  const majorGridColor = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(15,23,42,0.08)';
  const modeGridOpacity = neutralGridOpacity;

  // V201 — No FUTURO mode, filtrar connections cujo source/target foi ocultado
  const displayConnections = React.useMemo(() => {
    if (viewMode !== 'future_simulated') return processedConnections;

    // ── Pre-builds para filtro no modo futuro ─────────────────────────────
    // V202: membros de célula de fluxo contínuo
    const cellMemberIds = new Set(
      activeCards
        .filter(c => (c as any).vsmFlowCellId && !(c as any).vsmFlowCellBlocked)
        .map(c => c.id)
    );
    // V207: IDs de cards PCPM — somente PCPM é silenciado no futuro (nivelamento mantém seus sinais de puxada)
    const pcpmCardIds = new Set(
      activeCards
        .filter(c => (c as any).vsmIcon === 'pcpm')
        .map(c => c.id)
    );
    // V207: connectionIds kanban-habilitados e bloqueados (varredura única nos raw data)
    const kanbanConnIds = new Set<string>();
    const blockedConnIds = new Set<string>();
    activeCards.forEach(card => {
      (card.conexoes_saida ?? []).forEach((rc: any) => {
        if (rc.kanbanLoopEnabled) kanbanConnIds.add(rc.id as string);
        if (rc.vsmInfoBlocked)    blockedConnIds.add(rc.id as string);
      });
    });

    // V208: estoques adjacentes a membros de célula — IDs pre-computados
    const stockAdjacentToCellIds = new Set<string>();
    activeCards.forEach(c => {
      if (c.tipo !== 'inventory' || (c as any).vsmIcon !== 'estoque') return;
      // V208.1: converted stocks (supermercado/pulmão) are future-state objects — keep their connections
      if ((c as any).vsmConvertedToIcon) return;
      const outgoing = (c.conexoes_saida ?? []).some((rc: any) => cellMemberIds.has(rc.targetId));
      if (outgoing) { stockAdjacentToCellIds.add(c.id); return; }
      const incoming = activeCards.some(src => cellMemberIds.has(src.id) &&
        (src.conexoes_saida ?? []).some((rc: any) => rc.targetId === c.id));
      if (incoming) stockAdjacentToCellIds.add(c.id);
    });

    return processedConnections.filter(conn => {
      const srcId = conn.sourceId ?? '';
      const tgtId = conn.targetId ?? '';

      // Remover conexões com cards ocultos (vsmFuturoHide / vsmFlowCellBlocked)
      if (futuroHiddenSet.has(srcId) || futuroHiddenSet.has(tgtId)) return false;

      // V208: conexões envolvendo estoques adjacentes à célula também removidas
      if (stockAdjacentToCellIds.has(srcId) || stockAdjacentToCellIds.has(tgtId)) return false;

      // V202: conexões internas à célula (ambos membros)
      if (cellMemberIds.has(srcId) && cellMemberIds.has(tgtId)) return false;

      // V205: vsm_info para/de postos individuais da célula
      const connType = (conn as any).connectionType ?? '';
      const srcCard = cardById.get(srcId);
      const tgtCard = cardById.get(tgtId);
      const getVsmCardLabel = (card?: CardData) => String(
        (card as any)?.atividade ??
        (card as any)?.vsmNome ??
        (card as any)?.titulo ??
        (card as any)?.title ??
        '',
      );
      const isEstampariaCard = (card?: CardData) => /estamparia/i.test(getVsmCardLabel(card));
      const isExpedicaoCard = (card?: CardData) => /expedi[cç][aã]o/i.test(getVsmCardLabel(card));
      if (connType === 'push' && (isEstampariaCard(srcCard) || isEstampariaCard(tgtCard) || isExpedicaoCard(tgtCard))) return false;
      if (connType === 'push' && (cellMemberIds.has(srcId) || cellMemberIds.has(tgtId))) return false;
      if (connType === 'vsm_info' && (cellMemberIds.has(srcId) || cellMemberIds.has(tgtId))) return false;

      // V207: no mapa futuro, PCPM não emite setas de info para processos —
      // exceto vsm_pull_signal (seta de retirada PCPM → Expedição)
      if (pcpmCardIds.has(srcId) && connType !== 'vsm_pull_signal') return false;

      // V207c: conexões explicitamente bloqueadas (vsmInfoBlocked) → remover
      if (blockedConnIds.has(conn.connectionId)) return false;

      return true;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processedConnections, futuroHiddenSet, activeCards, viewMode]);

  return (
    <div
      ref={canvasRef}
      className="w-full h-full relative overflow-hidden"
      style={{
        backgroundColor: effectiveCanvasBackground,
        boxShadow: isDark
          ? 'inset 0 0 0 1px #27272A'
          : 'inset 0 0 0 1px #CBD5E1',
        cursor: isCreatingPostIt
          ? postItCursor
          : manualArrowMode
            ? (manualArrowSource ? 'alias' : 'crosshair')
            : (draggingPostIt ? 'grabbing' : isPanning ? 'grabbing' : (interactionMode === 'move' ? 'grab' : 'default')),
        minHeight: '400px',
      }}
      onMouseDown={handleCanvasMouseDown}
      onPointerDownCapture={(e) => {
        // Blur qualquer textarea ativa (sai do modo edicao postit com 1 clique)
        // Fase captura: dispara antes de qualquer handler filho
        const target = e.target as HTMLElement;
        if (manualArrowMode && manualArrowSource && e.button === 0) {
          const targetCardEl = target.closest('[data-card-id]') as HTMLElement | null;
          let targetCardId = targetCardEl?.dataset.cardId;
          if (canvasRef.current) {
            const rect = canvasRef.current.getBoundingClientRect();
            const effectiveScale = scale * (contentScale / 12);
            const x = (e.clientX - rect.left - pan.x) / effectiveScale;
            const y = (e.clientY - rect.top - pan.y) / effectiveScale;
            const viewportProcessTarget = findProcessCardByViewportPoint(interactableCards, e.clientX, e.clientY, manualArrowSource.id);
            const coordinateTarget = findCardAtCanvasPoint(interactableCards, x, y, isTimeScaleActive, manualArrowSource.id);
            const domTarget = targetCardId ? interactableCards.find(card => card.id === targetCardId) : null;
            if (viewportProcessTarget) {
              targetCardId = viewportProcessTarget.id;
            } else if (!targetCardId || targetCardId === manualArrowSource.id || (coordinateTarget && isFactoryProcessTarget(coordinateTarget) && !isFactoryProcessTarget(domTarget as CardData))) {
              targetCardId = coordinateTarget?.id;
            }
          }
          if (targetCardId && targetCardId !== manualArrowSource.id) {
            e.preventDefault();
            e.stopPropagation();
            handleFinishConnection(targetCardId);
            if (setManualArrowSource) setManualArrowSource(null);
            if (setManualArrowWaypoints) setManualArrowWaypoints([]);
            return;
          }
        }
        if (!target.closest('[data-post-it]')) {
          const activeEl = document.activeElement;
          if (activeEl instanceof HTMLTextAreaElement || activeEl instanceof HTMLInputElement) {
            activeEl.blur();
          }
        }
      }}
      onMouseMove={handleCanvasMouseMove}
      onMouseUp={handleCanvasMouseUp}
      onMouseLeave={handleCanvasMouseUp}
      onContextMenu={handleContextMenu}
      onDragOver={handleCanvasDragOver}
      onDrop={handleCanvasDrop}
      onDragLeave={handleCanvasDragLeave}
    >
      {/* Override global de cursor: garante que elementos filhos dos cards n�o sobreponham o cursor durante cria��o/arrastar post-it */}
      {(isCreatingPostIt || !!draggingPostIt) && (
        <style>{`
          #canvas-content [data-card-id],
          #canvas-content [data-card-id] * { cursor: none !important; }
          ${draggingPostIt ? '#canvas-content [data-post-it] { pointer-events: none !important; }' : ''}
        `}</style>
      )}
      {/* Time Grid - Renderizado no fundo quando Time Scale est+� ativo */}
      <TimeGrid
        active={isTimeScaleActive}
        scale={scale}
        pan={pan}
        cards={activeCards}
      />

      {isOptimizationMode && !manualArrowMode && (
        <div className="pointer-events-none absolute left-4 top-3 z-30 text-[11px] font-medium text-amber-300/75 dark:text-amber-200/70">
          Modo Otimização Ativo - Preservando Mapa Atual
        </div>
      )}

      {/* Banner de Instru+�+�es do Modo Manual de Conex+�o */}
      {manualArrowMode && (
        <div 
          className="absolute top-0 left-0 right-0 z-30 bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg"
          style={{ pointerEvents: 'auto' }}
        >
          <div className="flex items-center justify-center px-6 py-3 gap-4">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
              <span className="text-sm font-bold tracking-wide">���� Modo Conectar Ativo</span>
            </div>
            <div className="h-4 w-px bg-white/40"></div>
            {!manualArrowSource ? (
              <span className="text-sm text-blue-100">
                ���� Clique em um card para iniciar a conex+�o
              </span>
            ) : (
              <div className="flex items-center gap-4 text-sm text-blue-100">
                <span>���� Card origem selecionado</span>
                <span className="text-white">���</span>
                <span>��Ļ Clique no <b>card destino</b> ou em uma <b>seta existente</b> (jun+�+�o em T)</span>
              </div>
            )}
            <div className="h-4 w-px bg-white/40"></div>
            <span className="text-xs text-blue-200">
              Pressione ESC para cancelar
            </span>
          </div>
        </div>
      )}

      {/* Grid de Linhas/Pontos - Renderizado quando showGrid est+� ativo */}
      {showGrid && !executiveMode && (canvasGridType || gridType) !== 'none' && !isTimeScaleActive && (
        <LineGrid
          ref={gridRef}
          gridSize={gridScale}
          lineColor={modeGridColor}
          majorLineColor={majorGridColor}
          lineWidth={1}
          opacity={modeGridOpacity}
          offsetX={pan?.x || 0}
          offsetY={pan?.y || 0}
          scale={scale || 1}
        />
      )}
      
      {/* highlight-layer: C+�lula do grid destacada durante drag de card */}
      {draggingCardId !== null && dragHighlightPos !== null && (
        <div
          className="absolute pointer-events-none"
          style={{
            zIndex: 1,
            left: dragHighlightPos.x * (scale || 1) + (pan?.x || 0),
            top: dragHighlightPos.y * (scale || 1) + (pan?.y || 0),
            width: 10 * (scale || 1),
            height: 10 * (scale || 1),
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: 1,
          }}
        />
      )}
      
      {/* Overlay de P+�ginas - Mostrar quando pageMode est+� ativo */}
      {pageMode?.enabled && pageDimensions && (
        <div
          className="absolute pointer-events-none"
          style={{
            transform: `translate3d(${pan.x}px, ${pan.y}px, 0) scale(${scale})`,
            transformOrigin: 'top left',
            willChange: 'transform',
            zIndex: 1
          }}
        >
          {/* Renderizar grade de p+�ginas (3 colunas x 3 linhas para exemplo) */}
          {Array.from({ length: 3 }).map((_, row) =>
            Array.from({ length: 3 }).map((_, col) => {
              const pageX = col * pageDimensions.width;
              const pageY = row * pageDimensions.height;
              const pageNum = row * 3 + col + 1;
              
              return (
                <div
                  key={`page-${row}-${col}`}
                  className="absolute"
                  style={{
                    left: pageX,
                    top: pageY,
                    width: pageDimensions.width,
                    height: pageDimensions.height,
                    border: '2px solid #3b82f6',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                  }}
                >
                  {/* Margem de seguran+�a (20mm = ~76px @ 96dpi) */}
                  <div
                    className="absolute"
                    style={{
                      left: 76,
                      top: 76,
                      right: 76,
                      bottom: 76,
                      border: '1px dashed #93c5fd',
                      pointerEvents: 'none'
                    }}
                  />
                  {/* N+�mero da p+�gina */}
                  <div
                    className="absolute bottom-2 right-2 bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded"
                    style={{ fontSize: 10 }}
                  >
                    {pageNum}
                  </div>
                  {/* Formato da p+�gina no topo */}
                  <div
                    className="absolute top-2 left-2 bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded uppercase"
                    style={{ fontSize: 9 }}
                  >
                    {pageMode.format.replace('-', ' ')}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
      
      <div
        ref={contentDivRef}
        id="canvas-content"
        className="absolute inset-0 origin-top-left"
        style={{
          transform: `translate3d(${pan.x}px, ${pan.y}px, 0) scale(${scale * scaleFactor})`,
          willChange: 'transform',
          pointerEvents: isPanning ? 'none' : (isCreatingPostIt ? 'auto' : 'auto')
        }}
      >
        <svg 
          className="absolute pointer-events-none overflow-visible" 
          style={{ 
            zIndex: 0,
            left: 0,
            top: 0,
            width: '10000px',
            height: '10000px',
            overflow: 'visible'
          }}
          width="10000"
          height="10000"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            {/* ============================================================================ */}
            {/* MARKERS PARA EDGE ROUTING MANHATTAN (Lean Industrial Premium) */}
            {/* REGRA: Jun+�+�o em T com seta a 90-� - marker-end aponta para cima ou lateral */}
            {/* ============================================================================ */}
            
            {/* Seta padr+�o horizontal (direita) */}
            <marker id="arrowhead" markerWidth="10" markerHeight="10" refX="10" refY="5" orient="auto" markerUnits="userSpaceOnUse">
              <polygon points="0,0 10,5 0,10" fill={effectiveDefaultArrowColor} />
            </marker>
            
            {/* Seta azul (sele+�+�o) */}
            <marker id="arrowhead-blue" markerWidth="10" markerHeight="10" refX="10" refY="5" orient="auto" markerUnits="userSpaceOnUse">
              <polygon points="0,0 10,5 0,10" fill="#2563eb" />
            </marker>
            
            {/* Seta para cima (jun+�+�o em T - cinza) */}
            <marker id="arrowhead-up" markerWidth="10" markerHeight="7" refX="5" refY="0" orient="auto" markerUnits="userSpaceOnUse">
              <polygon points="0,7 5,0 10,7" fill={effectiveDefaultArrowColor} />
            </marker>
            
            {/* Seta vermelha horizontal (retorno) */}
            <marker id="arrowhead-red" viewBox="0 0 10 10" markerWidth="10" markerHeight="10" refX="10" refY="5" orient="auto" markerUnits="userSpaceOnUse">
              <polygon points="0,0 10,5 0,10" fill="#ef4444" stroke="#ef4444" strokeWidth="0.5" />
            </marker>
            
            {/* Seta vermelha para cima (jun+�+�o em T - retorno) */}
            <marker id="arrowhead-red-up" viewBox="0 0 10 10" markerWidth="10" markerHeight="7" refX="5" refY="0" orient="auto" markerUnits="userSpaceOnUse">
              <polygon points="0,7 5,0 10,7" fill="#ef4444" stroke="#ef4444" strokeWidth="0.5" />
            </marker>
            
            {/* Seta vermelha para esquerda (jun+�+�o em T - entrada lateral) */}
            <marker id="arrowhead-red-left" viewBox="0 0 10 10" markerWidth="10" markerHeight="10" refX="0" refY="5" orient="auto" markerUnits="userSpaceOnUse">
              <polygon points="10,0 0,5 10,10" fill="#ef4444" stroke="#ef4444" strokeWidth="0.5" />
            </marker>

            {/* Seta cyan (entrega — vsm_transport_delivery) */}
            <marker id="arrowhead-cyan" markerWidth="10" markerHeight="10" refX="10" refY="5" orient="auto" markerUnits="userSpaceOnUse">
              <polygon points="0,0 10,5 0,10" fill="#0891b2" />
            </marker>

            {/* Seta material — unica ponta no target, reforcada para canvas VSM escuro */}
            <marker id="arrowhead-material" viewBox="0 0 18 18" markerWidth="18" markerHeight="18" refX="17" refY="9" orient="auto" markerUnits="userSpaceOnUse" overflow="visible">
              <polygon points="0,0 18,9 0,18" fill={classicArrowColor} stroke={classicArrowColor} strokeWidth="0.6" />
            </marker>

            {/* Seta classic — layout clássico VSM Factory (adapta dark/blueprint) */}
            {/* refX="13": TIP at x=13 placed at path endpoint (card border); body extends backward into the path */}
            <marker id="arrowhead-classic" markerWidth="18" markerHeight="18" refX="16" refY="9" orient="auto" markerUnits="userSpaceOnUse">
              <polygon points="0,0 16,9 0,18" fill={classicArrowColor} />
            </marker>
            <marker id="arrowhead-classic-push" markerWidth="24" markerHeight="22" refX="22" refY="11" orient="auto" markerUnits="userSpaceOnUse">
              <polygon points="0,1 22,11 0,21" fill={pushArrowColor} stroke={pushArrowColor} strokeWidth="0.75" />
            </marker>

            {/* Seta loop kanban — teal modo normal */}
            <marker id="arrowhead-kanban" markerWidth="13" markerHeight="13" refX="13" refY="6.5" orient="auto" markerUnits="userSpaceOnUse">
              <polygon points="0,0 13,6.5 0,13" fill="#0e7490" />
            </marker>

            {/* Seta loop kanban — cyan blueprint, maior */}
            <marker id="arrowhead-kanban-bp" markerWidth="20" markerHeight="20" refX="20" refY="10" orient="auto" markerUnits="userSpaceOnUse">
              <polygon points="0,0 20,10 0,20" fill="#22d3ee" />
            </marker>
          </defs>
          {/* GRUPO 1: Renderizar m+�scaras com Z-Index baseado em isReturn */}
          {/* Retornos (isReturn=true) t+�m z-index baixo, fluxo principal tem z-index alto */}
          <g style={{ pointerEvents: 'none' }}>
            {/* Primeiro: Renderizar m+�scaras de retornos (z-index baixo) */}
            {displayConnections
              .filter(conn => conn && conn.id && conn.isReturn)
              .map((conn) => {
                const connectionId = conn.connectionId || conn.id;
                const sourceId = conn.sourceId;
                const isSelected = selectedConnection && sourceId === selectedConnection.sourceId && connectionId === selectedConnection.connectionId;
                const isPush = conn.connectionType === 'push';
                const connectionBaseColor = conn.isReturn || conn.color === '#ef4444' ? '#ef4444' : effectiveDefaultArrowColor;
                const color = isPush
                  ? pushArrowColor
                  : currentLayout === 'classic'
                  ? classicArrowColor
                  : (isSelected ? '#2563eb' : connectionBaseColor);
                const width = currentLayout === 'classic' ? (isPush ? 7.25 : 2) : (isPush ? 7.25 : isSelected ? 4 : (conn.width || 2.5));
                
                const backgroundColor = canvasBackgroundColor || '#ffffff';
                const maskWidth = width + 4;
                
                return (
                  <path 
                    key={`${conn.id}_bg`}
                    id={`${conn.id}_bg`}
                    d={conn.d} 
                    fill="none" 
                    stroke={backgroundColor}
                    strokeWidth={maskWidth}
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                    className="react-flow__edge-path-bg"
                    vectorEffect="non-scaling-stroke"
                    style={{ zIndex: 0 }}
                  />
                );
              })}
            
            {/* Depois: Renderizar m+�scaras de fluxo principal (z-index alto) */}
            {displayConnections
              .filter(conn => conn && conn.id && !conn.isReturn)
              .map((conn) => {
                const connectionId = conn.connectionId || conn.id;
                const sourceId = conn.sourceId;
                const isSelected = selectedConnection && sourceId === selectedConnection.sourceId && connectionId === selectedConnection.connectionId;
                const isPush = conn.connectionType === 'push';
                const connectionBaseColor = conn.isReturn || conn.color === '#ef4444' ? '#ef4444' : effectiveDefaultArrowColor;
                const color = isPush
                  ? pushArrowColor
                  : currentLayout === 'classic'
                  ? classicArrowColor
                  : (isSelected ? '#2563eb' : connectionBaseColor);
                const width = currentLayout === 'classic' ? (isPush ? 7.25 : 2) : (isPush ? 7.25 : isSelected ? 4 : (conn.width || 2.5));
                
                const backgroundColor = canvasBackgroundColor || '#ffffff';
                const maskWidth = width + 4;
                
                return (
                  <path 
                    key={`${conn.id}_bg`}
                    id={`${conn.id}_bg`}
                    d={conn.d} 
                    fill="none" 
                    stroke={backgroundColor}
                    strokeWidth={maskWidth}
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                    className="react-flow__edge-path-bg"
                    vectorEffect="non-scaling-stroke"
                    style={{ zIndex: 10 }}
                  />
                );
              })}
          </g>
          
          {/* GRUPO 2: Renderizar linhas com Z-Index baseado em isReturn */}
          <g style={{ pointerEvents: 'auto' }}>
            {/* Primeiro: Renderizar linhas de retorno (z-index baixo) */}
            {displayConnections
              .filter(conn => conn && conn.id && conn.isReturn)
              .map((conn) => {
                const connectionId = conn.connectionId || conn.id;
                const sourceId = conn.sourceId;
                const targetId = conn.targetId;
                const isSelected = selectedConnection && sourceId === selectedConnection.sourceId && connectionId === selectedConnection.connectionId;
                const wip = targetId ? wipByTargetId.get(targetId) : undefined;
                const incomingToTarget = targetId ? incomingConnectionsByTargetId.get(targetId) ?? [] : [];
                const isMergeTarget = incomingToTarget.length > 1;
                const anchorConnectionId = targetId ? badgeAnchorConnectionIdByTargetId.get(targetId) : undefined;
                const isAnchorEdgeForBadge = !isMergeTarget || anchorConnectionId === conn.id;

                // V109.0 — Arestas customizadas têm prioridade mesmo em bloco isReturn
                {
                  const isVsmTR = conn.connectionType === 'vsm_transport';
                  const isVsmFR = conn.connectionType === 'vsm_fifo';
                  const isVsmIR = conn.connectionType === 'vsm_info';
                  if ((isVsmTR || isVsmFR || isVsmIR) && conn.sourcePoint && conn.targetPoint) {
                    const srcCard = activeCards.find(c => c.id === sourceId);
                    const targetCard = activeCards.find(c => c.id === targetId);
                    const rawConn = srcCard?.conexoes_saida?.find(c => c.id === connectionId);
                    const isKanbanProductionNode = (card?: CardData) => String((card as any)?.vsmIcon ?? '') === 'kanban_prod';
                    if (isVsmIR && (rawConn?.kanbanLoopEnabled || isKanbanProductionNode(srcCard) || isKanbanProductionNode(targetCard))) return null;
                    const isPullBufferNode = (card?: CardData) => !!card && (
                      ['supermercado', 'pulmao'].includes((card as any).vsmIcon ?? '') ||
                      (card as any).vsmConvertedToIcon === 'supermercado'
                    );
                    const isPullInfoSignal = isVsmIR && Boolean(
                      rawConn?.kanbanLoopEnabled
                      || rawConn?.kanbanSupermarketId
                      || isPullBufferNode(srcCard)
                      || isPullBufferNode(targetCard)
                    );
                    const srcIcon = (srcCard as any)?.vsmIcon ?? '';
                    const iconDefaultTransmission: 'Manual' | 'Eletrônico' | undefined =
                      srcIcon === 'info_manual' ? 'Manual'
                      : srcIcon === 'info_eletro' ? 'Eletrônico'
                      : undefined;
                    const boundComunicacao = isVsmIR
                      ? activeCards.find((c: any) =>
                          c.vsmIcon === 'comunicacao' && (
                            c.vsmBoundConnection?.connectionId === connectionId ||
                            c.vsmBoundConnection?.targetId === targetId ||
                            (rawConn?.targetId && c.vsmBoundConnection?.targetId === rawConn.targetId)
                          )
                        )
                      : undefined;
                    const comunicacaoMethod: 'Manual' | 'Eletrônico' | undefined =
                      (boundComunicacao as any)?.vsmInfoTransmissionMethod;
                    const resolvedTransmission = comunicacaoMethod ?? rawConn?.vsmInfoTransmission ?? iconDefaultTransmission;
                    const fifoState = isVsmFR
                      ? getFifoRuntimeState(rawConn, srcCard, targetCard, activeCards, simulationShiftMinutes)
                      : { isBlocked: false, maxCapacity: undefined };
                    // V207 — padroniza setas de transporte cliente/fornecedor em comprimento absoluto
                    // (mesmo tamanho visual nas duas setas), centradas entre os cards.
                    const isExternalEntity = (card?: CardData) => {
                      if (!card) return false;
                      const icon = String((card as any).vsmIcon ?? '');
                      const ext = String((card as any).extType ?? '').toUpperCase();
                      return icon === 'cliente' || icon === 'fornecedor' || icon === 'externo'
                        || card.tipo === 'card_inicial' || card.tipo === 'card_final'
                        || ext === 'CLIENTE' || ext === 'FORNECEDOR';
                    };
                    const transportFixedLength = isVsmTR && (isExternalEntity(srcCard) || isExternalEntity(targetCard))
                      ? 76
                      : undefined;
                    const isInPath = effectiveFocusIds
                      ? (effectiveFocusSet.has(sourceId) && effectiveFocusSet.has(targetId))
                      : true;
                    return (
                      <g key={conn.id} data-edge="true" data-target-id={conn.targetId} style={{ opacity: isInPath ? 1 : 0.1, pointerEvents: manualArrowMode && manualArrowSource ? 'none' : 'auto' }}>
                        <g transform={isVsmTR ? `translate(0, ${Number((rawConn as any)?.vsmTransportOffsetY ?? 0)})` : undefined}>
                        <VsmTransportEdge
                          d={conn.d}
                          sourcePoint={conn.sourcePoint}
                          targetPoint={conn.targetPoint}
                          arrow={conn.arrow}
                          edgeType={conn.connectionType as VsmEdgeType}
                          transportIcon={rawConn?.vsmTransportIcon ?? 'truck'}
                          label={rawConn?.frequencia}
                          isSelected={!!isSelected}
                          isDark={isDark}
                          isBlueprint={vsmDesignStyle === 'blueprint'}
                          isTechnical={vsmDesignStyle === 'technical'}
                          isBlocked={fifoState.isBlocked}
                          isPullSignalBlocked={Boolean(rawConn?.vsmInfoBlocked || rawConn?.vsmInfoBlockedBySupermarket)}
                          isPullSignal={isPullInfoSignal}
                          maxCapacity={fifoState.maxCapacity}
                          transmissionMethod={resolvedTransmission}
                          fixedLength={transportFixedLength}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (canEditCanvas && engineeringTool === 'block_info' && isVsmIR && sourceId && connectionId) {
                              blockInformationConnection(sourceId, connectionId);
                              return;
                            }
                            if (sourceId && connectionId) setSelectedConnection({ sourceId, connectionId });
                          }}
                          onContextMenu={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (sourceId && connectionId) {
                              setSelectedConnection({ sourceId, connectionId });
                              setContextMenu({ x: e.clientX, y: e.clientY, type: 'edge' });
                            }
                          }}
                        />
                        </g>
                      </g>
                    );
                  }
                }
                const connectionBaseColor =
                  conn.isReturn || conn.color === '#ef4444' ? '#ef4444' : effectiveDefaultArrowColor;
                const color = currentLayout === 'classic'
                  ? classicArrowColor
                  : (isSelected ? '#2563EB' : connectionBaseColor);
                const width = currentLayout === 'classic'
                  ? 4
                  : (isSelected ? 4.25 : Math.max((conn.width || 2.5) + 0.35, 2.85));
                const showStockBadge = Boolean(
                  wip &&
                  wip.loadPercent > 100 &&
                  wip.overflowPerDay > 0 &&
                  !isSelected &&
                  isAnchorEdgeForBadge
                );
                const stockBadgeSeverity: FlowStockBadgeSeverity =
                  wip && wip.loadPercent > STOCK_BADGE_CRITICAL_THRESHOLD
                    ? 'critical'
                    : wip && wip.loadPercent > STOCK_BADGE_WARNING_THRESHOLD
                    ? 'warning'
                    : 'normal';
                const stockBadgePos = showStockBadge
                  ? targetId
                    ? getStockBadgeTopPosition(targetId, conn)
                    : getStockBadgePosition(conn)
                  : null;
                
                // Ocultar se highlightedPath est+� ativo e a conex+�o n+�o est+� no caminho
                const isInHighlightedPath = effectiveFocusIds
                  ? (effectiveFocusSet.has(sourceId) && effectiveFocusSet.has(targetId))
                  : true;
                const opacity = isInHighlightedPath ? 1 : executiveMode ? 0.08 : 0.1;
                
                const markerId = currentLayout === 'classic' ? 'arrowhead-classic' : color === '#2563EB' ? 'arrowhead-blue' : 'arrowhead';
                const targetCard = targetId ? activeCards.find(card => card.id === targetId) : undefined;
                const sourceCard = sourceId ? activeCards.find(card => card.id === sourceId) : undefined;
                const isMaterialLikeEdge = conn.connectionType === 'material' || conn.connectionType === 'push' || sourceCard?.tipo === 'inventory' || targetCard?.tipo === 'inventory';
                const shouldShowMaterialTargetArrow = targetCard?.tipo !== 'inventory';
                const visibleWidth = isMaterialLikeEdge ? (currentLayout === 'classic' ? 9.45 : 7.35) : width;
                const materialArrowLength = currentLayout === 'classic' ? 14.58 : 12.96;
                const materialRightExtension = isMaterialLikeEdge && shouldShowMaterialTargetArrow
                  ? (sourceCard?.tipo === 'inventory'
                    ? 11.1375
                    : getIntermediateStockRightExtension(conn.d, activeCards, sourceId, targetId, materialArrowLength))
                  : 0;
                const materialArrowPathD = materialRightExtension > 0
                  ? extendStraightPathEnd(conn.d, materialRightExtension)
                  : conn.d;
                const materialSplitPath = isMaterialLikeEdge && shouldShowMaterialTargetArrow
                  ? splitStraightPathAroundIntermediateStock(conn.d, activeCards, sourceId, targetId, materialArrowLength, materialRightExtension)
                  : null;
                const materialVisiblePath = materialSplitPath ?? (isMaterialLikeEdge && shouldShowMaterialTargetArrow
                  ? trimStraightPathEnd(materialArrowPathD, materialArrowLength)
                  : isMaterialLikeEdge && targetCard?.tipo === 'inventory'
                  ? trimStraightPath(conn.d, 118, 2)
                  : conn.d);
                const materialArrowPath = isMaterialLikeEdge && shouldShowMaterialTargetArrow
                  ? buildMaterialArrowHead(materialArrowPathD, materialArrowLength, Math.max(7.5, visibleWidth * 0.95))
                  : null;
                
                return (
                  <g 
                    key={conn.id} 
                    data-edge="true"
                    data-target-id={conn.targetId}
                    onClick={(e) => { 
                      e.stopPropagation(); 
                      if (manualArrowMode && manualArrowSource) {
                        return;
                      }
                      if (sourceId && connectionId) setSelectedConnection({ sourceId, connectionId }); 
                    }} 
                    onContextMenu={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (sourceId && connectionId) {
                        setSelectedConnection({ sourceId, connectionId });
                        setContextMenu({ x: e.clientX, y: e.clientY, type: 'edge' });
                      }
                    }}
                    style={{ cursor: manualArrowMode && manualArrowSource ? 'crosshair' : 'pointer', pointerEvents: manualArrowMode && manualArrowSource ? 'none' : 'auto', zIndex: 0, opacity }}
                  >
                    {/* Path invis+�vel mais grosso para facilitar sele+�+�o */}
                    <path 
                      d={conn.d} 
                      fill="none" 
                      stroke="rgba(0,0,0,0.01)" 
                      strokeWidth={Math.max(visibleWidth * 4, 24)} 
                      strokeLinecap={isMaterialLikeEdge ? 'butt' : 'round'} 
                      strokeLinejoin="round"
                      onContextMenu={(e) => {
                        console.log('[EDGE CONTEXT] Bot+�o direito na seta!', { sourceId, connectionId });
                        e.preventDefault();
                        e.stopPropagation();
                        if (sourceId && connectionId) {
                          setSelectedConnection({ sourceId, connectionId });
                          setContextMenu({ x: e.clientX, y: e.clientY, type: 'edge' });
                          console.log('[EDGE CONTEXT] Menu setado!', { x: e.clientX, y: e.clientY });
                        }
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        console.log('[EDGE CLICK] Click detectado na seta de retorno', { altKey: e.altKey, shiftKey: e.shiftKey, editingWaypoints, sourceId, connectionId });

                        if (manualArrowMode && manualArrowSource) {
                          return;
                        }
                        
                        // MODO MANUAL ARROW: Conectar em seta existente (jun+�+�o em T)
                        if (manualArrowMode && manualArrowSource && canvasRef.current && sourceId && connectionId) {
                          const rect = canvasRef.current.getBoundingClientRect();
                          const effectiveScale = scale * (contentScale / 12);
                          const clickX = (e.clientX - rect.left - pan.x) / effectiveScale;
                          const clickY = (e.clientY - rect.top - pan.y) / effectiveScale;
                          
                          // Encontrar a seta clicada para obter o targetId
                          const clickedSourceCard = activeCards.find(c => c.id === sourceId);
                          if (!clickedSourceCard?.conexoes_saida) return;
                          
                          const clickedConn = clickedSourceCard.conexoes_saida.find(c => c.id === connectionId);
                          if (!clickedConn) return;
                          
                          // Criar conex+�o que termina no ponto clicado da seta existente
                          // O merge point +� onde a nova conex+�o se junta +� existente
                          const mergeTargetId = clickedConn.targetId;
                          
                          // Criar a nova conex+�o com merge point
                          const newSourceCard = activeCards.find(c => c.id === manualArrowSource.id);
                          if (!newSourceCard) return;
                          
                          const newConnId = `conn-${Date.now()}`;
                          const newConnection = {
                            id: newConnId,
                            targetId: mergeTargetId,
                            sourceHandle: (manualArrowSource.handle || 'right') as 'right' | 'bottom',
                            waypoints: [...manualArrowWaypoints],
                            // Marcar que esta conex+�o faz merge em outra seta
                            mergeToConnection: {
                              targetConnectionId: connectionId,
                              mergePoint: { x: clickX, y: clickY }
                            }
                          };
                          
                          const updatedConexoes = [...(newSourceCard.conexoes_saida || []), newConnection];
                          handleUpdateCardData(newSourceCard.id, { conexoes_saida: updatedConexoes });
                          
                          // Limpar modo manual arrow
                          if (setManualArrowMode) setManualArrowMode(false);
                          if (setManualArrowSource) setManualArrowSource(null);
                          if (setManualArrowWaypoints) setManualArrowWaypoints([]);
                          
                          console.log('[EDGE MERGE] Conex+�o criada com merge em seta existente', {
                            newConnId,
                            sourceId: manualArrowSource.id,
                            targetId: mergeTargetId,
                            mergePoint: { x: clickX, y: clickY }
                          });
                          return;
                        }
                        
                        // Se Shift+Click e estamos em modo de edi+�+�o, adicionar waypoint
                        if (e.shiftKey && editingWaypoints && canvasRef.current && sourceId && connectionId) {
                          // Garantir que a conex+�o correta est+� selecionada
                          if (selectedConnection?.connectionId !== connectionId) {
                            setSelectedConnection({ sourceId, connectionId });
                          }
                          const rect = canvasRef.current.getBoundingClientRect();
                          // CR+�TICO: Usar scale * scaleFactor para coordenadas corretas
                          const effectiveScale = scale * (contentScale / 12);
                          const x = (e.clientX - rect.left - pan.x) / effectiveScale;
                          const y = (e.clientY - rect.top - pan.y) / effectiveScale;
                          
                          // Adicionar waypoint diretamente (sem depender do selectedConnection)
                          const sourceCard = activeCards.find(c => c.id === sourceId);
                          if (sourceCard?.conexoes_saida) {
                            const connIndex = sourceCard.conexoes_saida.findIndex(c => c.id === connectionId);
                            if (connIndex !== -1) {
                              const conn = sourceCard.conexoes_saida[connIndex];
                              const newWaypoints = [...(conn.waypoints || []), { x, y }];
                              const updatedConexoes = [...sourceCard.conexoes_saida];
                              updatedConexoes[connIndex] = { ...conn, waypoints: newWaypoints };
                              handleUpdateCardData(sourceId, { conexoes_saida: updatedConexoes });
                              console.log('[EDGE] Waypoint adicionado em', { x, y, totalWaypoints: newWaypoints.length });
                            }
                          }
                          return;
                        }
                        
                        // Se Alt+Click e estamos em modo de edi+�+�o, adicionar jump manual
                        if (e.altKey && editingWaypoints && canvasRef.current && sourceId && connectionId) {
                          // Garantir que a conex+�o correta est+� selecionada
                          if (selectedConnection?.connectionId !== connectionId) {
                            setSelectedConnection({ sourceId, connectionId });
                          }
                          const rect = canvasRef.current.getBoundingClientRect();
                          const effectiveScale = scale * (contentScale / 12);
                          const x = (e.clientX - rect.left - pan.x) / effectiveScale;
                          const y = (e.clientY - rect.top - pan.y) / effectiveScale;
                          
                          // Adicionar jump diretamente
                          const sourceCard = activeCards.find(c => c.id === sourceId);
                          if (sourceCard?.conexoes_saida) {
                            const connIndex = sourceCard.conexoes_saida.findIndex(c => c.id === connectionId);
                            if (connIndex !== -1) {
                              const conn = sourceCard.conexoes_saida[connIndex];
                              const newJumps = [...(conn.manualJumps || []), { x, y }];
                              const updatedConexoes = [...sourceCard.conexoes_saida];
                              updatedConexoes[connIndex] = { ...conn, manualJumps: newJumps };
                              handleUpdateCardData(sourceId, { conexoes_saida: updatedConexoes });
                              console.log('[EDGE] Jump manual adicionado em', { x, y, totalJumps: newJumps.length });
                            }
                          }
                          return;
                        }
                        
                        // Clique normal: selecionar a seta
                        if (sourceId && connectionId) setSelectedConnection({ sourceId, connectionId });
                      }}
                      style={{ 
                        pointerEvents: 'all',
                        cursor: 'pointer'
                      }}
                    />
                    {/* Path vis+�vel */}
                    <path 
                      id={conn.id}
                      d={materialVisiblePath} 
                      fill="none" 
                      stroke={color} 
                      strokeWidth={visibleWidth} 
                      strokeDasharray={undefined}
                      strokeLinecap={isMaterialLikeEdge ? 'butt' : 'round'} 
                      strokeLinejoin="round"
                      className="react-flow__edge-path"
                      vectorEffect="non-scaling-stroke"
                      markerStart="none"
                      markerEnd={!conn.arrow
                        ? (isMaterialLikeEdge ? undefined : currentLayout === 'classic' ? `url(#arrowhead-classic)` : conn.marker)
                        : undefined}
                      style={{ 
                        pointerEvents: manualArrowMode && manualArrowSource ? 'none' : 'stroke',
                        cursor: manualArrowMode && manualArrowSource ? 'crosshair' : 'pointer',
                        animation: undefined,
                        strokeWidth: visibleWidth,
                        strokeLinecap: isMaterialLikeEdge ? 'butt' : 'round',
                      }}
                    />
                    {materialArrowPath && (
                      <path
                        d={materialArrowPath}
                        fill={color}
                        stroke={color}
                        strokeWidth={0.5}
                        strokeLinejoin="miter"
                        vectorEffect="non-scaling-stroke"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (sourceId && connectionId) setSelectedConnection({ sourceId, connectionId });
                        }}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (sourceId && connectionId) {
                            setSelectedConnection({ sourceId, connectionId });
                            setContextMenu({ x: e.clientX, y: e.clientY, type: 'edge' });
                          }
                        }}
                        style={{ pointerEvents: manualArrowMode && manualArrowSource ? 'none' : 'all', cursor: 'pointer' }}
                      />
                    )}
                    {/* Renderizar seta customizada se dispon+�vel */}
                    {/* JUN+�+�O EM T: Seta a 90-� apontando para cima ou lateral */}
                    {conn.arrow && conn.connectionType !== 'material' && conn.connectionType !== 'push' && (
                      <path 
                        d={((a) => {
                          const isUp   = a <= -45 && a >= -135;
                          const isDown = a >= 45  && a <= 135;
                          const isLeft = Math.abs(a) > 135;
                          const isIndustrialArrow = conn.connectionType === 'material' || conn.connectionType === 'push';
                          const sz = isIndustrialArrow ? 1.55 : currentLayout === 'classic' ? 1.5 : 1;
                          return isUp   ? `M${-6*sz},0 L0,${-9*sz} L${6*sz},0 Z`
                               : isDown ? `M${-6*sz},0 L0,${9*sz} L${6*sz},0 Z`
                               : isLeft ? `M0,${-6*sz} L${-9*sz},0 L0,${6*sz} Z`
                                        : `M${-9*sz},${-5*sz} L0,0 L${-9*sz},${5*sz} Z`;
                        })(conn.arrow.angle)}
                        fill={color} 
                        stroke={color} 
                        strokeWidth={0.5}
                        strokeLinejoin="miter"
                        transform={`translate(${conn.arrow.x},${conn.arrow.y})`}
                        style={{ pointerEvents: 'none' }}
                      />
                    )}
                    {showStockBadge && stockBadgePos && wip && (
                      <FlowStockBadge
                        value={wip.overflowPerDay}
                        severity={stockBadgeSeverity}
                        x={stockBadgePos.x}
                        y={stockBadgePos.y}
                      />
                    )}
                  </g>
                );
              })}
            
            {/* Depois: Renderizar linhas de fluxo principal (z-index alto) */}
            {displayConnections
              .filter(conn => conn && conn.id && !conn.isReturn)
              .map((conn) => {
                const connectionId = conn.connectionId || conn.id;
                const sourceId = conn.sourceId;
                const targetId = conn.targetId;
                const isSelected = selectedConnection && sourceId === selectedConnection.sourceId && connectionId === selectedConnection.connectionId;
                const wip = targetId ? wipByTargetId.get(targetId) : undefined;
                const incomingToTarget = targetId ? incomingConnectionsByTargetId.get(targetId) ?? [] : [];
                const isMergeTarget = incomingToTarget.length > 1;
                const anchorConnectionId = targetId ? badgeAnchorConnectionIdByTargetId.get(targetId) : undefined;
                const isAnchorEdgeForBadge = !isMergeTarget || anchorConnectionId === conn.id;
                const isTransportDelivery = conn.connectionType === 'vsm_transport_delivery';
                const isMaterialEdge = conn.connectionType === 'material';
                const isPushEdge = conn.connectionType === 'push';
                // V108.0 — Sistema de Classes de Arestas
                const isVsmTransport = conn.connectionType === 'vsm_transport';
                const isVsmFifo      = conn.connectionType === 'vsm_fifo';
                const isVsmInfo      = conn.connectionType === 'vsm_info';
                const isVsmPullSignal = conn.connectionType === 'vsm_pull_signal';
                const isCustomEdge   = isVsmTransport || isVsmFifo || isVsmInfo || isVsmPullSignal;
                const connectionBaseColor =
                  conn.isReturn || conn.color === '#ef4444'
                    ? '#ef4444'
                    : isTransportDelivery || isVsmTransport
                    ? '#0891b2'
                    : isVsmFifo
                    ? '#a855f7'
                    : isVsmInfo
                    ? '#94a3b8'
                    : (conn.color && conn.color !== '#64748b' ? conn.color : effectiveDefaultArrowColor);
                const color = currentLayout === 'classic'
                  ? (isPushEdge ? pushArrowColor : classicArrowColor)
                  : isPushEdge
                  ? pushArrowColor
                  : (isSelected ? '#2563EB' : connectionBaseColor);
                const width = currentLayout === 'classic'
                  ? (isPushEdge ? 60 : isMaterialEdge ? 12 : 4)
                  : isPushEdge
                  ? 57
                  : isMaterialEdge
                  ? 8.5
                  : isTransportDelivery
                  ? 3
                  : (isSelected ? 4.25 : Math.max((conn.width || 2.5) + 0.35, 2.85));
                const targetCard = targetId ? activeCards.find(card => card.id === targetId) : undefined;
                const sourceCard = sourceId ? activeCards.find(card => card.id === sourceId) : undefined;
                const isMaterialLikeEdge = isMaterialEdge || isPushEdge || sourceCard?.tipo === 'inventory' || targetCard?.tipo === 'inventory';
                const visibleWidth = isMaterialLikeEdge ? (currentLayout === 'classic' ? 9.45 : 7.35) : width;
                const shouldShowMaterialTargetArrow = targetCard?.tipo !== 'inventory';
                const materialArrowLength = currentLayout === 'classic' ? 14.58 : 12.96;
                const materialRightExtension = isMaterialLikeEdge && shouldShowMaterialTargetArrow
                  ? (sourceCard?.tipo === 'inventory'
                    ? 11.1375
                    : getIntermediateStockRightExtension(conn.d, activeCards, sourceId, targetId, materialArrowLength))
                  : 0;
                const materialArrowPathD = materialRightExtension > 0
                  ? extendStraightPathEnd(conn.d, materialRightExtension)
                  : conn.d;
                const materialSplitPath = isMaterialLikeEdge && shouldShowMaterialTargetArrow
                  ? splitStraightPathAroundIntermediateStock(conn.d, activeCards, sourceId, targetId, materialArrowLength, materialRightExtension)
                  : null;
                const materialVisiblePath = materialSplitPath ?? (isMaterialLikeEdge && shouldShowMaterialTargetArrow
                  ? trimStraightPathEnd(materialArrowPathD, materialArrowLength)
                  : isMaterialLikeEdge && targetCard?.tipo === 'inventory'
                  ? trimStraightPath(conn.d, 118, 2)
                  : conn.d);
                const materialArrowPath = isMaterialLikeEdge && shouldShowMaterialTargetArrow
                  ? buildMaterialArrowHead(materialArrowPathD, materialArrowLength, Math.max(7.5, visibleWidth * 0.95))
                  : null;
                const showStockBadge = Boolean(
                  wip &&
                  wip.loadPercent > 100 &&
                  wip.overflowPerDay > 0 &&
                  !isSelected &&
                  isAnchorEdgeForBadge
                );
                const stockBadgeSeverity: FlowStockBadgeSeverity =
                  wip && wip.loadPercent > STOCK_BADGE_CRITICAL_THRESHOLD
                    ? 'critical'
                    : wip && wip.loadPercent > STOCK_BADGE_WARNING_THRESHOLD
                    ? 'warning'
                    : 'normal';
                const stockBadgePos = showStockBadge
                  ? targetId
                    ? getStockBadgeTopPosition(targetId, conn)
                    : getStockBadgePosition(conn)
                  : null;
                
                // Ocultar se highlightedPath est+� ativo e a conex+�o n+�o est+� no caminho
                const isInHighlightedPath = effectiveFocusIds
                  ? (effectiveFocusSet.has(sourceId) && effectiveFocusSet.has(targetId))
                  : true;
                const srcCard2 = activeCards.find(c => c.id === sourceId);
                const tgtCard2 = activeCards.find(c => c.id === targetId);
                const isPcpmLocked2 = (c: any) => ['pcpm','nivelamento','info_eletro'].includes(c?.vsmIcon ?? '') && !!(c as any)?.vsmLocked;
                const isPushLockedByPcpm = isPushEdge && (isPcpmLocked2(srcCard2) || isPcpmLocked2(tgtCard2));
                const opacity = isInHighlightedPath ? (isPushLockedByPcpm ? 0.3 : 1) : executiveMode ? 0.08 : 0.1;
                
                const markerId = currentLayout === 'classic'
                  ? (isPushEdge ? 'arrowhead-classic-push' : 'arrowhead-classic')
                  : color === '#2563EB' ? 'arrowhead-blue' : 'arrowhead';

                // V108.0 — Early return para arestas customizadas (vsm_transport, vsm_fifo, vsm_info)
                if (isCustomEdge && conn.sourcePoint && conn.targetPoint) {
                  const srcCard = activeCards.find(c => c.id === sourceId);
                  const targetCard = activeCards.find(c => c.id === targetId);
                  const rawConn = srcCard?.conexoes_saida?.find(c => c.id === connectionId);
                  const isKanbanProductionNode = (card?: CardData) => String((card as any)?.vsmIcon ?? '') === 'kanban_prod';
                  if (isVsmInfo && (rawConn?.kanbanLoopEnabled || isKanbanProductionNode(srcCard) || isKanbanProductionNode(targetCard))) return null;
                  const isPullBufferNode = (card?: CardData) => !!card && (
                    ['supermercado', 'pulmao'].includes((card as any).vsmIcon ?? '') ||
                    (card as any).vsmConvertedToIcon === 'supermercado'
                  );
                  const getPullBufferVisualCenterAnchor = (card?: CardData) => {
                    if (!card?.posicao) return null;
                    const { width, height } = getCanvasHitBox(card, isTimeScaleActive);
                    if (!isFutureMode && (card as any).vsmIcon === 'estoque' && (card as any).vsmConvertedToIcon === 'supermercado') {
                      const offset = ((card as any).vsmSupermarketOverlayOffset ?? { x: 0, y: 0 }) as { x?: number; y?: number };
                      const marketW = Math.round(width * 0.92);
                      const marketH = Math.round(height * 0.98);
                      const marketLeft = Math.round(width * 0.46) + (Number(offset.x) || 0);
                      const marketTop = -Math.round(height * 1.08) + (Number(offset.y) || 0);
                      return {
                        x: card.posicao.x + marketLeft + marketW / 2,
                        y: card.posicao.y + marketTop + marketH / 2,
                      };
                    }
                    return { x: card.posicao.x + width / 2, y: card.posicao.y + height / 2 };
                  };
                  const renderSourcePoint = isVsmPullSignal && isPullBufferNode(srcCard)
                    ? getPullBufferVisualCenterAnchor(srcCard) ?? conn.sourcePoint
                    : conn.sourcePoint;
                  const renderTargetPoint = isVsmPullSignal && isPullBufferNode(targetCard)
                    ? getPullBufferVisualCenterAnchor(targetCard) ?? conn.targetPoint
                    : conn.targetPoint;
                  const isPullInfoSignal = isVsmPullSignal || (isVsmInfo && Boolean(
                    rawConn?.kanbanLoopEnabled
                    || rawConn?.kanbanSupermarketId
                    || isPullBufferNode(srcCard)
                    || isPullBufferNode(targetCard)
                  ));
                  // V154.0: herda transmissão do vsmIcon quando não configurado explicitamente
                  const srcIcon = (srcCard as any)?.vsmIcon ?? '';
                  const iconDefaultTransmission: 'Manual' | 'Eletrônico' | undefined =
                    srcIcon === 'info_manual' ? 'Manual'
                    : srcIcon === 'info_eletro' ? 'Eletrônico'
                    : undefined;
                  // V159.2: targetId é estável — connectionId pode ficar stale
                  const boundComunicacao = activeCards.find((c: any) =>
                    c.vsmIcon === 'comunicacao' && (
                      c.vsmBoundConnection?.connectionId === connectionId ||
                      c.vsmBoundConnection?.targetId === targetId ||
                      (rawConn?.targetId && c.vsmBoundConnection?.targetId === rawConn.targetId)
                    )
                  ) ?? (() => {
                    // V159.3: fallback espacial — card comunicacao mais próximo do midpoint da aresta
                    const mx = (conn.sourcePoint!.x + conn.targetPoint!.x) / 2;
                    const my = (conn.sourcePoint!.y + conn.targetPoint!.y) / 2;
                    const THRESHOLD = 120;
                    let best: any = undefined;
                    let bestDist = THRESHOLD;
                    activeCards.forEach((c: any) => {
                      if (c.vsmIcon !== 'comunicacao' || !c.vsmInfoTransmissionMethod) return;
                      const cx = c.posicao.x + 59; // centro aproximado
                      const cy = c.posicao.y + 20;
                      const dist = Math.sqrt((cx - mx) ** 2 + (cy - my) ** 2);
                      if (dist < bestDist) { bestDist = dist; best = c; }
                    });
                    return best;
                  })();
                  const comunicacaoMethod: 'Manual' | 'Eletrônico' | undefined =
                    (boundComunicacao as any)?.vsmInfoTransmissionMethod;
                  // Ordem final: comunicacao card (fonte de verdade) > rawConn > iconDefault
                  const resolvedTransmission = comunicacaoMethod ?? rawConn?.vsmInfoTransmission ?? iconDefaultTransmission;
                  const fifoState = isVsmFifo
                    ? getFifoRuntimeState(rawConn, srcCard, targetCard, activeCards, simulationShiftMinutes)
                    : { isBlocked: false, maxCapacity: undefined };
                  // V207 — padroniza setas de transporte cliente/fornecedor em comprimento absoluto
                  // (mesmo tamanho visual nas duas setas), centradas entre os cards.
                  const isExternalEntityCard = (card?: CardData) => {
                    if (!card) return false;
                    const icon = String((card as any).vsmIcon ?? '');
                    const ext = String((card as any).extType ?? '').toUpperCase();
                    return icon === 'cliente' || icon === 'fornecedor' || icon === 'externo'
                      || card.tipo === 'card_inicial' || card.tipo === 'card_final'
                      || ext === 'CLIENTE' || ext === 'FORNECEDOR';
                  };
                  const transportFixedLength = (conn.connectionType === 'vsm_transport') && (isExternalEntityCard(srcCard) || isExternalEntityCard(targetCard))
                    ? 76
                    : undefined;

                  return (
                    <g key={conn.id} data-edge="true" data-target-id={conn.targetId} style={{ opacity, pointerEvents: manualArrowMode && manualArrowSource ? 'none' : 'auto' }}>
                      {(() => {
                        // Sinal Puxada: bezier suave quando há waypoints
                        const psWaypoints = isVsmPullSignal ? (rawConn?.waypoints ?? []) : [];
                        let displayD = conn.d;
                        if (isVsmPullSignal && renderSourcePoint && renderTargetPoint) {
                          const pts = psWaypoints.length > 0
                            ? [renderSourcePoint, ...psWaypoints.map((wp: any) => ({ x: Number(wp.x), y: Number(wp.y) })), renderTargetPoint]
                            : [renderSourcePoint, renderTargetPoint];
                          if (pts.length === 2) {
                            displayD = `M ${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)} L ${pts[1].x.toFixed(1)} ${pts[1].y.toFixed(1)}`;
                          } else {
                            // V220: Catmull-Rom via midpoints — stubs retos nos endpoints + Q centrado em cada waypoint.
                            // Evita o "kink" próximo à ponta da seta causado pelo Q final usando p[n-2] como controle.
                            const n = pts.length;
                            const mid = (a: { x: number; y: number }, b: { x: number; y: number }) =>
                              ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
                            const m0 = mid(pts[0], pts[1]);
                            let sd = `M ${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)} L ${m0.x.toFixed(1)} ${m0.y.toFixed(1)}`;
                            for (let i = 1; i < n - 1; i++) {
                              const mi = mid(pts[i], pts[i + 1]);
                              sd += ` Q ${pts[i].x.toFixed(1)} ${pts[i].y.toFixed(1)} ${mi.x.toFixed(1)} ${mi.y.toFixed(1)}`;
                            }
                            const last = pts[n - 1];
                            sd += ` L ${last.x.toFixed(1)} ${last.y.toFixed(1)}`;
                            displayD = sd;
                          }
                        }
                        // Posição do ícone: midpoint + offset armazenado
                        const psIconOffset = isVsmPullSignal ? ((rawConn as any)?.vsmPullSignalIconOffset ?? { x: 0, y: 0 }) : null;
                        const psIconCx = isVsmPullSignal && renderSourcePoint && renderTargetPoint
                          ? (renderSourcePoint.x + renderTargetPoint.x) / 2 + Number(psIconOffset?.x ?? 0)
                          : 0;
                        const psIconCy = isVsmPullSignal && renderSourcePoint && renderTargetPoint
                          ? (renderSourcePoint.y + renderTargetPoint.y) / 2 + Number(psIconOffset?.y ?? 0)
                          : 0;
                        // Estilos adaptativos: blueprint usa cyan, modo normal usa azul
                        const isBPEdge = vsmDesignStyle === 'blueprint';
                        const isTechEdge = vsmDesignStyle === 'technical';
                        const psColor   = isBPEdge ? '#22d3ee' : isTechEdge ? (isDark ? '#e5e7eb' : '#1a1a1a') : '#0e7490';
                        // Paridade total com buildKanbanIcon (kanban-retirada): mesmas dimensões, mesmos strokes, mesma hachura NE
                        const psIconSW  = isBPEdge ? 1.6 : 1.2;
                        const psHatchSW = isBPEdge ? 0.9 : 0.7;
                        const psSpacing = isBPEdge ? 5 : 4;
                        const iw = isBPEdge ? 36 : 31;
                        const ih = isBPEdge ? 26 : 24;
                        const ifold = isBPEdge ? 8 : 6;
                        const psBgFill = canvasBackgroundColor || '#ffffff';
                        const ix0 = psIconCx - iw / 2;
                        const iy0 = psIconCy - ih / 2;
                        const psIconPath = `M ${ix0} ${iy0} L ${ix0 + iw - ifold} ${iy0} L ${ix0 + iw} ${iy0 + ifold} L ${ix0 + iw} ${iy0 + ih} L ${ix0} ${iy0 + ih} Z`;
                        const psClipId = `ps-icon-${connectionId ?? conn.id}`;
                        const psHatchLines: React.ReactNode[] = [];
                        if (isVsmPullSignal) {
                          // Hachura NE (fiel ao kanban-retirada.svg): bottom-left → top-right
                          for (let hi = -ih; hi < iw + ih; hi += psSpacing) {
                            psHatchLines.push(<line key={hi} x1={ix0 + hi} y1={iy0 + ih} x2={ix0 + hi + ih} y2={iy0} stroke={psColor} strokeWidth={psHatchSW} vectorEffect="non-scaling-stroke" />);
                          }
                        }
                        return (
                          <>
                            <g transform={isVsmTransport ? `translate(0, ${Number((rawConn as any)?.vsmTransportOffsetY ?? 0)})` : undefined}>
                            <VsmTransportEdge
                              d={displayD}
                              sourcePoint={renderSourcePoint}
                              targetPoint={renderTargetPoint}
                              arrow={conn.arrow}
                              edgeType={conn.connectionType as VsmEdgeType}
                              transportIcon={rawConn?.vsmTransportIcon ?? 'truck'}
                              label={rawConn?.frequencia}
                              isSelected={!!isSelected}
                              isDark={isDark}
                              isBlueprint={vsmDesignStyle === 'blueprint'}
                              isTechnical={vsmDesignStyle === 'technical'}
                              isBlocked={fifoState.isBlocked}
                              isPullSignalBlocked={Boolean(rawConn?.vsmInfoBlocked || rawConn?.vsmInfoBlockedBySupermarket)}
                              isPullSignal={isPullInfoSignal}
                              maxCapacity={fifoState.maxCapacity}
                              transmissionMethod={resolvedTransmission}
                              fixedLength={transportFixedLength}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (canEditCanvas && engineeringTool === 'block_info' && isVsmInfo && sourceId && connectionId) {
                                  blockInformationConnection(sourceId, connectionId);
                                  return;
                                }
                                // Shift+click: adicionar waypoint
                                if (e.shiftKey && editingWaypoints && selectedConnection?.connectionId === connectionId && canvasRef.current) {
                                  const rect = canvasRef.current.getBoundingClientRect();
                                  const effectiveScale = scale * (contentScale / 12);
                                  const x = (e.clientX - rect.left - pan.x) / effectiveScale;
                                  const y = (e.clientY - rect.top - pan.y) / effectiveScale;
                                  handleAddWaypointToConnection(x, y);
                                  return;
                                }
                                if (sourceId && connectionId) setSelectedConnection({ sourceId, connectionId });
                              }}
                              onContextMenu={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (sourceId && connectionId) {
                                  setSelectedConnection({ sourceId, connectionId });
                                  setContextMenu({ x: e.clientX, y: e.clientY, type: 'edge' });
                                }
                              }}
                            />
                            </g>
                            {isVsmPullSignal && renderSourcePoint && renderTargetPoint && (
                              <g style={{ pointerEvents: 'none' }}>
                                <defs>
                                  <clipPath id={psClipId}>
                                    <path d={psIconPath} />
                                  </clipPath>
                                </defs>
                                <rect x={ix0 - 1} y={iy0 - 1} width={iw + 2} height={ih + 2} fill={psBgFill} stroke="none" />
                                <path d={psIconPath} fill={psBgFill} stroke="none" />
                                <g clipPath={`url(#${psClipId})`}>{psHatchLines}</g>
                                <path d={psIconPath} fill="none" stroke={psColor} strokeWidth={psIconSW} vectorEffect="non-scaling-stroke" />
                                <path d={`M ${ix0 + iw - ifold} ${iy0} L ${ix0 + iw - ifold} ${iy0 + ifold} L ${ix0 + iw} ${iy0 + ifold}`} fill="none" stroke={psColor} strokeWidth={psIconSW} vectorEffect="non-scaling-stroke" />
                              </g>
                            )}
                          </>
                        );
                      })()}
                    </g>
                  );
                }

                return (
                  <g 
                    key={conn.id} 
                    data-edge="true"
                    data-target-id={conn.targetId}
                    onClick={(e) => { 
                      e.stopPropagation(); 
                      if (manualArrowMode && manualArrowSource) {
                        return;
                      }
                      if (sourceId && connectionId) setSelectedConnection({ sourceId, connectionId }); 
                    }} 
                    onContextMenu={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (sourceId && connectionId) {
                        setSelectedConnection({ sourceId, connectionId });
                        setContextMenu({ x: e.clientX, y: e.clientY, type: 'edge' });
                      }
                    }}
                    style={{ cursor: manualArrowMode && manualArrowSource ? 'crosshair' : 'pointer', pointerEvents: manualArrowMode && manualArrowSource ? 'none' : 'auto', zIndex: 10, opacity }}
                  >
                    {/* Path invis+�vel mais grosso para facilitar sele+�+�o */}
                    <path 
                      d={conn.d} 
                      fill="none" 
                      stroke="rgba(0,0,0,0.01)" 
                      strokeWidth={Math.max(visibleWidth * 4, 24)} 
                      strokeLinecap={isMaterialLikeEdge ? 'butt' : 'round'} 
                      strokeLinejoin="round"
                      onContextMenu={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (sourceId && connectionId) {
                          setSelectedConnection({ sourceId, connectionId });
                          setContextMenu({ x: e.clientX, y: e.clientY, type: 'edge' });
                        }
                      }}
                      onClick={(e) => {
                        e.stopPropagation();

                        if (manualArrowMode && manualArrowSource) {
                          return;
                        }
                        
                        // MODO MANUAL ARROW: Conectar em seta existente (jun+�+�o em T)
                        if (manualArrowMode && manualArrowSource && canvasRef.current && sourceId && connectionId) {
                          const rect = canvasRef.current.getBoundingClientRect();
                          const effectiveScale = scale * (contentScale / 12);
                          const clickX = (e.clientX - rect.left - pan.x) / effectiveScale;
                          const clickY = (e.clientY - rect.top - pan.y) / effectiveScale;
                          
                          // Encontrar a seta clicada para obter o targetId
                          const clickedSourceCard = activeCards.find(c => c.id === sourceId);
                          if (!clickedSourceCard?.conexoes_saida) return;
                          
                          const clickedConn = clickedSourceCard.conexoes_saida.find(c => c.id === connectionId);
                          if (!clickedConn) return;
                          
                          // Criar conex+�o que termina no ponto clicado da seta existente
                          const mergeTargetId = clickedConn.targetId;
                          
                          // Criar a nova conex+�o com merge point
                          const newSourceCard = activeCards.find(c => c.id === manualArrowSource.id);
                          if (!newSourceCard) return;
                          
                          const newConnId = `conn-${Date.now()}`;
                          const newConnection = {
                            id: newConnId,
                            targetId: mergeTargetId,
                            sourceHandle: (manualArrowSource.handle || 'right') as 'right' | 'bottom',
                            waypoints: [...manualArrowWaypoints],
                            mergeToConnection: {
                              targetConnectionId: connectionId,
                              mergePoint: { x: clickX, y: clickY }
                            }
                          };
                          
                          const updatedConexoes = [...(newSourceCard.conexoes_saida || []), newConnection];
                          handleUpdateCardData(newSourceCard.id, { conexoes_saida: updatedConexoes });
                          
                          // Limpar modo manual arrow
                          if (setManualArrowMode) setManualArrowMode(false);
                          if (setManualArrowSource) setManualArrowSource(null);
                          if (setManualArrowWaypoints) setManualArrowWaypoints([]);
                          
                          console.log('[EDGE MERGE] Conex+�o criada com merge em seta existente', {
                            newConnId,
                            sourceId: manualArrowSource.id,
                            targetId: mergeTargetId,
                            mergePoint: { x: clickX, y: clickY }
                          });
                          return;
                        }
                        
                        // Se Shift+Click e estamos em modo de edi+�+�o, adicionar waypoint
                        if (e.shiftKey && editingWaypoints && selectedConnection?.connectionId === connectionId && canvasRef.current) {
                          const rect = canvasRef.current.getBoundingClientRect();
                          const effectiveScale = scale * (contentScale / 12);
                          const x = (e.clientX - rect.left - pan.x) / effectiveScale;
                          const y = (e.clientY - rect.top - pan.y) / effectiveScale;
                          handleAddWaypointToConnection(x, y);
                          return;
                        }
                        
                        // Se Alt+Click e estamos em modo de edi+�+�o, adicionar jump manual
                        if (e.altKey && editingWaypoints && canvasRef.current && sourceId && connectionId) {
                          // Garantir que a conex+�o correta est+� selecionada
                          if (selectedConnection?.connectionId !== connectionId) {
                            setSelectedConnection({ sourceId, connectionId });
                          }
                          const rect = canvasRef.current.getBoundingClientRect();
                          const effectiveScale = scale * (contentScale / 12);
                          const x = (e.clientX - rect.left - pan.x) / effectiveScale;
                          const y = (e.clientY - rect.top - pan.y) / effectiveScale;
                          
                          // Adicionar jump diretamente
                          const sourceCard = activeCards.find(c => c.id === sourceId);
                          if (sourceCard?.conexoes_saida) {
                            const connIndex = sourceCard.conexoes_saida.findIndex(c => c.id === connectionId);
                            if (connIndex !== -1) {
                              const conn = sourceCard.conexoes_saida[connIndex];
                              const newJumps = [...(conn.manualJumps || []), { x, y }];
                              const updatedConexoes = [...sourceCard.conexoes_saida];
                              updatedConexoes[connIndex] = { ...conn, manualJumps: newJumps };
                              handleUpdateCardData(sourceId, { conexoes_saida: updatedConexoes });
                              console.log('[EDGE] Jump manual adicionado em', { x, y, totalJumps: newJumps.length });
                            }
                          }
                          return;
                        }
                        
                        if (manualArrowMode && manualArrowSource) {
                          return;
                        }
                        
                        // Clique normal: selecionar a seta
                        if (sourceId && connectionId) setSelectedConnection({ sourceId, connectionId });
                      }}
                      onDoubleClick={(e) => {
                        // Duplo clique em qualquer ponto da seta: adicionar waypoint nesse ponto
                        // (auto-seleciona a conex+�o e entra em modo de edi+�+�o de waypoints)
                        if (!canvasRef.current || !sourceId || !connectionId) return;
                        e.preventDefault();
                        e.stopPropagation();
                        const rect = canvasRef.current.getBoundingClientRect();
                        const effectiveScale = scale * (contentScale / 12);
                        const x = (e.clientX - rect.left - pan.x) / effectiveScale;
                        const y = (e.clientY - rect.top - pan.y) / effectiveScale;
                        const srcCard = activeCards.find(c => c.id === sourceId);
                        if (!srcCard?.conexoes_saida) return;
                        const cIdx = srcCard.conexoes_saida.findIndex(c => c.id === connectionId);
                        if (cIdx === -1) return;
                        const c = srcCard.conexoes_saida[cIdx];
                        const newWaypoints = [...(c.waypoints || []), { x, y }];
                        const updated = [...srcCard.conexoes_saida];
                        updated[cIdx] = { ...c, waypoints: newWaypoints };
                        handleUpdateCardData(sourceId, { conexoes_saida: updated });
                        setSelectedConnection({ sourceId, connectionId });
                        setEditingWaypoints(true);
                      }}
                      style={{ 
                        pointerEvents: 'all',
                        cursor: manualArrowMode && manualArrowSource ? 'crosshair' : 'pointer'
                      }}
                    />
                    {/* Path vis+�vel */}
                    <path 
                      id={conn.id}
                      d={materialVisiblePath} 
                      fill="none" 
                      stroke={color} 
                      strokeWidth={visibleWidth} 
                      strokeDasharray={undefined}
                      strokeLinecap={isMaterialLikeEdge ? 'butt' : 'round'} 
                      strokeLinejoin="round"
                      className="react-flow__edge-path"
                      vectorEffect="non-scaling-stroke"
                      markerStart="none"
                      markerEnd={!conn.arrow
                        ? (isMaterialLikeEdge ? undefined : currentLayout === 'classic' ? `url(#arrowhead-classic)` : conn.marker)
                        : undefined}
                      style={{ 
                        pointerEvents: manualArrowMode && manualArrowSource ? 'none' : 'stroke',
                        cursor: manualArrowMode && manualArrowSource ? 'crosshair' : 'pointer',
                        animation: undefined,
                        strokeWidth: visibleWidth,
                        strokeLinecap: isMaterialLikeEdge ? 'butt' : 'round',
                      }}
                    />
                    {materialArrowPath && (
                      <path
                        d={materialArrowPath}
                        fill={color}
                        stroke={color}
                        strokeWidth={0.5}
                        strokeLinejoin="miter"
                        vectorEffect="non-scaling-stroke"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (sourceId && connectionId) setSelectedConnection({ sourceId, connectionId });
                        }}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (sourceId && connectionId) {
                            setSelectedConnection({ sourceId, connectionId });
                            setContextMenu({ x: e.clientX, y: e.clientY, type: 'edge' });
                          }
                        }}
                        style={{ pointerEvents: manualArrowMode && manualArrowSource ? 'none' : 'all', cursor: 'pointer' }}
                      />
                    )}
                    {/* Renderizar seta customizada se dispon+�vel */}
                    {/* CR+�TICO: N+�O renderizar seta para linhas que param na converg+�ncia - ser+� renderizada separadamente */}
                    {/* JUN+�+�O EM T: Seta a 90-� apontando para cima ou lateral */}
                    {conn.arrow && !conn.stopsAtConvergence && conn.connectionType !== 'material' && conn.connectionType !== 'push' && (
                      <path 
                        d={((a) => {
                          const isUp   = a <= -45 && a >= -135;
                          const isDown = a >= 45  && a <= 135;
                          const isLeft = Math.abs(a) > 135;
                          const isIndustrialArrow = conn.connectionType === 'material' || conn.connectionType === 'push';
                          const sz = isIndustrialArrow ? 1.55 : currentLayout === 'classic' ? 1.5 : 1;
                          return isUp   ? `M${-6*sz},0 L0,${-9*sz} L${6*sz},0 Z`
                               : isDown ? `M${-6*sz},0 L0,${9*sz} L${6*sz},0 Z`
                               : isLeft ? `M0,${-6*sz} L${-9*sz},0 L0,${6*sz} Z`
                                        : `M${-9*sz},${-5*sz} L0,0 L${-9*sz},${5*sz} Z`;
                        })(conn.arrow.angle)}
                        fill={color} 
                        stroke={color} 
                        strokeWidth={0.5}
                        strokeLinejoin="miter"
                        transform={`translate(${conn.arrow.x},${conn.arrow.y})`}
                        style={{ pointerEvents: 'none' }}
                      />
                    )}
                    {showStockBadge && stockBadgePos && wip && (
                      <FlowStockBadge
                        value={wip.overflowPerDay}
                        severity={stockBadgeSeverity}
                        x={stockBadgePos.x}
                        y={stockBadgePos.y}
                      />
                    )}
                    {/* Ícone de caminhão para entrega física (vsm_transport_delivery) */}
                    {isTransportDelivery && conn.sourcePoint && conn.targetPoint && (() => {
                      // Offset perpendicular à linha (lateral) para não sobrepor a seta
                      const dx = conn.targetPoint.x - conn.sourcePoint.x;
                      const dy = conn.targetPoint.y - conn.sourcePoint.y;
                      const len = Math.sqrt(dx * dx + dy * dy) || 1;
                      // Vetor perpendicular normalizado (para a direita da direção da linha)
                      const perpX = -dy / len;
                      const perpY = dx / len;
                      const OFFSET = 34;
                      const mx = (conn.sourcePoint.x + conn.targetPoint.x) / 2 + perpX * OFFSET;
                      const my = (conn.sourcePoint.y + conn.targetPoint.y) / 2 + perpY * OFFSET;
                      const srcCard = activeCards.find(c => c.id === sourceId);
                      const delivConn = srcCard?.conexoes_saida?.find(c => c.id === connectionId);
                      const freq = delivConn?.frequencia;
                      const lote = delivConn?.lote;
                      return (
                        <g transform={`translate(${mx}, ${my})`} style={{ pointerEvents: 'none' }}>
                          <rect x={-26} y={-10} width={lote ? 72 : 52} height={20} rx={4}
                            fill="#083344" stroke="#0891b2" strokeWidth={0.8} opacity={0.92} />
                          <g transform="translate(-22,-7) scale(0.65)">
                            <rect x="0" y="4" width="16" height="10" rx="1.5" fill="#0891b2" opacity="0.9" />
                            <path d="M16 7h5l2 4v3h-7V7z" fill="#0e7490" />
                            <circle cx="4"  cy="15" r="2" fill="#083344" stroke="#22d3ee" strokeWidth="1" />
                            <circle cx="13" cy="15" r="2" fill="#083344" stroke="#22d3ee" strokeWidth="1" />
                            <circle cx="20" cy="15" r="2" fill="#083344" stroke="#22d3ee" strokeWidth="1" />
                          </g>
                          {freq && (
                            <text x={-4} y={4} fontSize={8} fontFamily="JetBrains Mono, monospace"
                              fill="#22d3ee" fontWeight="bold" textAnchor="start">{freq}</text>
                          )}
                          {lote != null && lote > 0 && (
                            <text x={-4} y={13} fontSize={7} fontFamily="JetBrains Mono, monospace"
                              fill="#67e8f9" textAnchor="start">{lote.toLocaleString('pt-BR')}pç</text>
                          )}
                        </g>
                      );
                    })()}
                  </g>
                );
              })}
            
            {/* Renderizar pontos de merge (converg+�ncia de m+�ltiplas entradas) */}
            {/* CR+�TICO: Renderizar seta APENAS no barramento vertical (trunk node), n+�o nas linhas secund+�rias */}
            {/* N+�O renderizar seta no merge node se houver QUALQUER linha convergindo - a seta j+� est+� na linha de converg+�ncia */}
            {mergeNodes && mergeNodes.filter(node => {
              // Filtrar para N+�O renderizar se houver linha vermelha convergindo
              if (!node.isTrunkNode) return false;
              
              const connToThisTarget = processedConnections.filter(c => c.targetId === node.targetId);
              const hasRedConnection = connToThisTarget.some(c => c.color === '#ef4444' || c.isReturn);
              const hasAnyConvergence = connToThisTarget.some(c => c.stopsAtConvergence === true);
              
              // Se h+� linha vermelha convergindo OU qualquer linha com stopsAtConvergence, N+�O renderizar seta no barramento
              // A seta j+� est+� sendo renderizada na linha vertical de converg+�ncia
              return !hasRedConnection && !hasAnyConvergence;
            }).map((node) => {
              // Seta no barramento sempre aponta horizontalmente para a direita (dire+�+�o do fluxo principal)
              const angle = 0;
              const color = currentLayout === 'classic' ? classicArrowColor : effectiveDefaultArrowColor;
              
              return (
                <g key={node.pathId}>
                  <path 
                    d="M-8,-4 L0,0 L-8,4 Z" 
                    fill={color} 
                    stroke={color} 
                    strokeWidth={0.5}
                    strokeLinejoin="miter"
                    transform={`translate(${node.x},${node.y})`} 
                  />
                </g>
              );
            })}
            
            {/* Renderizar setas nas linhas que param na converg+�ncia (linhas secund+�rias que sobem at+� o trunk) */}
            {/* TODAS as linhas de converg+�ncia devem ter setas, independente da cor */}
            {displayConnections && displayConnections
              .filter(conn => conn.stopsAtConvergence && conn.arrow && conn.targetPoint)
              .map((conn) => {
                const arrowColor = effectiveDefaultArrowColor;
                // JUN+�+�O EM T: Determinar forma da seta baseado no +�ngulo
                const arrowPath = conn.arrow!.angle === -90 || conn.arrow!.angle === 270 
                  ? "M-5,0 L0,-8 L5,0 Z"  // Seta apontando para CIMA
                  : conn.arrow!.angle === 90 || conn.arrow!.angle === -270
                    ? "M-5,0 L0,8 L5,0 Z"  // Seta apontando para BAIXO
                    : conn.arrow!.angle === 180 || conn.arrow!.angle === -180
                      ? "M0,-5 L-8,0 L0,5 Z"  // Seta apontando para ESQUERDA
                      : "M-8,-4 L0,0 L-8,4 Z";  // Seta apontando para DIREITA
                      
                return (
                  <g key={`arrow-${conn.id}`}>
                    <path 
                      d={arrowPath}
                      fill={arrowColor} 
                      stroke={arrowColor} 
                      strokeWidth={0.5}
                      strokeLinejoin="miter"
                      transform={`translate(${conn.arrow!.x},${conn.arrow!.y})`} 
                    />
                  </g>
                );
              })}
          </g>
          
          {/* ============================================================================ */}
          {/* HANDLES DE EDI+�+�O DE WAYPOINTS E JUMPS - Dentro do mesmo SVG das setas */}
          {/* ============================================================================ */}
          {editingWaypoints && selectedConnection && (() => {
            const conn = getSelectedConnectionData();
            if (!conn) return null;
            
            const sourceCard = activeCards.find(c => c.id === selectedConnection.sourceId);
            const targetCard = activeCards.find(c => c.id === conn.targetId);
            if (!sourceCard || !targetCard) return null;
            
            const waypoints = conn.waypoints || [];
            const manualJumps = conn.manualJumps || [];
            
            return (
              <g style={{ pointerEvents: 'auto' }}>
                {/* Handles para waypoints existentes (azul) */}
                {waypoints.map((wp, idx) => (
                  <g 
                    key={`waypoint-handle-${idx}`}
                    style={{ cursor: draggingWaypointIndex === idx ? 'grabbing' : 'grab' }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      console.log('[WAYPOINT] MouseDown no waypoint', idx);
                      setDraggingWaypointIndex(idx);
                    }}
                    onDoubleClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleRemoveWaypoint(idx);
                    }}
                  >
                    {/* +�rea clic+�vel maior (invis+�vel) */}
                    <circle 
                      cx={wp.x} 
                      cy={wp.y} 
                      r={28} 
                      fill="transparent"
                      pointerEvents="all"
                      style={{ cursor: 'grab' }}
                    />
                    {/* C+�rculo vis+�vel */}
                    <circle 
                      cx={wp.x} 
                      cy={wp.y} 
                      r={8} 
                      fill={draggingWaypointIndex === idx ? "#1d4ed8" : "#2563eb"} 
                      stroke="#fff" 
                      strokeWidth={2}
                    />
                    <text 
                      x={wp.x} 
                      y={wp.y + 1} 
                      textAnchor="middle" 
                      dominantBaseline="middle" 
                      fill="#fff" 
                      fontSize={10} 
                      fontWeight="bold"
                      style={{ pointerEvents: 'none' }}
                    >
                      {idx + 1}
                    </text>
                  </g>
                ))}
                
                {/* Handles para jumps manuais (laranja para horizontal, verde para vertical) */}
                {manualJumps.map((jump, idx) => {
                  const isHorizontal = jump.type === 'horizontal' || !jump.type;
                  const color = isHorizontal ? '#f97316' : '#10b981'; // Laranja para H, Verde para V
                  const bgColor = isHorizontal ? 'rgba(249, 115, 22, 0.2)' : 'rgba(16, 185, 129, 0.2)';
                  
                  return (
                    <g 
                      key={`jump-handle-${idx}`}
                      style={{ cursor: 'pointer' }}
                      onDoubleClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleRemoveManualJump(idx);
                      }}
                    >
                      {/* +�rea clic+�vel maior (invis+�vel) */}
                      <circle 
                        cx={jump.x} 
                        cy={jump.y} 
                        r={15} 
                        fill={bgColor}
                        style={{ cursor: 'pointer' }}
                      />
                      {/* Indicador de jump - arco diferente por tipo */}
                      {isHorizontal ? (
                        // Arco horizontal (para cima) ���
                        <path 
                          d={`M ${jump.x - 6} ${jump.y} A 6 6 0 0 1 ${jump.x + 6} ${jump.y}`}
                          fill="none" 
                          stroke={color} 
                          strokeWidth={2.5}
                        />
                      ) : (
                        // Arco vertical (para o lado) ���
                        <path 
                          d={`M ${jump.x} ${jump.y - 6} A 6 6 0 0 1 ${jump.x} ${jump.y + 6}`}
                          fill="none" 
                          stroke={color} 
                          strokeWidth={2.5}
                        />
                      )}
                      {/* C+�rculo de fundo com letra */}
                      <circle 
                        cx={jump.x} 
                        cy={isHorizontal ? jump.y - 4 : jump.y} 
                        r={7} 
                        fill={color} 
                        stroke="#fff" 
                        strokeWidth={2}
                      />
                      <text 
                        x={jump.x} 
                        y={isHorizontal ? jump.y - 3 : jump.y + 1} 
                        textAnchor="middle" 
                        dominantBaseline="middle" 
                        fill="#fff" 
                        fontSize={9} 
                        fontWeight="bold"
                        style={{ pointerEvents: 'none' }}
                      >
                        {isHorizontal ? 'H' : 'V'}
                      </text>
                    </g>
                  );
                })}
              </g>
            );
          })()}
        </svg>

        {/* Swimlanes Overlay - Raias horizontais leves */}
        <SwimLaneOverlay
          lanes={swimLanes}
          visible={swimLanesVisible}
          panX={pan.x}
          scale={scale * scaleFactor}
        />

        {/* Instru+�+�es de edi+�+�o de waypoints - posi+�+�o fixa na tela */}
        {editingWaypoints && selectedConnection && (
          <div 
            className="absolute bg-blue-100 border border-blue-400 rounded-lg p-2 text-xs text-blue-800 shadow-lg dark:bg-blue-500/15 dark:border-blue-800/50 dark:text-blue-200"
            style={{ top: 20, left: 20, zIndex: 1000, pointerEvents: 'none' }}
          >
            <strong>Editando Seta:</strong>
            <ul className="mt-1">
              <li>��� <b>Shift+Click na seta</b>: Adicionar ponto</li>
              <li>��� <b>Alt+Click na seta</b>: Adicionar jump (cruzamento)</li>
              <li>��� <b>Arrastar c+�rculo azul</b>: Mover ponto</li>
              <li>��� <b>Double-click no c+�rculo</b>: Remover ponto/jump</li>
              <li>��� <b>ESC</b>: Finalizar edi+�+�o</li>
            </ul>
          </div>
        )}

        {manualArrowMode && manualArrowSource && (
          <svg className="absolute pointer-events-none w-[5000px] h-[5000px] overflow-visible" style={{ zIndex: 5 }}>
            {(() => {
              const s = activeCards.find(c => c.id === manualArrowSource.id);
              if (!s) return null;
              let sx = s.posicao.x + 240, sy = s.posicao.y + 90;
              if (s.tipo === 'decisao') {
                const center = { x: s.posicao.x + 80, y: s.posicao.y + 90 };
                const edge = getDiamondConnectionPoint(center, 160, 160, manualArrowWaypoints[0] || { x: center.x + 100, y: center.y }, s.layoutType === 'classic' ? 2.5 : 0, s.layoutType === 'classic');
                sx = edge.x; sy = edge.y;
              }
              // Criar caminho ortogonal (sem diagonais) para seta manual
              let path = `M ${sx} ${sy}`;
              let lastX = sx;
              let lastY = sy;
              
              manualArrowWaypoints.forEach(p => {
                // Movimento horizontal primeiro, depois vertical (ou vice-versa) para garantir ortogonalidade
                if (Math.abs(p.x - lastX) > 0.1) {
                  path += ` L ${p.x} ${lastY}`;
                  lastX = p.x;
                }
                if (Math.abs(p.y - lastY) > 0.1) {
                  path += ` L ${lastX} ${p.y}`;
                  lastY = p.y;
                }
              });
              
              const d = path;
              return (
                <>
                  <path d={d} fill="none" stroke="#2563eb" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
                  {/* Indicadores visuais dos waypoints */}
                  {manualArrowWaypoints.map((wp, idx) => (
                    <g key={idx}>
                      <circle cx={wp.x} cy={wp.y} r={6} fill="#2563eb" stroke="#fff" strokeWidth={2} />
                      <text x={wp.x} y={wp.y + 1} textAnchor="middle" dominantBaseline="middle" fill="#fff" fontSize="10" fontWeight="bold">{idx + 1}</text>
                    </g>
                  ))}
                </>
              );
            })()}
          </svg>
        )}

        {continuousFlowCells.length > 0 && (
          <svg className="absolute overflow-visible" style={{ zIndex: 6, width: 1, height: 1, pointerEvents: 'auto' }}>
            {continuousFlowCells.map(cell => {
              const isFuturoMode = viewMode === 'future_simulated';
              const staffingValue = globalTaktTimeSeconds && globalTaktTimeSeconds > 0
                ? cell.displayCycleSeconds / globalTaktTimeSeconds
                : null;
              const staffingText = staffingValue != null ? staffingValue.toFixed(2) : null;
              const staffingDecimal = staffingValue != null ? staffingValue - Math.floor(staffingValue) : 0;
              const staffingColor = staffingDecimal <= 0.85
                ? '#22c55e'
                : staffingDecimal <= 0.95
                ? '#facc15'
                : '#ef4444';
              const labelX = cell.x + 10;
              const labelY = cell.y + 11;
              const ungroupX = cell.x + cell.width - 10;
              const ungroupY = cell.y - 10;
              const showUngroupButton = !isFuturoMode && canEditCanvas && (hoveredFlowCellId === cell.id || activeFlowCellId === cell.id);

              // ── V202: FUTURO mode — render como card de processo unificado ──
              if (isFuturoMode) {
                // Compute merged cell name from first + last process card names
                const cellProcessCards = activeCards
                  .filter(c => (c as any).vsmFlowCellId === cell.id && !(c as any).vsmFlowCellBlocked)
                  .sort((a, b) => a.posicao.x - b.posicao.x);
                const getFirstWord = (s: string) => s.split(/[\s\-\/]/)[0];
                const firstName = (cellProcessCards[0] as any)?.atividade ?? 'Célula';
                const lastName  = (cellProcessCards[cellProcessCards.length - 1] as any)?.atividade ?? '';
                const mergedName = !lastName || getFirstWord(firstName) === getFirstWord(lastName)
                  ? firstName
                  : `${getFirstWord(firstName)} + ${getFirstWord(lastName)}`;
                const displayName = mergedName.length > 16 ? mergedName.slice(0, 14) + '…' : mergedName;

                // V204: Pitch computation from nearest nivelamento (OXOX) card
                const nivCard = activeCards.find(c =>
                  (c as any).vsmIcon === 'nivelamento' &&
                  Math.abs((c.posicao.x + 40) - (cell.x + cell.width / 2)) < cell.width * 1.5
                );
                const packSize = Number((nivCard as any)?.vsmNivelamentoPackSize) || 0;
                const pitchSec = packSize > 0 && globalTaktTimeSeconds ? packSize * globalTaktTimeSeconds : 0;
                const pitchMin: string | null = pitchSec > 0
                  ? (pitchSec < 60 ? `${pitchSec.toFixed(0)}s` : `${(pitchSec / 60).toFixed(0)}min`)
                  : null;

                const cardW   = 230;
                const cardH   = 179;
                const celulaScaleX = cardW / 299.44125;
                const celulaScaleY = cardH / 232.99123;
                const hasPitch = pitchMin != null;
                const cardX  = cell.x + (cell.width  - cardW) / 2;
                const cardY  = cell.y + (cell.height - cardH) / 2;
                const isBlueprintCell = vsmDesignStyle === 'blueprint';
                const isTechnicalCell = vsmDesignStyle === 'technical';
                const techCellInk = isDark ? '#e5e7eb' : '#1a1a1a';
                const cellStroke = isBlueprintCell ? (vsmCardColor || '#10b981') : isTechnicalCell ? techCellInk : '#000000';
                const cellSoftFill = isBlueprintCell ? `${cellStroke}16` : isTechnicalCell ? (vsmCardColor || (isDark ? '#cbd5e1' : '#e5e7eb')) : '#ffffff';
                const textMain = isBlueprintCell ? (isDark ? '#dbeafe' : '#0f172a') : isTechnicalCell ? techCellInk : '#000000';
                const labelMuted = isBlueprintCell ? cellStroke : isTechnicalCell ? techCellInk : '#000000';
                const tcSeconds = Math.round(cell.displayCycleSeconds);
                const opsCount = staffingValue != null ? Math.ceil(staffingValue) : null;
                const setupValues = cellProcessCards
                  .map(card => Number((card as any).vsmKaizenProjection?.metrics?.setup?.projectedValue))
                  .filter(value => Number.isFinite(value) && value > 0);
                const setupDisplay = setupValues.length > 0 ? Math.min(...setupValues) : 2;
                const availabilityValues = cellProcessCards
                  .map(card => Number((card as any).vsmKaizenProjection?.metrics?.availability?.projectedValue))
                  .filter(value => Number.isFinite(value) && value > 0);
                const availabilityDisplay = availabilityValues.length > 0
                  ? `${Math.max(...availabilityValues).toFixed(0)}%`
                  : '100%';
                const cellFontFamily = isTechnicalCell
                  ? `Inter, 'Segoe UI', system-ui, sans-serif`
                  : `'JetBrains Mono','Courier New',monospace`;
                const titleFont = `700 15px ${cellFontFamily}`;
                const rowFont = `700 12px ${cellFontFamily}`;
                const rowLabelFont = `400 12px ${cellFontFamily}`;
                const operatorX = cardX + 216 * celulaScaleX;
                const operatorTop = cardY + 75 * celulaScaleY;
                const operatorIconSize = 16;
                const dataBoxLeft = cardX + 79.104457 * celulaScaleX;
                const dataBoxRight = cardX + 222.56616 * celulaScaleX;
                const dataBoxPadding = 10;
                const profileRow = (centerY: number, value: string) => (
                  <text
                    x={(dataBoxLeft + dataBoxRight) / 2}
                    y={cardY + centerY * celulaScaleY}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    style={{ font: rowFont, fill: textMain }}
                  >
                    {value}
                  </text>
                );
                const metricRow = (centerY: number, label: string, value: string | null) => value ? (
                  <g>
                    <text
                      x={dataBoxLeft + dataBoxPadding}
                      y={cardY + centerY * celulaScaleY}
                      textAnchor="start"
                      dominantBaseline="middle"
                      style={{ font: rowLabelFont, fill: labelMuted }}
                    >
                      {label}
                    </text>
                    <text
                      x={dataBoxRight - dataBoxPadding}
                      y={cardY + centerY * celulaScaleY}
                      textAnchor="end"
                      dominantBaseline="middle"
                      style={{ font: rowFont, fill: textMain }}
                    >
                      {value}
                    </text>
                  </g>
                ) : null;

                return (
                  <g key={cell.id} style={{ pointerEvents: 'none' }}>
                    {isBlueprintCell && selectedCardId === cell.id && (
                      <rect x={cardX - 4} y={cardY - 4} width={cardW + 8} height={cardH + 8} fill="none" stroke={cellStroke} strokeWidth={1.4} opacity={0.6} vectorEffect="non-scaling-stroke" />
                    )}
                    {isBlueprintCell ? (
                      <g transform={`translate(${cardX}, ${cardY}) scale(${celulaScaleX}, ${celulaScaleY})`} style={{ filter: `drop-shadow(0 0 2px ${cellStroke}aa) drop-shadow(0 0 8px ${cellStroke}44)` }}>
                        <rect x="1.4223887" y="1.4210055" width="296.59787" height="40.54726" fill={cellSoftFill} stroke={cellStroke} strokeWidth="1.8" vectorEffect="non-scaling-stroke" />
                        <path d="M 1.4173572,41.723192 V 149.07426 H 63.658557 V 107.29399 H 236.61266 v 41.78027 h 61.4107 V 41.723192 Z" fill={cellSoftFill} stroke={cellStroke} strokeWidth="1.8" vectorEffect="non-scaling-stroke" />
                        <path d="M 79.104457,203.91333 H 222.56616 v 27.46924 H 79.104457 Z m 0,-27.42741 H 222.56616 v 27.46924 H 79.104457 Z m 0,-27.07573 H 222.56616 v 27.46924 H 79.104457 Z m 0,-27.25167 H 222.56616 v 27.46934 H 79.104457 Z" fill="none" stroke={cellStroke} strokeWidth="1" vectorEffect="non-scaling-stroke" />
                        <path d="M 77.564657,121.99611 H 222.70616 v 109.2251 H 77.564657 Z" fill="none" stroke={cellStroke} strokeWidth="1.8" vectorEffect="non-scaling-stroke" />
                      </g>
                    ) : isTechnicalCell ? (
                      <g transform={`translate(${cardX}, ${cardY}) scale(${celulaScaleX}, ${celulaScaleY})`}>
                        {/* Solid color fill — title bar + U-body */}
                        <rect x="1.4223887" y="1.4210055" width="296.59787" height="40.54726" fill={cellSoftFill} stroke="none" />
                        <path d="M 1.4173572,41.723192 V 149.07426 H 63.658557 V 107.29399 H 236.61266 v 41.78027 h 61.4107 V 41.723192 Z" fill={cellSoftFill} stroke="none" />
                        {/* Outline stroke */}
                        <rect x="1.4223887" y="1.4210055" width="296.59787" height="40.54726" fill="none" stroke={cellStroke} strokeWidth="1.8" vectorEffect="non-scaling-stroke" />
                        <path d="M 1.4173572,41.723192 V 149.07426 H 63.658557 V 107.29399 H 236.61266 v 41.78027 h 61.4107 V 41.723192 Z" fill="none" stroke={cellStroke} strokeWidth="1.8" vectorEffect="non-scaling-stroke" />
                        <path d="M 79.104457,203.91333 H 222.56616 v 27.46924 H 79.104457 Z m 0,-27.42741 H 222.56616 v 27.46924 H 79.104457 Z m 0,-27.07573 H 222.56616 v 27.46924 H 79.104457 Z m 0,-27.25167 H 222.56616 v 27.46934 H 79.104457 Z" fill="none" stroke={cellStroke} strokeWidth="1" vectorEffect="non-scaling-stroke" />
                        <path d="M 77.564657,121.99611 H 222.70616 v 109.2251 H 77.564657 Z" fill="none" stroke={cellStroke} strokeWidth="1.8" vectorEffect="non-scaling-stroke" />
                      </g>
                    ) : (
                      <image href="/assets/vsm/sketch/celula.svg" x={cardX} y={cardY} width={cardW} height={cardH} preserveAspectRatio="none" />
                    )}
                    <text
                      x={cardX + cardW / 2}
                      y={cardY + 21 * celulaScaleY}
                      textAnchor="middle" dominantBaseline="middle"
                      style={{ font: titleFont, fill: textMain }}
                    >
                      {displayName}
                    </text>
                    {opsCount != null && (
                      <g transform={`translate(${operatorX}, ${operatorTop})`} style={{ pointerEvents: 'none' }}>
                        <text
                          x={operatorIconSize / 2}
                          y={0}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          style={{ font: `700 13px ${cellFontFamily}`, fill: textMain }}
                        >
                          {opsCount}
                        </text>
                        <image
                          href="/assets/vsm/operador.svg"
                          x={0}
                          y={5}
                          width={operatorIconSize}
                          height={operatorIconSize}
                          preserveAspectRatio="xMidYMid meet"
                          style={{ filter: isDark ? 'invert(1)' : 'none', opacity: isBlueprintCell ? 0.92 : 1 }}
                        />
                      </g>
                    )}
                    {profileRow(135.9, 'Dedicado')}
                    {metricRow(163.2, 'T/C', `${tcSeconds}s`)}
                    {metricRow(190.8, 'Setup', setupDisplay > 0 ? `${setupDisplay.toFixed(0)} min` : pitchMin)}
                    {metricRow(218.3, 'Disp', availabilityDisplay)}
                  </g>
                );
              }

              // ── KAIZEN mode — frame tracejado azul (comportamento original) ──
              const cellStroke    = '#2563eb';
              const cellFill      = 'rgba(37,99,235,0.055)';
              const cellDash      = `${8 / scale},${6 / scale}`;
              const cellLabelFill = '#67e8f9';
              const cellLabelDropShadow = 'drop-shadow(0 0 3px rgba(34,211,238,0.7))';

              return (
                <g key={cell.id}>
                  <rect
                    x={cell.x}
                    y={cell.y}
                    width={cell.width}
                    height={cell.height}
                    rx={0}
                    fill={cellFill}
                    stroke={cellStroke}
                    strokeWidth={2 / scale}
                    strokeDasharray={cellDash}
                    vectorEffect="non-scaling-stroke"
                    style={{ pointerEvents: 'none' }}
                  />
                  <rect
                    x={cell.x}
                    y={cell.y}
                    width={cell.width}
                    height={cell.height}
                    rx={0}
                    fill="none"
                    stroke="transparent"
                    strokeWidth={18 / scale}
                    vectorEffect="non-scaling-stroke"
                    onMouseEnter={() => setHoveredFlowCellId(cell.id)}
                    onMouseLeave={() => setHoveredFlowCellId(current => current === cell.id ? null : current)}
                    onClick={(event) => handleFlowCellKaizenClick(cell, event)}
                    style={{ cursor: engineeringTool === 'kaizen' ? 'crosshair' : 'default', pointerEvents: 'stroke' }}
                  />
                  {Array.from({ length: FLOW_CELL_HEADER_SLOT_COUNT }, (_, index) => {
                    const slot = index + 1;
                    const slotX = cell.x + (cell.width / (FLOW_CELL_HEADER_SLOT_COUNT + 1)) * slot;
                    const isOccupied = activeCards.some(card => {
                      const projection = (card as any).vsmKaizenProjection;
                      return projection?.scope === 'flowCell' && projection.flowCellId === cell.id && Number(projection.cellAnchorSlot) === slot;
                    });
                    if (!isOccupied) return null;
                    return (
                      <g key={`slot-${cell.id}-${slot}`} style={{ pointerEvents: 'none' }}>
                        <line x1={slotX} y1={cell.y - 7} x2={slotX} y2={cell.y + 7} stroke={'#facc15'} strokeWidth={1.4 / scale} vectorEffect="non-scaling-stroke" opacity={0.95} />
                        <rect x={slotX - 9} y={cell.y - 13} width={18} height={5} rx={1} fill={'rgba(250,204,21,0.25)'} stroke={'#facc15'} strokeWidth={0.7 / scale} vectorEffect="non-scaling-stroke" />
                      </g>
                    );
                  })}
                  <text
                    x={labelX}
                    y={labelY}
                    textAnchor="start"
                    dominantBaseline="middle"
                    style={{
                      font: "700 10px 'JetBrains Mono','Courier New',monospace",
                      fill: cellLabelFill,
                      filter: cellLabelDropShadow,
                      pointerEvents: 'none',
                    }}
                  >
                    <>
                      <tspan fill={cellLabelFill}>{`Work Content: ${Math.round(cell.rawCycleSeconds)}s | `}</tspan>
                      {staffingText != null ? (
                        <>
                          <tspan fill={cellLabelFill}>Staffing: </tspan>
                          <tspan fill={staffingColor}>{staffingText}</tspan>
                          <tspan fill={cellLabelFill}> Op</tspan>
                        </>
                      ) : (
                        <tspan fill="#facc15">Defina o Takt</tspan>
                      )}
                    </>
                  </text>
                  {showUngroupButton && (
                    <g
                      role="button"
                      aria-label="Desagrupar celula de fluxo continuo"
                      tabIndex={0}
                      onClick={(event) => {
                        event.stopPropagation();
                        ungroupContinuousFlowCell(cell.id);
                        setHoveredFlowCellId(null);
                        setActiveFlowCellId(null);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          event.stopPropagation();
                          ungroupContinuousFlowCell(cell.id);
                          setHoveredFlowCellId(null);
                          setActiveFlowCellId(null);
                        }
                      }}
                      style={{ cursor: 'pointer', pointerEvents: 'auto' }}
                    >
                      <circle cx={ungroupX} cy={ungroupY} r={10} fill="rgba(15,23,42,0.92)" stroke="#60a5fa" strokeWidth={1.25 / scale} vectorEffect="non-scaling-stroke" />
                      <path d={`M ${ungroupX - 4} ${ungroupY - 4} L ${ungroupX + 4} ${ungroupY + 4} M ${ungroupX + 4} ${ungroupY - 4} L ${ungroupX - 4} ${ungroupY + 4}`} stroke="#dbeafe" strokeWidth={1.7 / scale} strokeLinecap="round" vectorEffect="non-scaling-stroke" />
                    </g>
                  )}
                </g>
              );
            })}
          </svg>
        )}

        {boxSelection && !draggingCardId && (
          <svg className="absolute pointer-events-none overflow-visible" style={{ zIndex: 100, width: 1, height: 1 }}>
            <rect x={Math.min(boxSelection.startX, boxSelection.endX)} y={Math.min(boxSelection.startY, boxSelection.endY)} width={Math.abs(boxSelection.endX - boxSelection.startX)} height={Math.abs(boxSelection.endY - boxSelection.startY)} fill="rgba(59, 130, 246, 0.15)" stroke="#3b82f6" strokeWidth={2 / scale} strokeDasharray={`${4/scale},${4/scale}`} />
          </svg>
        )}

        {renderedCards}

        <svg className="absolute overflow-visible" style={{ zIndex: 180, width: 1, height: 1, pointerEvents: 'auto' }}>
          {displayConnections.map((conn) => {
            if (!conn?.sourcePoint || !conn?.targetPoint || conn.connectionType !== 'vsm_info') return null;
            const sourceId = conn.sourceId;
            const connectionId = conn.connectionId || conn.id;
            const srcCard = activeCards.find(card => card.id === sourceId);
            const storedTargetCard = activeCards.find(card => card.id === conn.targetId);
            const rawConn = srcCard?.conexoes_saida?.find(raw => raw.id === connectionId);
            const isKanbanProductionNode = (card?: CardData) => String((card as any)?.vsmIcon ?? '') === 'kanban_prod';
            if (!sourceId || !connectionId || !(rawConn?.kanbanLoopEnabled || isKanbanProductionNode(srcCard) || isKanbanProductionNode(storedTargetCard))) return null;

            const isPullBufferCard = (card?: CardData) => Boolean(card && (
              ['supermercado', 'pulmao'].includes(String((card as any).vsmIcon ?? '')) ||
              (card as any).vsmConvertedToIcon === 'supermercado'
            ));
            const isSupermarketCard = (card?: CardData) => Boolean(card && (
              String((card as any).vsmIcon ?? '') === 'supermercado' ||
              (card as any).vsmConvertedToIcon === 'supermercado'
            ));

            const getTopAnchor = (card?: CardData) => {
              if (!card?.posicao) return null;
              const { width } = getCanvasHitBox(card, isTimeScaleActive);
              return { x: card.posicao.x + width / 2, y: card.posicao.y };
            };
            const getCardCenterAnchor = (card?: CardData) => {
              if (!card?.posicao) return null;
              const { width, height } = getCanvasHitBox(card, isTimeScaleActive);
              return { x: card.posicao.x + width / 2, y: card.posicao.y + height / 2 };
            };
            const getProcessTopOrCenterAnchor = (card?: CardData) => getTopAnchor(card) ?? getCardCenterAnchor(card);
            const getPullBufferCenterAnchor = (card?: CardData) => {
              if (!card?.posicao) return null;
              const { width, height } = getCanvasHitBox(card, isTimeScaleActive);
              if ((card as any).vsmIcon === 'estoque' && (card as any).vsmConvertedToIcon === 'supermercado') {
                const offset = ((card as any).vsmSupermarketOverlayOffset ?? { x: 0, y: 0 }) as { x?: number; y?: number };
                const marketW = Math.round(width * 0.782);
                const marketH = Math.round(height * 0.833);
                const marketLeft = Math.round(width * 0.46) + (Number(offset.x) || 0);
                const marketTop = -Math.round(height * 1.08) + (Number(offset.y) || 0);
                return {
                  x: card.posicao.x + marketLeft + marketW / 2,
                  y: card.posicao.y + marketTop + marketH / 2,
                };
              }
              if ((card as any).vsmIcon === 'estoque' && (card as any).vsmConvertedToIcon === 'pulmao') {
                const offset = ((card as any).vsmPulmaoOverlayOffset ?? { x: 0, y: 0 }) as { x?: number; y?: number };
                const pulmaoW = Math.round(width * 0.553);
                const pulmaoH = Math.round(height * 0.935);
                const pulmaoLeft = Math.round(width * 0.46) + (Number(offset.x) || 0);
                const pulmaoTop = -Math.round(height * 1.15) + (Number(offset.y) || 0);
                return {
                  x: card.posicao.x + pulmaoLeft + pulmaoW / 2,
                  y: card.posicao.y + pulmaoTop + pulmaoH / 2,
                };
              }
              return { x: card.posicao.x + width / 2, y: card.posicao.y + height / 2 };
            };
            const linkedSupermarketCard = activeCards.find(card => card.id === rawConn.kanbanSupermarketId);
            const anchorReference = linkedSupermarketCard ?? storedTargetCard ?? srcCard;
            const nearestSupermarketCard = anchorReference?.posicao
              ? activeCards
                  .filter(card => isPullBufferCard(card) && card.posicao)
                  .sort((a, b) => {
                    const aDistance = Math.hypot(a.posicao.x - anchorReference.posicao.x, a.posicao.y - anchorReference.posicao.y);
                    const bDistance = Math.hypot(b.posicao.x - anchorReference.posicao.x, b.posicao.y - anchorReference.posicao.y);
                    return aDistance - bDistance;
                  })[0]
              : undefined;
            const processCard = !isPullBufferCard(srcCard)
              ? srcCard
              : !isPullBufferCard(storedTargetCard)
              ? storedTargetCard
              : srcCard;
            const supermarketCard = isPullBufferCard(linkedSupermarketCard)
              ? linkedSupermarketCard
              : nearestSupermarketCard
              ?? (isPullBufferCard(srcCard) ? srcCard : undefined)
              ?? (isPullBufferCard(storedTargetCard) ? storedTargetCard : undefined);
            const isKanbanProductionConnection = isKanbanProductionNode(srcCard) || isKanbanProductionNode(storedTargetCard);
            const kanbanCard = isKanbanProductionNode(srcCard) ? srcCard : isKanbanProductionNode(storedTargetCard) ? storedTargetCard : undefined;
            const oppositeKanbanCard = kanbanCard?.id === srcCard?.id ? storedTargetCard : srcCard;
            // V210: no Futuro, o kanban (informação) entra pelo TOPO do supermercado/processo,
            // trafegando na zona de informação acima da linha de fluxo de material.
            // Em Atual/Kaizen mantém o ancoramento ao centro visual do overlay (offset).
            const getPullBufferEntryAnchor = (card?: CardData) => {
              if (isFutureMode) return getTopAnchor(card);
              return getPullBufferCenterAnchor(card);
            };
            const sourcePoint = isKanbanProductionConnection
              ? getCardCenterAnchor(kanbanCard) ?? conn.sourcePoint
              : getTopAnchor(processCard) ?? conn.sourcePoint;
            const targetPoint = isKanbanProductionConnection
              ? isPullBufferCard(oppositeKanbanCard)
                ? getPullBufferEntryAnchor(oppositeKanbanCard) ?? conn.targetPoint
                : getProcessTopOrCenterAnchor(oppositeKanbanCard) ?? conn.targetPoint
              : getPullBufferEntryAnchor(supermarketCard) ?? conn.targetPoint;
            const laneY = targetPoint.y;
            const d = `M ${sourcePoint.x.toFixed(1)} ${sourcePoint.y.toFixed(1)} L ${sourcePoint.x.toFixed(1)} ${laneY.toFixed(1)} L ${targetPoint.x.toFixed(1)} ${targetPoint.y.toFixed(1)}`;
            const cardX = (sourcePoint.x + targetPoint.x) / 2;
            const cardY = laneY;
            const selected = selectedConnection?.sourceId === sourceId && selectedConnection.connectionId === connectionId;

            return (
              <g
                key={`kanban-overlay-${conn.id}`}
                data-edge="true"
                data-target-id={conn.targetId}
                style={{ cursor: 'pointer' }}
                onClick={(event) => {
                  event.stopPropagation();
                  setSelectedConnection({ sourceId, connectionId });
                }}
                onContextMenu={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setSelectedConnection({ sourceId, connectionId });
                  setContextMenu({ x: event.clientX, y: event.clientY, type: 'edge' });
                }}
              >
                <path d={d} fill="none" stroke="transparent" strokeWidth={28} strokeLinecap="round" strokeLinejoin="round" style={{ pointerEvents: 'stroke' }} />
                <path
                  d={d}
                  fill="none"
                  stroke={selected ? '#1d4ed8' : '#2563eb'}
                  strokeWidth={selected ? 2.4 : 1.8}
                  strokeDasharray="5 5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  vectorEffect="non-scaling-stroke"
                  markerEnd="url(#arrowhead-blue)"
                  style={{ pointerEvents: 'none' }}
                />
                <g transform={`translate(${cardX}, ${cardY})`} style={{ pointerEvents: 'none' }}>
                  <rect x={-13} y={-8} width={26} height={16} rx={1.5} fill="#ffffff" stroke="#2563eb" strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
                  {rawConn?.kanbanPackSize != null ? (
                    <text
                      x={0} y={5}
                      textAnchor="middle"
                      fontFamily="'Courier New', Courier, monospace"
                      fontSize={10}
                      fontWeight="bold"
                      fill="#16a34a"
                      style={{ pointerEvents: 'none', userSelect: 'none' }}
                    >
                      {rawConn.kanbanPackSize}
                    </text>
                  ) : (
                    <>
                      <line x1={-8} y1={-3} x2={8} y2={-3} stroke="#2563eb" strokeWidth={1} vectorEffect="non-scaling-stroke" />
                      <line x1={-8} y1={3} x2={5} y2={3} stroke="#2563eb" strokeWidth={1} vectorEffect="non-scaling-stroke" />
                    </>
                  )}
                </g>
              </g>
            );
          })}

          {/* V193 — Loop Kanban: setas tracejadas geradas por vsmKanbanPullerId / vsmKanbanSupplierId */}
          {activeCards.flatMap(superCard => {
            const pullerId  = (superCard as any).vsmKanbanPullerId  as string | undefined;
            const supplierId = (superCard as any).vsmKanbanSupplierId as string | undefined;
            const postoId    = (superCard as any).vsmKanbanPostoId   as string | undefined;
            const pcpmId     = (superCard as any).vsmKanbanPcpmId    as string | undefined;
            const vsmKanbanRetPackSize  = (superCard as any).vsmKanbanRetPackSize  as number | undefined;
            const vsmKanbanProdPackSize = (superCard as any).vsmKanbanProdPackSize as number | undefined;
            if (!pullerId && !supplierId && !pcpmId) return [];
            const isSupermarket = String((superCard as any).vsmIcon ?? '') === 'supermercado' || (superCard as any).vsmConvertedToIcon === 'supermercado';
            const isConvertedPulmao = (superCard as any).vsmConvertedToIcon === 'pulmao';
            if (!isSupermarket && !isConvertedPulmao || !superCard.posicao) return [];

            // Estilos adaptativos blueprint
            const isBP = vsmDesignStyle === 'blueprint';
            const isTechKanban = vsmDesignStyle === 'technical';
            const techKanbanInk = isDark ? '#e5e7eb' : '#1a1a1a';
            const kanbanColor    = isBP ? '#22d3ee' : isTechKanban ? techKanbanInk : '#0e7490';
            const kanbanMarkerId = isBP ? 'arrowhead-kanban-bp' : 'arrowhead-kanban';
            const kanbanSW       = isBP ? 2.2 : 1.8;
            const kanbanDash     = isBP ? '7 4' : '5 5';
            const iconSW         = isBP ? 1.6 : 1.2;
            const iconHatchSW    = isBP ? 0.9 : 0.7;
            // +20% (V215): kanban prod/ret ícones inline
            const iconW          = isBP ? 36 : 31;
            const iconH          = isBP ? 26 : 24;
            const iconFold       = isBP ? 8 : 6;

            // Ícone kanban inline — rect com dobra no canto superior + linha divisória + hachura opcional
            // Renderizado SOBRE a seta (bloqueando visualmente a linha tracejada atrás do ícone)
            const bgFill = canvasBackgroundColor || '#ffffff';

            // Ícone sinal kanban inline (triângulo sólido) para seta de produção do pulmão kanban
            // +20% (V215) +20% (V216)
            const signalSize = isBP ? 35 : 29;
            const buildKanbanSignalIcon = (cx: number, cy: number): React.ReactNode => {
              const s = signalSize;
              const x0 = cx - s / 2;
              const y0 = cy - s / 2;
              // Triângulo apontando para baixo (VSM kanban signal padrão)
              const pts = `${x0},${y0} ${x0 + s},${y0} ${x0 + s / 2},${y0 + s}`;
              return (
                <g key={`sig-${cx}-${cy}`} style={{ pointerEvents: 'none' }}>
                  <rect x={x0 - 2} y={y0 - 2} width={s + 4} height={s + 4} fill={bgFill} stroke="none" />
                  <polygon points={pts} fill={bgFill} stroke={kanbanColor} strokeWidth={iconSW} vectorEffect="non-scaling-stroke" />
                </g>
              );
            };

            const buildKanbanIcon = (cx: number, cy: number, hasHatch: boolean, packSize?: number): React.ReactNode => {
              const x0 = cx - iconW / 2;
              const y0 = cy - iconH / 2;
              // Caminho do ícone (com dobra no canto superior direito)
              const iconPath = `M ${x0} ${y0} L ${x0 + iconW - iconFold} ${y0} L ${x0 + iconW} ${y0 + iconFold} L ${x0 + iconW} ${y0 + iconH} L ${x0} ${y0 + iconH} Z`;
              // ID único por posição para o clipPath SVG
              const clipId = `kbi-${Math.round(cx)}-${Math.round(cy)}`;
              const hatchLines: React.ReactNode[] = [];
              if (hasHatch) {
                // Hachura única direção / (NE), fiel ao SVG canônico kanban-retirada.svg
                const spacing = isBP ? 5 : 4;
                for (let i = -iconH; i < iconW + iconH; i += spacing) {
                  hatchLines.push(
                    <line key={i}
                      x1={x0 + i}          y1={y0 + iconH}
                      x2={x0 + i + iconH}  y2={y0}
                      stroke={kanbanColor} strokeWidth={iconHatchSW}
                      vectorEffect="non-scaling-stroke"
                    />
                  );
                }
              }
              return (
                <g key={`icon-${cx}-${cy}`} style={{ pointerEvents: 'none' }}>
                  {/* Fundo opaco bloqueia a linha da seta atrás do ícone */}
                  <rect x={x0 - 1} y={y0 - 1} width={iconW + 2} height={iconH + 2} fill={bgFill} stroke="none" />
                  {/* Fundo do ícone */}
                  <path d={iconPath} fill={bgFill} stroke="none" />
                  {/* Hachura clipada ao formato do ícone (sem vazar pelo canto dobrado) */}
                  {hasHatch && (
                    <>
                      <defs>
                        <clipPath id={clipId}>
                          <path d={iconPath} />
                        </clipPath>
                      </defs>
                      <g clipPath={`url(#${clipId})`}>{hatchLines}</g>
                    </>
                  )}
                  {/* Contorno do ícone sobre a hachura */}
                  <path d={iconPath} fill="none" stroke={kanbanColor} strokeWidth={iconSW} vectorEffect="non-scaling-stroke" />
                  {/* Dobra do canto superior direito */}
                  <path
                    d={`M ${x0 + iconW - iconFold} ${y0} L ${x0 + iconW - iconFold} ${y0 + iconFold} L ${x0 + iconW} ${y0 + iconFold}`}
                    fill="none" stroke={kanbanColor} strokeWidth={iconSW}
                    vectorEffect="non-scaling-stroke"
                  />
                  {/* Pack size label — número centralizado em cor do kanban, fundo opaco sutil */}
                  {packSize != null && (() => {
                    // V217: tipografia profissional — sem neon, cor do próprio kanban, fundo branco sutil
                    const fontSize = isBP ? 11 : 10;
                    const tw = String(packSize).length * (isBP ? 7 : 6.4);
                    const th = fontSize + 1;
                    return (
                      <g style={{ pointerEvents: 'none' }}>
                        <rect
                          x={cx - tw / 2 - 1.5} y={cy - th / 2}
                          width={tw + 3} height={th}
                          fill={bgFill} stroke="none"
                        />
                        <text
                          x={cx} y={cy}
                          textAnchor="middle"
                          dominantBaseline="central"
                          fontFamily="system-ui, -apple-system, 'Segoe UI', sans-serif"
                          fontSize={fontSize}
                          fontWeight={600}
                          fill={kanbanColor}
                          style={{ userSelect: 'none', letterSpacing: '0.02em' }}
                        >
                          {packSize}
                        </text>
                      </g>
                    );
                  })()}
                </g>
              );
            };

            /** Sketch fallback: PNG image + optional pack size label */
            const buildSketchKanbanOverlay = (cx: number, cy: number, href: string, packSize?: number): React.ReactNode => {
              // V217: tipografia profissional (sem neon)
              const skFontSize = 10;
              const tw = packSize != null ? String(packSize).length * 6.4 : 0;
              const th = skFontSize + 1;
              return (
                <g key={`sk-${cx}-${cy}`} style={{ pointerEvents: 'none' }}>
                  <image href={href} x={cx - 17} y={cy - 13} width={34} height={26} preserveAspectRatio="xMidYMid meet" style={{ pointerEvents: 'none' }} />
                  {packSize != null && (
                    <>
                      <rect
                        x={cx - tw / 2 - 1.5} y={cy - th / 2}
                        width={tw + 3} height={th}
                        fill={bgFill} stroke="none"
                      />
                      <text
                        x={cx} y={cy}
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontFamily="system-ui, -apple-system, 'Segoe UI', sans-serif"
                        fontSize={skFontSize}
                        fontWeight={600}
                        fill={kanbanColor}
                        style={{ userSelect: 'none', letterSpacing: '0.02em' }}
                      >
                        {packSize}
                      </text>
                    </>
                  )}
                </g>
              );
            };

            const smBox = getCanvasHitBox(superCard, isTimeScaleActive);

            // Para supermercado convertido (overlay flutuante acima do estoque ghost),
            // calcular a posição real do ícone supermercado usando os mesmos multiplicadores
            // do InventoryTriangle: left=0.46*W, top=-1.08*H, size=0.92W×0.98H + overlayOffset
            const isConverted = (superCard as any).vsmConvertedToIcon === 'supermercado';
            let smLeft: { x: number; y: number };
            let smRight: { x: number; y: number };
            if (isConvertedPulmao) {
              // Pulmão kanban overlay — same draggable offset pattern using vsmPulmaoOverlayOffset
              const persistedOffset = (superCard as any).vsmPulmaoOverlayOffset ?? { x: 0, y: 0 };
              const ox = Number(persistedOffset.x) || 0;
              const oy = Number(persistedOffset.y) || 0;
              const mW = Math.round(smBox.width * 0.553);
              const mH = Math.round(smBox.height * 0.935);
              const mLeft = Math.round(smBox.width * 0.46) + ox;
              const mTop  = -Math.round(smBox.height * 1.15) + oy;
              const mCenterY = superCard.posicao.y + mTop + mH / 2;
              smLeft  = { x: superCard.posicao.x + mLeft,       y: mCenterY };
              smRight = { x: superCard.posicao.x + mLeft + mW,  y: mCenterY };
            } else if (isConverted) {
              const persistedOffset = (superCard as any).vsmSupermarketOverlayOffset ?? { x: 0, y: 0 };
              const ox = Number(persistedOffset.x) || 0;
              const oy = Number(persistedOffset.y) || 0;
              const mW = Math.round(smBox.width * 0.782);
              const mH = Math.round(smBox.height * 0.833);
              const mLeft = Math.round(smBox.width * 0.46) + ox;
              const mTop  = -Math.round(smBox.height * 1.08) + oy;
              const mCenterY = superCard.posicao.y + mTop + mH / 2;
              smLeft  = { x: superCard.posicao.x + mLeft,       y: mCenterY };
              smRight = { x: superCard.posicao.x + mLeft + mW,  y: mCenterY };
            } else {
              smRight = { x: superCard.posicao.x + smBox.width, y: superCard.posicao.y + smBox.height / 2 };
              smLeft  = { x: superCard.posicao.x,               y: superCard.posicao.y + smBox.height / 2 };
            }

            // Separação vertical entre as duas setas no lado DIREITO do SM
            // Retirada: entra pela direita ACIMA do centro; Produção (com posto): sai pela direita ABAIXO do centro
            // Produção (sem posto/direto para fornecedor): sai pela esquerda abaixo do centro
            const SM_LANE = 9;
            // Ajuste Y de cada seta (draggável pelo usuário — ns-resize)
            const retArrowOY  = Number(((superCard as any).vsmKanbanRetArrowOffset  as any)?.y ?? 0);
            const prodArrowOY = Number(((superCard as any).vsmKanbanProdArrowOffset as any)?.y ?? 0);
            let smRightEntry = { x: smRight.x, y: smRight.y - SM_LANE + retArrowOY };  // retirada entra aqui (direita, acima)
            let smRightExit  = { x: smRight.x, y: smRight.y + SM_LANE + prodArrowOY };  // produção sai aqui se postoId (direita, abaixo)
            let smLeftExit   = { x: smLeft.x,  y: smLeft.y  + SM_LANE + prodArrowOY };  // produção sai pela esquerda (sem posto)
            // V210: no Futuro, kanban entra/sai pelo TOPO do supermercado (zona de informação Rother & Shook).
            // Retirada e produção compartilham o topo do bbox, separadas lateralmente por SM_LANE.
            if (isFutureMode) {
              const cxTop = superCard.posicao.x + smBox.width / 2;
              const topY  = superCard.posicao.y;
              smLeft  = { x: cxTop, y: topY };
              smRight = { x: cxTop, y: topY };
              smRightEntry = { x: cxTop - SM_LANE, y: topY };
              smRightExit  = { x: cxTop + SM_LANE, y: topY };
              smLeftExit   = { x: cxTop + SM_LANE, y: topY };
            }
            // Gap visual antes do ícone posto kanban (evita sobreposição ponta de seta + ícone)
            // V220: reduzido 20% (18 → 14.4) para padronizar com o gap das setas info.
            const POSTO_GAP = 14.4;
            // V220b: para endpoints que TERMINAM no ícone posto kanban (cuja âncora é o
            // CENTRO do ícone, não a borda), somar metade da altura do ícone + respiro de
            // 6 svg-px para manter a respiração visual da seta em relação ao ícone.
            const POSTO_ICON_GAP = iconH / 2 + 6;

            // Resolve anchor top-center: aceita cardId direto ou vsmFlowCellId
            const resolveTopAnchor = (id: string): { x: number; y: number } | null => {
              const directCard = activeCards.find(c => c.id === id);
              if (directCard?.posicao) {
                const box = getCanvasHitBox(directCard, isTimeScaleActive);
                return { x: directCard.posicao.x + box.width / 2, y: directCard.posicao.y };
              }
              // Tenta como vsmFlowCellId — usa bounding box de todos os cards da célula
              const cellCards = activeCards.filter(c => (c as any).vsmFlowCellId === id && c.posicao);
              if (cellCards.length === 0) return null;
              const minX = Math.min(...cellCards.map(c => c.posicao.x));
              const maxX = Math.max(...cellCards.map(c => {
                const box = getCanvasHitBox(c, isTimeScaleActive);
                return c.posicao.x + box.width;
              }));
              const minY = Math.min(...cellCards.map(c => c.posicao.y));
              return { x: (minX + maxX) / 2, y: minY };
            };

            const resolveBottomAnchor = (id: string): { x: number; y: number } | null => {
              const directCard = activeCards.find(c => c.id === id);
              if (directCard?.posicao) {
                const box = getCanvasHitBox(directCard, isTimeScaleActive);
                return { x: directCard.posicao.x + box.width / 2, y: directCard.posicao.y + box.height };
              }
              const cellCards = activeCards.filter(c => (c as any).vsmFlowCellId === id && c.posicao);
              if (cellCards.length === 0) return null;
              const minX = Math.min(...cellCards.map(c => c.posicao.x));
              const maxX = Math.max(...cellCards.map(c => { const box = getCanvasHitBox(c, isTimeScaleActive); return c.posicao.x + box.width; }));
              const maxY = Math.max(...cellCards.map(c => { const box = getCanvasHitBox(c, isTimeScaleActive); return c.posicao.y + box.height; }));
              return { x: (minX + maxX) / 2, y: maxY };
            };

            // Resolve anchor center: para posto kanban (ponto central do ícone)
            const resolveCenterAnchor = (id: string): { x: number; y: number } | null => {
              const card = activeCards.find(c => c.id === id);
              if (!card?.posicao) return null;
              const box = getCanvasHitBox(card, isTimeScaleActive);
              return { x: card.posicao.x + box.width / 2, y: card.posicao.y + box.height / 2 };
            };

            // V220c: helper que retorna a CAIXA do card posto kanban (top/bottom reais),
            // para garantir que setas terminando no posto respeitem POSTO_GAP a partir da
            // borda visível do card, não do centro (cards grandes ficavam sobrepostos).
            const resolvePostoBox = (id: string): { x: number; top: number; bottom: number; left: number; right: number } | null => {
              const card = activeCards.find(c => c.id === id);
              if (!card?.posicao) return null;
              const box = getCanvasHitBox(card, isTimeScaleActive);
              return {
                x: card.posicao.x + box.width / 2,
                top: card.posicao.y,
                bottom: card.posicao.y + box.height,
                left: card.posicao.x,
                right: card.posicao.x + box.width,
              };
            };

            const arrows: React.ReactNode[] = [];

            // ── Fase 2: rota com waypoints + Bezier suave (similar ao PCPM→Expedição) ──
            const buildSmoothKanbanPath = (pts: Array<{ x: number; y: number }>): string => {
              const n = pts.length;
              if (n === 0) return '';
              if (n === 1) return `M ${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)}`;
              if (n === 2) return `M ${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)} L ${pts[1].x.toFixed(1)} ${pts[1].y.toFixed(1)}`;
              // Quadráticas centradas em cada waypoint, com pontas retas até o midpoint inicial/final
              const mid = (a: { x: number; y: number }, b: { x: number; y: number }) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
              const m01 = mid(pts[0], pts[1]);
              let sd = `M ${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)} L ${m01.x.toFixed(1)} ${m01.y.toFixed(1)}`;
              for (let i = 1; i < n - 1; i++) {
                const m = mid(pts[i], pts[i + 1]);
                sd += ` Q ${pts[i].x.toFixed(1)} ${pts[i].y.toFixed(1)} ${m.x.toFixed(1)} ${m.y.toFixed(1)}`;
              }
              sd += ` L ${pts[n - 1].x.toFixed(1)} ${pts[n - 1].y.toFixed(1)}`;
              return sd;
            };
            const makeKanbanPathClickHandler = (field: 'vsmKanbanRetWaypoints' | 'vsmKanbanProdWaypoints') =>
              (e: React.MouseEvent<SVGPathElement>) => {
                if (!e.shiftKey || !canvasRef.current) return;
                e.stopPropagation();
                const rect = canvasRef.current.getBoundingClientRect();
                const es = Math.max(0.01, scale * (contentScale / 12));
                const x = (e.clientX - rect.left - pan.x) / es;
                const y = (e.clientY - rect.top - pan.y) / es;
                addKanbanWaypoint(superCard.id, field, x, y);
              };

            // Helper: ícone kanban visual no SVG (sem pointer events — drag handle é div separado)
            // Retorna [svgNode, {cx, cy, offsetField}] para renderizar o div handle fora do SVG
            const kanbanIconHandles: Array<{ cx: number; cy: number; offsetField: 'vsmKanbanRetIconOffset' | 'vsmKanbanProdIconOffset' | 'vsmKanbanPostoIconOffset'; superCardId: string }> = [];
            const buildDraggableIcon = (
              key: string,
              defaultX: number,
              defaultY: number,
              offsetField: 'vsmKanbanRetIconOffset' | 'vsmKanbanProdIconOffset' | 'vsmKanbanPostoIconOffset',
              iconNode: (cx: number, cy: number) => React.ReactNode,
            ): React.ReactNode => {
              const saved = (superCard as any)[offsetField] as { x: number; y: number } | undefined;
              const ox = Number(saved?.x ?? 0);
              const oy = Number(saved?.y ?? 0);
              const iconCx = defaultX + ox;
              const iconCy = defaultY + oy;
              // Registra posição para o div handle fora do SVG
              kanbanIconHandles.push({ cx: iconCx, cy: iconCy, offsetField, superCardId: superCard.id });
              // V219: removida linha "leash" entre posição original e atual (visual sujo)
              return (
                <g key={key} style={{ pointerEvents: 'none' }}>
                  {iconNode(iconCx, iconCy)}
                </g>
              );
            };

            // ── Kanban Retirada: Puxador → Supermercado
            if (pullerId) {
              const src = resolveTopAnchor(pullerId);
              if (src) {
                const tgt = smRightEntry;
                let d: string;
                let midX: number;
                let midY: number;
                let startPoint: { x: number; y: number };
                let endPoint: { x: number; y: number };
                if (isFutureMode) {
                  // V212/213: ambas as setas no TOPO do supermercado E do puxador, próximas ao centro,
                  // com pequena separação lateral. Cada lado escolhido conforme direção relativa.
                  const smLeftX  = superCard.posicao.x;
                  const smRightX = superCard.posicao.x + smBox.width;
                  const smCenter = (smLeftX + smRightX) / 2;
                  const SM_TOP_LANE = 10;
                  // Retirada entra SEMPRE pela direita do topo do SM (separação fixa da seta de produção)
                  const entryX = smCenter + SM_TOP_LANE;
                  // Puxador (origem): lado voltado ao SM
                  const srcX   = smCenter < src.x ? src.x - SM_TOP_LANE : src.x + SM_TOP_LANE;
                  // Mantém o gap padrão (POSTO_GAP) entre a ponta da seta e a borda do card
                  const srcAdj = { x: srcX, y: src.y - POSTO_GAP };
                  const tgtAdj = { x: entryX, y: tgt.y - POSTO_GAP };
                  const laneY = Math.min(srcAdj.y, tgtAdj.y) - 50 + retArrowOY;
                  d = `M ${srcAdj.x.toFixed(1)} ${srcAdj.y.toFixed(1)} L ${srcAdj.x.toFixed(1)} ${laneY.toFixed(1)} L ${tgtAdj.x.toFixed(1)} ${laneY.toFixed(1)} L ${tgtAdj.x.toFixed(1)} ${tgtAdj.y.toFixed(1)}`;
                  midX = (srcAdj.x + tgtAdj.x) / 2;
                  midY = laneY;
                  startPoint = srcAdj;
                  endPoint = tgtAdj;
                } else {
                  const dirOffset = tgt.x > src.x ? 18 : -18;
                  const srcX = src.x + dirOffset;
                  d = `M ${srcX.toFixed(1)} ${src.y.toFixed(1)} L ${srcX.toFixed(1)} ${tgt.y.toFixed(1)} L ${tgt.x.toFixed(1)} ${tgt.y.toFixed(1)}`;
                  midX = (srcX + tgt.x) / 2;
                  midY = tgt.y;
                  startPoint = { x: srcX, y: src.y };
                  endPoint = { x: tgt.x, y: tgt.y };
                }
                // Fase 2: se há waypoints, substitui rota por Bezier suave
                const retWps = (((superCard as any).vsmKanbanRetWaypoints ?? []) as Array<{ x: number; y: number }>);
                if (retWps.length > 0) {
                  d = buildSmoothKanbanPath([startPoint, ...retWps, endPoint]);
                }
                arrows.push(
                  <g key={`v193-retirada-${superCard.id}-${pullerId}`}>
                    <path d={d} fill="none" stroke={kanbanColor} strokeWidth={kanbanSW} strokeDasharray={kanbanDash} strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" markerEnd={`url(#${kanbanMarkerId})`} style={{ pointerEvents: 'none' }} />
                    {/* Hit-area invisível para Shift+Click adicionar waypoint */}
                    <path d={d} fill="none" stroke="transparent" strokeWidth={14} style={{ pointerEvents: 'stroke', cursor: 'crosshair' }} onClick={makeKanbanPathClickHandler('vsmKanbanRetWaypoints')} onMouseEnter={() => setHoveredKanbanArrow(`${superCard.id}::vsmKanbanRetWaypoints`)} onMouseLeave={() => setHoveredKanbanArrow(null)}>
                      <title>Shift+Clique para adicionar waypoint</title>
                    </path>
                    {buildDraggableIcon(
                      `v193-ret-icon-${superCard.id}`,
                      midX, midY,
                      'vsmKanbanRetIconOffset',
                      // V224: kanban de retirada usa pentágono + crosshatch em todos os estilos
                      // (sketch/technical/blueprint), eliminando o raster legado.
                      (cx, cy) => buildKanbanIcon(cx, cy, true, vsmKanbanRetPackSize),
                    )}
                  </g>
                );
              }
            }

            // ── Kanban Produção / Sinal Kanban: Supermercado → (Posto Kanban →) Fornecedor
            if (supplierId || postoId) {
              // Com posto kanban: produção sai pela DIREITA do SM (abaixo da retirada)
              // Sem posto: produção sai pela ESQUERDA (seta direta para fornecedor)
              const src = postoId ? smRightExit : smLeftExit;

              if (postoId) {
                const posto = resolveCenterAnchor(postoId);
                const postoBox = resolvePostoBox(postoId);
                if (posto && postoBox) {
                  // Seta 1: SM → Posto Kanban
                  let d1: string;
                  let mid1X: number;
                  let mid1Y: number;
                  let startP1: { x: number; y: number };
                  let endP1: { x: number; y: number };
                  if (isFutureMode) {
                    // V214: rota em U invertido pelo topo (zona de informação).
                    const smLeftX  = superCard.posicao.x;
                    const smRightX = superCard.posicao.x + smBox.width;
                    const smCenter = (smLeftX + smRightX) / 2;
                    const SM_TOP_LANE = 10;
                    // Produção sai SEMPRE pela esquerda do topo do SM (separação fixa da seta de retirada)
                    const exitX = smCenter - SM_TOP_LANE;
                    // Mantém o gap padrão (POSTO_GAP) entre a ponta da seta e a borda do supermercado
                    const srcAdj = { x: exitX, y: superCard.posicao.y - POSTO_GAP };
                    // V220p: a seta desce do lane ate a LATERAL ESQUERDA do posto,
                    // entrando no MEIO do card (mesmo Y das saidas d2/d3), com gap horizontal.
                    const postoEndX = postoBox.left - POSTO_GAP;
                    const postoEndY = posto.y;
                    const laneY = Math.min(srcAdj.y, postoEndY - 40) + prodArrowOY;
                    d1 = `M ${srcAdj.x.toFixed(1)} ${srcAdj.y.toFixed(1)} L ${srcAdj.x.toFixed(1)} ${laneY.toFixed(1)} L ${postoEndX.toFixed(1)} ${laneY.toFixed(1)} L ${postoEndX.toFixed(1)} ${postoEndY.toFixed(1)}`;
                    mid1X = (srcAdj.x + postoEndX) / 2;
                    mid1Y = laneY;
                    startP1 = srcAdj;
                    endP1 = { x: postoEndX, y: postoEndY };
                  } else {
                    // V220n: entrada pela LATERAL ESQUERDA, no centro geometrico do card
                    // (espelha as saidas d2/d3 que usam posto.y ± 5).
                    const postoEndX = postoBox.left - POSTO_GAP;
                    const iconCenterY = posto.y;
                    d1 = `M ${src.x.toFixed(1)} ${src.y.toFixed(1)} L ${src.x.toFixed(1)} ${iconCenterY.toFixed(1)} L ${postoEndX.toFixed(1)} ${iconCenterY.toFixed(1)}`;
                    mid1X = (src.x + postoEndX) / 2;
                    mid1Y = iconCenterY;
                    startP1 = { x: src.x, y: src.y };
                    endP1 = { x: postoEndX, y: iconCenterY };
                  }
                  // Fase 2: waypoints + Bezier suave
                  const prodWps = (((superCard as any).vsmKanbanProdWaypoints ?? []) as Array<{ x: number; y: number }>);
                  if (prodWps.length > 0) {
                    // V220j: dois stubs horizontais antes do endpoint p/ forcar a
                    // curva a chegar 100% reta na lateral esquerda do posto.
                    const stubFar = { x: endP1.x - 48, y: endP1.y };
                    const stubNear = { x: endP1.x - 16, y: endP1.y };
                    d1 = buildSmoothKanbanPath([startP1, ...prodWps, stubFar, stubNear, endP1]);
                  }
                  arrows.push(
                    <g key={`v193-posto1-${superCard.id}-${postoId}`}>
                      <path d={d1} fill="none" stroke={kanbanColor} strokeWidth={kanbanSW} strokeDasharray={kanbanDash} strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" markerEnd={`url(#${kanbanMarkerId})`} style={{ pointerEvents: 'none' }} />
                      <path d={d1} fill="none" stroke="transparent" strokeWidth={14} style={{ pointerEvents: 'stroke', cursor: 'crosshair' }} onClick={makeKanbanPathClickHandler('vsmKanbanProdWaypoints')} onMouseEnter={() => setHoveredKanbanArrow(`${superCard.id}::vsmKanbanProdWaypoints`)} onMouseLeave={() => setHoveredKanbanArrow(null)}>
                        <title>Shift+Clique para adicionar waypoint</title>
                      </path>
                      {buildDraggableIcon(
                        `v193-posto1-icon-${superCard.id}`,
                        mid1X, mid1Y,
                        'vsmKanbanPostoIconOffset',
                        (cx, cy) => isBP
                          ? buildKanbanIcon(cx, cy, false, vsmKanbanProdPackSize)
                          : buildSketchKanbanOverlay(cx, cy, '/assets/vsm/kanban_prod.svg', vsmKanbanProdPackSize),
                      )}
                    </g>
                  );

                  // Seta 2: Posto Kanban → Fornecedor
                  if (supplierId) {
                    const tgt = resolveTopAnchor(supplierId);
                    if (tgt) {
                      // V220e: sai pela LATERAL DIREITA do card; separação vertical p/ não sobrepor d3
                      const stemX2 = postoBox.right + POSTO_GAP;
                      const exitY2 = posto.y - 5; // levemente acima do centro
                      const d2 = `M ${stemX2.toFixed(1)} ${exitY2.toFixed(1)} L ${tgt.x.toFixed(1)} ${exitY2.toFixed(1)} L ${tgt.x.toFixed(1)} ${tgt.y.toFixed(1)}`;
                      const mid2X = (stemX2 + tgt.x) / 2;
                      const startY2 = exitY2;
                      arrows.push(
                        <g key={`v193-posto2-${superCard.id}-${supplierId}`}>
                          <path d={d2} fill="none" stroke={kanbanColor} strokeWidth={kanbanSW} strokeDasharray={kanbanDash} strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" markerEnd={`url(#${kanbanMarkerId})`} style={{ pointerEvents: 'none' }} />
                          {buildDraggableIcon(
                            `v193-posto2-icon-${superCard.id}`,
                            mid2X, startY2,
                            'vsmKanbanProdIconOffset',
                            (cx, cy) => isBP
                              ? buildKanbanIcon(cx, cy, false, vsmKanbanProdPackSize)
                              : buildSketchKanbanOverlay(cx, cy, '/assets/vsm/kanban_prod.svg', vsmKanbanProdPackSize),
                          )}
                        </g>
                      );
                    }
                  }

                  // Seta 3: Posto Kanban → PCPM
                  if (pcpmId) {
                    const pcpmTgt = resolveBottomAnchor(pcpmId);
                    if (pcpmTgt) {
                      // V220e: sai pela LATERAL DIREITA do card; separação vertical p/ não sobrepor d2
                      const stemX3 = postoBox.right + POSTO_GAP;
                      const exitY3 = posto.y + 5; // levemente abaixo do centro
                      const d3 = `M ${stemX3.toFixed(1)} ${exitY3.toFixed(1)} L ${pcpmTgt.x.toFixed(1)} ${exitY3.toFixed(1)} L ${pcpmTgt.x.toFixed(1)} ${pcpmTgt.y.toFixed(1)}`;
                      const mid3X = (stemX3 + pcpmTgt.x) / 2;
                      const startY3 = exitY3;
                      arrows.push(
                        <g key={`v193-pcpm-${superCard.id}-${pcpmId}`}>
                          <path d={d3} fill="none" stroke={kanbanColor} strokeWidth={kanbanSW} strokeDasharray={kanbanDash} strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" markerEnd={`url(#${kanbanMarkerId})`} style={{ pointerEvents: 'none' }} />
                          {buildDraggableIcon(
                            `v193-pcpm-icon-${superCard.id}`,
                            mid3X, startY3,
                            'vsmKanbanPcpmIconOffset',
                            (cx, cy) => isBP
                              ? buildKanbanIcon(cx, cy, false)
                              : <image href="/assets/vsm/kanban_prod.svg" x={cx - 17} y={cy - 13} width={34} height={26} preserveAspectRatio="xMidYMid meet" style={{ pointerEvents: 'none' }} />,
                          )}
                        </g>
                      );
                    }
                  }
                }
              } else if (supplierId) {
                // Seta direta SM → Fornecedor
                const tgt = resolveTopAnchor(supplierId);
                if (tgt) {
                  let d: string;
                  let midX: number;
                  let midY: number;
                  let startP: { x: number; y: number };
                  let endP: { x: number; y: number };
                  if (isFutureMode) {
                    // V212/213: ambas as setas no TOPO do supermercado E do fornecedor, próximas ao centro,
                    // com pequena separação lateral. Cada lado escolhido conforme direção relativa.
                    const smLeftX  = superCard.posicao.x;
                    const smRightX = superCard.posicao.x + smBox.width;
                    const smCenter = (smLeftX + smRightX) / 2;
                    const SM_TOP_LANE = 10;
                    // SM (origem): produção sai SEMPRE pela esquerda do topo (separação fixa da seta de retirada)
                    const exitX = smCenter - SM_TOP_LANE;
                    // Fornecedor (destino): lado voltado ao SM
                    const tgtX  = smCenter < tgt.x ? tgt.x - SM_TOP_LANE : tgt.x + SM_TOP_LANE;
                    // Mantém o gap padrão (POSTO_GAP) entre a ponta da seta e a borda dos cards
                    const srcAdj = { x: exitX, y: src.y - POSTO_GAP };
                    const tgtAdj = { x: tgtX, y: tgt.y - POSTO_GAP };
                    const laneY = Math.min(srcAdj.y, tgtAdj.y) - 50 + prodArrowOY;
                    d = `M ${srcAdj.x.toFixed(1)} ${srcAdj.y.toFixed(1)} L ${srcAdj.x.toFixed(1)} ${laneY.toFixed(1)} L ${tgtAdj.x.toFixed(1)} ${laneY.toFixed(1)} L ${tgtAdj.x.toFixed(1)} ${tgtAdj.y.toFixed(1)}`;
                    midX = (srcAdj.x + tgtAdj.x) / 2;
                    midY = laneY;
                    startP = srcAdj;
                    endP = tgtAdj;
                  } else {
                    d = `M ${src.x.toFixed(1)} ${src.y.toFixed(1)} L ${tgt.x.toFixed(1)} ${src.y.toFixed(1)} L ${tgt.x.toFixed(1)} ${tgt.y.toFixed(1)}`;
                    midX = (src.x + tgt.x) / 2;
                    midY = src.y;
                    startP = { x: src.x, y: src.y };
                    endP = { x: tgt.x, y: tgt.y };
                  }
                  // Fase 2: waypoints + Bezier suave
                  const prodWps = (((superCard as any).vsmKanbanProdWaypoints ?? []) as Array<{ x: number; y: number }>);
                  if (prodWps.length > 0) {
                    d = buildSmoothKanbanPath([startP, ...prodWps, endP]);
                  }
                  arrows.push(
                    <g key={`v193-prod-${superCard.id}-${supplierId}`}>
                      <path d={d} fill="none" stroke={kanbanColor} strokeWidth={kanbanSW} strokeDasharray={kanbanDash} strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" markerEnd={`url(#${kanbanMarkerId})`} style={{ pointerEvents: 'none' }} />
                      <path d={d} fill="none" stroke="transparent" strokeWidth={14} style={{ pointerEvents: 'stroke', cursor: 'crosshair' }} onClick={makeKanbanPathClickHandler('vsmKanbanProdWaypoints')} onMouseEnter={() => setHoveredKanbanArrow(`${superCard.id}::vsmKanbanProdWaypoints`)} onMouseLeave={() => setHoveredKanbanArrow(null)}>
                        <title>Shift+Clique para adicionar waypoint</title>
                      </path>
                      {buildDraggableIcon(
                        `v193-prod-icon-${superCard.id}`,
                        midX, midY,
                        'vsmKanbanProdIconOffset',
                        (cx, cy) => isConvertedPulmao
                          ? isBP
                            ? buildKanbanSignalIcon(cx, cy)
                            : <image href="/assets/vsm/kanban_sinal.svg" x={cx - 16} y={cy - 16} width={31} height={31} preserveAspectRatio="xMidYMid meet" style={{ pointerEvents: 'none' }} />
                          : isBP
                            ? buildKanbanIcon(cx, cy, false, vsmKanbanProdPackSize)
                            : buildSketchKanbanOverlay(cx, cy, '/assets/vsm/kanban_prod.svg', vsmKanbanProdPackSize),
                      )}
                    </g>
                  );
                }
              }
            }

            return arrows;
          })}
        </svg>

        {/* V193 — Drag handles para ícones kanban (divs fora do SVG, mesmo padrão do kaizen marker) */}
        {activeCards.flatMap(superCard => {
          const pullerId   = (superCard as any).vsmKanbanPullerId   as string | undefined;
          const supplierId = (superCard as any).vsmKanbanSupplierId as string | undefined;
          const postoId    = (superCard as any).vsmKanbanPostoId    as string | undefined;
          const pcpmId     = (superCard as any).vsmKanbanPcpmId     as string | undefined;
          if (!pullerId && !supplierId && !postoId && !pcpmId) return [];
          const isSupermarket = String((superCard as any).vsmIcon ?? '') === 'supermercado' || (superCard as any).vsmConvertedToIcon === 'supermercado';
          const isConvPulmao  = (superCard as any).vsmConvertedToIcon === 'pulmao';
          if (!isSupermarket && !isConvPulmao || !superCard.posicao) return [];

          const smBox = getCanvasHitBox(superCard, isTimeScaleActive);
          const isConverted = (superCard as any).vsmConvertedToIcon === 'supermercado';

          let smRight: { x: number; y: number };
          let smLeft:  { x: number; y: number };
          if (isConvPulmao) {
            const po = (superCard as any).vsmPulmaoOverlayOffset ?? { x: 0, y: 0 };
            const mW = Math.round(smBox.width * 0.553);
            const mH = Math.round(smBox.height * 0.935);
            const mLeft = Math.round(smBox.width * 0.46) + Number(po.x || 0);
            const mTop  = -Math.round(smBox.height * 1.15) + Number(po.y || 0);
            const cy = superCard.posicao.y + mTop + mH / 2;
            smLeft  = { x: superCard.posicao.x + mLeft,       y: cy };
            smRight = { x: superCard.posicao.x + mLeft + mW,  y: cy };
          } else if (isConverted) {
            const so = (superCard as any).vsmSupermarketOverlayOffset ?? { x: 0, y: 0 };
            const mW = Math.round(smBox.width * 0.782);
            const mH = Math.round(smBox.height * 0.833);
            const mLeft = Math.round(smBox.width * 0.46) + Number(so.x || 0);
            const mTop  = -Math.round(smBox.height * 1.08) + Number(so.y || 0);
            const cy = superCard.posicao.y + mTop + mH / 2;
            smLeft  = { x: superCard.posicao.x + mLeft,       y: cy };
            smRight = { x: superCard.posicao.x + mLeft + mW,  y: cy };
          } else {
            smRight = { x: superCard.posicao.x + smBox.width, y: superCard.posicao.y + smBox.height / 2 };
            smLeft  = { x: superCard.posicao.x,               y: superCard.posicao.y + smBox.height / 2 };
          }

          const SM_LANE = 9;
          // Aplica os mesmos ajustes Y das setas para manter consistência com o SVG
          const retArrowOY2  = Number(((superCard as any).vsmKanbanRetArrowOffset  as any)?.y ?? 0);
          const prodArrowOY2 = Number(((superCard as any).vsmKanbanProdArrowOffset as any)?.y ?? 0);
          const smRightEntry = { x: smRight.x, y: smRight.y - SM_LANE + retArrowOY2 };
          const smRightExit  = { x: smRight.x, y: smRight.y + SM_LANE + prodArrowOY2 };
          const smLeftExit   = { x: smLeft.x,  y: smLeft.y  + SM_LANE + prodArrowOY2 };

          const resolveTopAnchorDiv = (id: string): { x: number; y: number } | null => {
            const dc = activeCards.find(c => c.id === id);
            if (dc?.posicao) {
              const b = getCanvasHitBox(dc, isTimeScaleActive);
              return { x: dc.posicao.x + b.width / 2, y: dc.posicao.y };
            }
            const cellCards = activeCards.filter(c => (c as any).vsmFlowCellId === id && c.posicao);
            if (!cellCards.length) return null;
            const minX = Math.min(...cellCards.map(c => c.posicao.x));
            const maxX = Math.max(...cellCards.map(c => { const b = getCanvasHitBox(c, isTimeScaleActive); return c.posicao.x + b.width; }));
            const minY = Math.min(...cellCards.map(c => c.posicao.y));
            return { x: (minX + maxX) / 2, y: minY };
          };
          const resolveBottomAnchorDiv = (id: string): { x: number; y: number } | null => {
            const dc = activeCards.find(c => c.id === id);
            if (dc?.posicao) {
              const b = getCanvasHitBox(dc, isTimeScaleActive);
              return { x: dc.posicao.x + b.width / 2, y: dc.posicao.y + b.height };
            }
            const cellCards = activeCards.filter(c => (c as any).vsmFlowCellId === id && c.posicao);
            if (!cellCards.length) return null;
            const minX = Math.min(...cellCards.map(c => c.posicao.x));
            const maxX = Math.max(...cellCards.map(c => { const b = getCanvasHitBox(c, isTimeScaleActive); return c.posicao.x + b.width; }));
            const maxY = Math.max(...cellCards.map(c => { const b = getCanvasHitBox(c, isTimeScaleActive); return c.posicao.y + b.height; }));
            return { x: (minX + maxX) / 2, y: maxY };
          };
          const resolveCenterDiv = (id: string): { x: number; y: number } | null => {
            const c = activeCards.find(x => x.id === id);
            if (!c?.posicao) return null;
            const b = getCanvasHitBox(c, isTimeScaleActive);
            return { x: c.posicao.x + b.width / 2, y: c.posicao.y + b.height / 2 };
          };

          // Coleta posições de cada ícone kanban e handles de seta
          type IconHandle = {
            key: string;
            field: 'vsmKanbanRetIconOffset' | 'vsmKanbanProdIconOffset' | 'vsmKanbanPostoIconOffset' | 'vsmKanbanRetArrowOffset' | 'vsmKanbanProdArrowOffset' | 'vsmKanbanPcpmIconOffset';
            defaultX: number;
            defaultY: number;
            yOnly?: boolean;    // só move verticalmente (setas)
            handleW?: number;   // largura override do handle
            handleH?: number;   // altura override do handle
          };
          const handles: IconHandle[] = [];

          if (pullerId) {
            const src = resolveTopAnchorDiv(pullerId);
            if (src) {
              const tgt = smRightEntry;
              let iconX: number;
              let iconY: number;
              if (isFutureMode) {
                // Espelha o cálculo de path em modo Futuro (V212/213)
                const smLeftX  = superCard.posicao.x;
                const smRightX = superCard.posicao.x + smBox.width;
                const smCenter = (smLeftX + smRightX) / 2;
                const SM_TOP_LANE = 10;
                const entryX = smCenter + SM_TOP_LANE;
                const srcX2  = smCenter < src.x ? src.x - SM_TOP_LANE : src.x + SM_TOP_LANE;
                // Espelha o gap padrão do path
                const srcY2  = src.y - 18;
                const tgtY   = superCard.posicao.y - 18;
                const laneY  = Math.min(srcY2, tgtY) - 50 + retArrowOY2;
                iconX = (srcX2 + entryX) / 2;
                iconY = laneY;
              } else {
                const dirOffset = tgt.x > src.x ? 18 : -18;
                const srcX = src.x + dirOffset;
                iconX = (srcX + tgt.x) / 2;
                iconY = tgt.y;
              }
              // Handle de ícone (move livremente)
              handles.push({ key: `ret-${superCard.id}`, field: 'vsmKanbanRetIconOffset', defaultX: iconX, defaultY: iconY });
              // Handle de linha de seta (move só Y) — controla a altura da "alameda" sobre o topo
              if (isFutureMode) {
                handles.push({ key: `retarrow-${superCard.id}`, field: 'vsmKanbanRetArrowOffset', defaultX: iconX, defaultY: iconY, yOnly: true, handleW: 48, handleH: 14 });
              } else {
                handles.push({ key: `retarrow-${superCard.id}`, field: 'vsmKanbanRetArrowOffset', defaultX: iconX, defaultY: smRight.y - SM_LANE + retArrowOY2, yOnly: true, handleW: 48, handleH: 14 });
              }
            }
          }
          if (postoId) {
            const posto = resolveCenterDiv(postoId);
            const src = smRightExit;
            if (posto) {
              let posto1X: number;
              let posto1Y: number;
              if (isFutureMode) {
                const smLeftX  = superCard.posicao.x;
                const smRightX = superCard.posicao.x + smBox.width;
                const smCenter = (smLeftX + smRightX) / 2;
                const SM_TOP_LANE = 10;
                const exitX = smCenter - SM_TOP_LANE;
                // Espelha o gap padrão do path
                const srcY  = superCard.posicao.y - 18;
                const postoTop = posto.y - 18;
                const laneY = Math.min(srcY, postoTop) - 50 + prodArrowOY2;
                posto1X = (exitX + posto.x) / 2;
                posto1Y = laneY;
              } else {
                posto1X = (src.x + posto.x) / 2;
                posto1Y = src.y;
              }
              handles.push({ key: `posto1-${superCard.id}`, field: 'vsmKanbanPostoIconOffset', defaultX: posto1X, defaultY: posto1Y });
              // Handle de linha de seta de produção (SM → Posto) — controla altura da alameda no Futuro
              if (isFutureMode) {
                handles.push({ key: `prodarrow-${superCard.id}`, field: 'vsmKanbanProdArrowOffset', defaultX: posto1X, defaultY: posto1Y, yOnly: true, handleW: 48, handleH: 14 });
              } else {
                handles.push({ key: `prodarrow-${superCard.id}`, field: 'vsmKanbanProdArrowOffset', defaultX: (smRight.x + posto.x) / 2, defaultY: smRight.y + SM_LANE + prodArrowOY2, yOnly: true, handleW: 48, handleH: 14 });
              }
              if (supplierId) {
                const tgt = resolveTopAnchorDiv(supplierId);
                if (tgt) handles.push({ key: `posto2-${superCard.id}`, field: 'vsmKanbanProdIconOffset', defaultX: (posto.x + tgt.x) / 2, defaultY: posto.y });
              }
              // Handle ícone posto → PCPM
              if (pcpmId) {
                const pcpmTgt = resolveBottomAnchorDiv(pcpmId);
                if (pcpmTgt) handles.push({ key: `pcpm-${superCard.id}`, field: 'vsmKanbanPcpmIconOffset', defaultX: (posto.x + pcpmTgt.x) / 2, defaultY: posto.y });
              }
            }
          } else if (supplierId) {
            const tgt = resolveTopAnchorDiv(supplierId);
            const src = smLeftExit;
            if (tgt) {
              let iconX: number;
              let iconY: number;
              if (isFutureMode) {
                const smLeftX  = superCard.posicao.x;
                const smRightX = superCard.posicao.x + smBox.width;
                const smCenter = (smLeftX + smRightX) / 2;
                const SM_TOP_LANE = 10;
                const exitX = smCenter - SM_TOP_LANE;
                const tgtX  = smCenter < tgt.x ? tgt.x - SM_TOP_LANE : tgt.x + SM_TOP_LANE;
                // Espelha o gap padrão do path
                const srcY  = superCard.posicao.y - 18;
                const tgtY  = tgt.y - 18;
                const laneY = Math.min(srcY, tgtY) - 50 + prodArrowOY2;
                iconX = (exitX + tgtX) / 2;
                iconY = laneY;
              } else {
                iconX = (src.x + tgt.x) / 2;
                iconY = src.y;
              }
              handles.push({ key: `prod-${superCard.id}`, field: 'vsmKanbanProdIconOffset', defaultX: iconX, defaultY: iconY });
              // Handle de linha de seta de produção direta — controla altura da alameda no Futuro
              if (isFutureMode) {
                handles.push({ key: `prodarrow-${superCard.id}`, field: 'vsmKanbanProdArrowOffset', defaultX: iconX, defaultY: iconY, yOnly: true, handleW: 48, handleH: 14 });
              } else {
                handles.push({ key: `prodarrow-${superCard.id}`, field: 'vsmKanbanProdArrowOffset', defaultX: (smLeft.x + tgt.x) / 2, defaultY: smLeft.y + SM_LANE + prodArrowOY2, yOnly: true, handleW: 48, handleH: 14 });
              }
            }
          }

          const effectiveScale = scale * (contentScale / 12);
          const HANDLE_SIZE = 28;

          // ── Waypoint handles (Fase 2) — pequenos círculos para arrastar/remover waypoints ──
          const waypointHandleNodes: React.ReactNode[] = [];
          (['vsmKanbanRetWaypoints', 'vsmKanbanProdWaypoints'] as const).forEach((wpField) => {
            const wps = (((superCard as any)[wpField] ?? []) as Array<{ x: number; y: number }>);
            const hoverKey = `${superCard.id}::${wpField}`;
            const groupVisible = hoveredKanbanArrow === hoverKey;
            wps.forEach((wp, idx) => {
              const WP_SIZE = 14;          // tamanho visual do ponto
              const WP_HIT  = 36;          // tamanho da área clicável (invisível)
              const dotColor = vsmDesignStyle === 'blueprint' ? '#22d3ee' : vsmDesignStyle === 'technical' ? (isDark ? '#e5e7eb' : '#1a1a1a') : '#0e7490';
              waypointHandleNodes.push(
                <div
                  key={`v193-wp-${superCard.id}-${wpField}-${idx}`}
                  style={{
                    position: 'absolute',
                    left: wp.x - WP_HIT / 2,
                    top:  wp.y - WP_HIT / 2,
                    width: WP_HIT,
                    height: WP_HIT,
                    background: 'transparent',
                    zIndex: 54,
                    pointerEvents: groupVisible ? 'auto' : 'none',
                    cursor: 'grab',
                    touchAction: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  title="Arraste para mover waypoint · botão direito remove"
                  onMouseEnter={() => setHoveredKanbanArrow(hoverKey)}
                  onMouseLeave={() => setHoveredKanbanArrow(null)}
                  onMouseDown={(e) => { e.stopPropagation(); }}
                  onPointerDown={(e) => {
                    e.stopPropagation();
                    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
                    kanbanWaypointDragRef.current = {
                      cardId: superCard.id,
                      field: wpField,
                      index: idx,
                      startClientX: e.clientX,
                      startClientY: e.clientY,
                      startWpX: wp.x,
                      startWpY: wp.y,
                    };
                  }}
                  onPointerMove={(e) => {
                    const drag = kanbanWaypointDragRef.current;
                    if (!drag || drag.cardId !== superCard.id || drag.field !== wpField || drag.index !== idx) return;
                    e.stopPropagation();
                    const es = Math.max(0.01, effectiveScale);
                    const nx = drag.startWpX + (e.clientX - drag.startClientX) / es;
                    const ny = drag.startWpY + (e.clientY - drag.startClientY) / es;
                    moveKanbanWaypoint(superCard.id, wpField, idx, nx, ny);
                  }}
                  onPointerUp={(e) => {
                    e.stopPropagation();
                    if ((e.currentTarget as HTMLElement).hasPointerCapture(e.pointerId)) {
                      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
                    }
                    kanbanWaypointDragRef.current = null;
                  }}
                  onPointerCancel={() => { kanbanWaypointDragRef.current = null; }}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    removeKanbanWaypoint(superCard.id, wpField, idx);
                  }}
                >
                  {/* Dot visual centralizado dentro da hitbox */}
                  <div
                    style={{
                      width: WP_SIZE,
                      height: WP_SIZE,
                      borderRadius: '50%',
                      background: dotColor,
                      border: '2px solid white',
                      boxShadow: '0 0 0 1px rgba(0,0,0,0.25)',
                      opacity: groupVisible ? 1 : 0,
                      transition: 'opacity 120ms ease',
                      pointerEvents: 'none',
                    }}
                  />
                </div>
              );
            });
          });

          const iconHandleNodes = handles.map(({ key, field, defaultX, defaultY, yOnly, handleW, handleH }) => {
            const saved = (superCard as any)[field] as { x: number; y: number } | undefined;
            const ox = Number(saved?.x ?? 0);
            const oy = Number(saved?.y ?? 0);
            const W = handleW ?? HANDLE_SIZE;
            const H = handleH ?? HANDLE_SIZE;
            return (
              <div
                key={`v193-handle-${key}`}
                style={{
                  position: 'absolute',
                  left: defaultX + (yOnly ? 0 : ox) - W / 2,
                  top:  defaultY + oy - H / 2,
                  width: W,
                  height: H,
                  zIndex: 53,
                  pointerEvents: 'auto',
                  cursor: yOnly ? 'ns-resize' : 'move',
                  touchAction: 'none',
                  borderRadius: yOnly ? 3 : 0,
                }}
                title={yOnly ? 'Arraste para ajustar altura da seta · duplo clique para resetar' : 'Arraste para mover · duplo clique para resetar'}
                onMouseDown={(e) => { e.stopPropagation(); }}
                onPointerDown={(e) => {
                  e.stopPropagation();
                  (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
                  kanbanIconDragRef.current = {
                    cardId: superCard.id,
                    offsetField: field,
                    yOnly: !!yOnly,
                    startClientX: e.clientX,
                    startClientY: e.clientY,
                    startOffsetX: ox,
                    startOffsetY: oy,
                  };
                }}
                onPointerMove={(e) => {
                  const drag = kanbanIconDragRef.current;
                  if (!drag || drag.cardId !== superCard.id || drag.offsetField !== field) return;
                  e.stopPropagation();
                  const es = Math.max(0.01, effectiveScale);
                  const newX = drag.yOnly ? 0 : Math.round(drag.startOffsetX + (e.clientX - drag.startClientX) / es);
                  const newY = Math.round(drag.startOffsetY + (e.clientY - drag.startClientY) / es);
                  moveKanbanIcon(superCard.id, field, newX, newY);
                }}
                onPointerUp={(e) => {
                  e.stopPropagation();
                  if ((e.currentTarget as HTMLElement).hasPointerCapture(e.pointerId)) {
                    (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
                  }
                  kanbanIconDragRef.current = null;
                }}
                onPointerCancel={() => { kanbanIconDragRef.current = null; }}
                onDoubleClick={(e) => {
                  e.stopPropagation();
                  moveKanbanIcon(superCard.id, field, 0, 0);
                }}
              />
            );
          });

          return [...iconHandleNodes, ...waypointHandleNodes];
        })}

        {/* Pull Signal — Drag handles para ícone de retirada kanban (vsmPullSignalIconOffset) */}
        {displayConnections
          .filter(conn => conn.connectionType === 'vsm_pull_signal' && conn.sourcePoint && conn.targetPoint)
          .map(conn => {
            const connId = conn.connectionId || (conn as any).id;
            const srcCard = activeCards.find(c =>
              (c.conexoes_saida ?? []).some((cx: any) => cx.id === connId)
            );
            if (!srcCard) return null;
            const rawPsConn = (srcCard.conexoes_saida ?? []).find((cx: any) => cx.id === connId) as any;
            const iconOffset = rawPsConn?.vsmPullSignalIconOffset ?? { x: 0, y: 0 };
            const ox = Number(iconOffset.x ?? 0);
            const oy = Number(iconOffset.y ?? 0);
            const defaultX = (conn.sourcePoint!.x + conn.targetPoint!.x) / 2;
            const defaultY = (conn.sourcePoint!.y + conn.targetPoint!.y) / 2;
            const cx = defaultX + ox;
            const cy = defaultY + oy;
            const HANDLE_SIZE = 28;
            const hasDrift = Math.hypot(ox, oy) > 4;
            const psEffectiveScale = scale * (contentScale / 12);
            return (
              <div
                key={`ps-icon-handle-${connId}`}
                style={{
                  position: 'absolute',
                  left: cx - HANDLE_SIZE / 2,
                  top: cy - HANDLE_SIZE / 2,
                  width: HANDLE_SIZE,
                  height: HANDLE_SIZE,
                  zIndex: 53,
                  pointerEvents: 'auto',
                  cursor: 'move',
                  touchAction: 'none',
                  opacity: hasDrift ? 0.4 : 0,
                  background: hasDrift ? 'rgba(37,99,235,0.15)' : 'transparent',
                  border: hasDrift ? '1px dashed #2563eb' : 'none',
                  borderRadius: 3,
                }}
                title="Arraste para mover ícone · duplo clique para resetar"
                onPointerDown={(e) => {
                  e.stopPropagation();
                  (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
                  (e.currentTarget as HTMLElement).style.opacity = '1';
                  pullSignalIconDragRef.current = {
                    srcCardId: srcCard.id,
                    connId,
                    startClientX: e.clientX,
                    startClientY: e.clientY,
                    startOffsetX: ox,
                    startOffsetY: oy,
                  };
                }}
                onPointerMove={(e) => {
                  const drag = pullSignalIconDragRef.current;
                  if (!drag || drag.connId !== connId) return;
                  e.stopPropagation();
                  const es = Math.max(0.01, psEffectiveScale);
                  const newX = Math.round(drag.startOffsetX + (e.clientX - drag.startClientX) / es);
                  const newY = Math.round(drag.startOffsetY + (e.clientY - drag.startClientY) / es);
                  movePullSignalIcon(drag.srcCardId, drag.connId, newX, newY);
                }}
                onPointerUp={(e) => {
                  e.stopPropagation();
                  if ((e.currentTarget as HTMLElement).hasPointerCapture(e.pointerId)) {
                    (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
                  }
                  (e.currentTarget as HTMLElement).style.opacity = hasDrift ? '0.4' : '0';
                  pullSignalIconDragRef.current = null;
                }}
                onPointerCancel={() => { pullSignalIconDragRef.current = null; }}
                onDoubleClick={(e) => {
                  e.stopPropagation();
                  movePullSignalIcon(srcCard.id, connId, 0, 0);
                }}
              />
            );
          })}

        {/* Transport Edge — Drag handle para deslocamento vertical do conjunto seta+caminhão+texto */}
        {canEditCanvas && displayConnections
          .filter(conn => conn.connectionType === 'vsm_transport' && conn.sourcePoint && conn.targetPoint)
          .map(conn => {
            const connId = conn.connectionId || (conn as any).id;
            const srcCard = activeCards.find(c =>
              (c.conexoes_saida ?? []).some((cx: any) => cx.id === connId)
            );
            if (!srcCard) return null;
            const rawTrConn = (srcCard.conexoes_saida ?? []).find((cx: any) => cx.id === connId) as any;
            const offsetY = Number(rawTrConn?.vsmTransportOffsetY ?? 0);
            const cx = (conn.sourcePoint!.x + conn.targetPoint!.x) / 2;
            const cy = (conn.sourcePoint!.y + conn.targetPoint!.y) / 2 + offsetY;
            const dx = conn.targetPoint!.x - conn.sourcePoint!.x;
            const dy = conn.targetPoint!.y - conn.sourcePoint!.y;
            // Handle dimensionado para coincidir com o corpo visível da seta (16px de largura).
            // Orientação alinhada ao eixo dominante para não invadir elementos adjacentes.
            // Limitado a uma região compacta no centro para não conflitar com waypoints sobre a linha.
            const ARROW_BODY = 18;
            const MAX_HANDLE_LEN = 60; // só a região do caminhão/label captura o drag vertical
            const isVerticalArrow = Math.abs(dy) >= Math.abs(dx);
            const HANDLE_W = isVerticalArrow ? ARROW_BODY : Math.min(MAX_HANDLE_LEN, Math.max(ARROW_BODY, Math.abs(dx)));
            const HANDLE_H = isVerticalArrow ? Math.min(MAX_HANDLE_LEN, Math.max(ARROW_BODY, Math.abs(dy))) : ARROW_BODY;
            const trEffectiveScale = scale * (contentScale / 12);
            return (
              <div
                key={`vsm-tr-y-handle-${connId}`}
                style={{
                  position: 'absolute',
                  left: cx - HANDLE_W / 2,
                  top: cy - HANDLE_H / 2,
                  width: HANDLE_W,
                  height: HANDLE_H,
                  zIndex: 53,
                  pointerEvents: 'auto',
                  cursor: 'ns-resize',
                  touchAction: 'none',
                  opacity: 0,
                  background: 'transparent',
                  border: 'none',
                  borderRadius: 3,
                }}
                title="Arraste verticalmente · duplo clique para resetar"
                onPointerDown={(e) => {
                  e.stopPropagation();
                  (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
                  transportEdgeYDragRef.current = {
                    srcCardId: srcCard.id,
                    connId,
                    startClientY: e.clientY,
                    startOffsetY: offsetY,
                    moved: false,
                  };
                }}
                onPointerMove={(e) => {
                  const drag = transportEdgeYDragRef.current;
                  if (!drag || drag.connId !== connId) return;
                  e.stopPropagation();
                  const es = Math.max(0.01, trEffectiveScale);
                  const newY = Math.round(drag.startOffsetY + (e.clientY - drag.startClientY) / es);
                  if (newY !== drag.startOffsetY) drag.moved = true;
                  moveVsmTransportEdgeY(drag.srcCardId, drag.connId, newY);
                }}
                onPointerUp={(e) => {
                  e.stopPropagation();
                  if ((e.currentTarget as HTMLElement).hasPointerCapture(e.pointerId)) {
                    (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
                  }
                  const drag = transportEdgeYDragRef.current;
                  // Se não houve movimento, propaga seleção da edge
                  if (drag && !drag.moved) {
                    const sourceCardId = drag.srcCardId;
                    setSelectedConnection({ sourceId: sourceCardId, connectionId: drag.connId });
                  }
                  transportEdgeYDragRef.current = null;
                }}
                onPointerCancel={() => { transportEdgeYDragRef.current = null; }}
                onDoubleClick={(e) => {
                  e.stopPropagation();
                  moveVsmTransportEdgeY(srcCard.id, connId, 0);
                }}
              />
            );
          })}

        {activeCards
          .filter(card => viewMode === 'kaizen_work' && isContinuousFlowProcess(card) && ((card as any).vsmKaizenProjection || simulation?.kaizenOverrides?.[card.id]))
          .map(card => {
            const { width } = getCanvasHitBox(card, isTimeScaleActive);
            const projection = (card as any).vsmKaizenProjection ?? {};
            const markerSize = KAIZEN_MARKER_SIZE;
            const markerOffsetX = Number(projection.markerOffsetX ?? width - markerSize + 6);
            const markerOffsetY = Number(projection.markerOffsetY ?? -16);
            const markerTitle = String(projection.title ?? projection.kaizenTitle ?? 'KAIZEN').trim() || 'KAIZEN';
            const markerLabel = markerTitle.length > 9 ? `${markerTitle.slice(0, 8)}…` : markerTitle;
            return (
              <div
                key={`v186-kaizen-${card.id}`}
                className="absolute flex items-center justify-center"
                style={{
                  left: card.posicao.x + markerOffsetX,
                  top: card.posicao.y + markerOffsetY,
                  width: markerSize,
                  height: markerSize,
                  // Kaizen markers devem ficar à frente de cards (≤10), SVGs de conexão (100) e linhas pontilhadas (180).
                  // 99998+ é reservado ao menu de contexto/modais — mantemos abaixo disso.
                  zIndex: 200,
                  pointerEvents: 'auto',
                  cursor: 'grab',
                  touchAction: 'none',
                  filter: 'drop-shadow(0 3px 7px rgba(0,0,0,0.42))',
                }}
                title={markerTitle}
                onPointerDown={(event) => {
                  event.stopPropagation();
                  (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
                  kaizenMarkerDragRef.current = {
                    cardId: card.id,
                    startClientX: event.clientX,
                    startClientY: event.clientY,
                    startOffsetX: markerOffsetX,
                    startOffsetY: markerOffsetY,
                    moved: false,
                  };
                }}
                onPointerMove={(event) => {
                  const drag = kaizenMarkerDragRef.current;
                  if (!drag || drag.cardId !== card.id) return;
                  event.stopPropagation();
                  const deltaX = (event.clientX - drag.startClientX) / Math.max(0.1, scale || 1);
                  const deltaY = (event.clientY - drag.startClientY) / Math.max(0.1, scale || 1);
                  if (Math.hypot(deltaX, deltaY) > 3) drag.moved = true;
                  moveKaizenMarker(card.id, drag.startOffsetX + deltaX, drag.startOffsetY + deltaY);
                }}
                onPointerUp={(event) => {
                  const drag = kaizenMarkerDragRef.current;
                  event.stopPropagation();
                  if ((event.currentTarget as HTMLElement).hasPointerCapture(event.pointerId)) {
                    (event.currentTarget as HTMLElement).releasePointerCapture(event.pointerId);
                  }
                  kaizenMarkerDragRef.current = null;
                  if (!drag?.moved) openKaizenMarkerProperties(card, event);
                }}
                onPointerCancel={(event) => {
                  event.stopPropagation();
                  kaizenMarkerDragRef.current = null;
                }}
              >
                <svg width={markerSize} height={markerSize} viewBox="0 0 60 60" fill="none">
                  <path d="M30 3.6L36.2 20.4L52.8 12.8L45.2 30L56.4 43.2L38.6 43.4L31.6 56.4L22.4 43.6L4.8 49.6L13.6 33.6L3.6 20.8L21 19.4Z" fill="rgba(254,240,138,0.58)" stroke="#b45309" strokeWidth="2.35" strokeLinejoin="round" />
                  <text x="30" y="33" textAnchor="middle" dominantBaseline="middle" fontSize={markerLabel.length > 6 ? 7.6 : 9} fontWeight={900} fontFamily="'Inter','Roboto Condensed',system-ui,sans-serif" fill="#78350f" fillOpacity={1}>{markerLabel}</text>
                </svg>
              </div>
            );
          })}
        {/* Indicador visual de drop: Card Fantasma mostrando onde sera inserido */}
        {dropTargetCardId && (() => {
          const targetCard = activeCards.find(c => c.id === dropTargetCardId);
          if (!targetCard) return null;

          const isDecision = targetCard.tipo === 'decisao';
          const isInitialFinal = targetCard.tipo === 'card_inicial' || targetCard.tipo === 'card_final';
          const targetDims = getCardDimensions(targetCard as any, isDecision, false);
          const targetVisualWidth = targetDims.width;
          const targetVisualHeight = targetDims.height;
          const isBefore = sidebarDropSide === 'before' && !isDecision;

          const ghostIsDecision = draggingCardType === 'decisao';
          // Derive vsmIcon key from draggingCardType (e.g. 'vsm_processo' -> 'processo')
          const ghostVsmKey = draggingCardType?.startsWith('vsm_') ? draggingCardType.slice(4) : null;
          const ghostMockData = { vsmIcon: ghostVsmKey || undefined } as any;
          const ghostDims = getCardDimensions(ghostMockData, ghostIsDecision, false);
          const ghostWidth = ghostIsDecision ? ghostDims.decisionSize : ghostDims.width;
          const ghostHeight = ghostIsDecision ? ghostDims.decisionSize : ghostDims.height;

          const HORIZONTAL_GAP = 80;
          // CARD_GAP = 180 (mesma constante do MapaPage � offset vertical do caminho NAO)
          const CARD_GAP = 180;

          let ghostX: number, ghostY: number;
          let lineStartX: number, lineStartY: number;
          let lineEndX: number, lineEndY: number;

          const isFactory = currentLayout === 'classic';
          const ghostIsProcesso = ghostVsmKey === 'processo' || draggingCardType === 'processo';
          const validPokaYoke = isFactory && targetCard.vsmIcon === 'inventory' && ghostIsProcesso;

          const targetVisualYBase = targetCard.posicao.y;
          const targetVisualLeft = targetCard.posicao.x;
          // ghostAnchorY: for processo (has databox), anchor at icon center ~55px; else vertical center
          const ghostAnchorY = ghostIsProcesso ? 55 : ghostHeight / 2;

          // Collision check using real card dimensions
          const checkGhostOccupied = (x: number, y: number): boolean => {
            const margin = 20;
            return activeCards.some(card => {
              if (card.id === targetCard.id) return false;
              const cdims = getCardDimensions(card as any, card.tipo === 'decisao', false);
              const cw = card.tipo === 'decisao' ? cdims.decisionSize : cdims.width;
              const ch = card.tipo === 'decisao' ? cdims.decisionSize : cdims.height;
              return !(x + ghostWidth + margin < card.posicao.x ||
                       x - margin > card.posicao.x + cw ||
                       y + ghostHeight + margin < card.posicao.y ||
                       y - margin > card.posicao.y + ch);
            });
          };

          // isNaoPath: saida NO de uma decisao (saida inferior/paralela)
          const isNaoPath = isDecision && dropPosition === 'no';



          if (validPokaYoke) {
            ghostX = targetVisualLeft + targetVisualWidth + HORIZONTAL_GAP;
            ghostY = targetVisualYBase + targetVisualHeight / 2 - ghostAnchorY;
            lineStartX = targetVisualLeft + targetVisualWidth;
            lineStartY = targetVisualYBase + targetVisualHeight / 2;
            lineEndX = ghostX;
            lineEndY = ghostY + ghostAnchorY;
          } else if (isBefore) {
            // MODO ANTES: fantasma na posicao atual do target; target desloca para a direita
            ghostX = targetCard.posicao.x;
            ghostY = targetCard.posicao.y + (targetVisualHeight - ghostHeight) / 2;
            lineStartX = ghostX + ghostWidth;
            lineStartY = ghostY + ghostAnchorY;
            lineEndX = targetCard.posicao.x + targetVisualWidth + HORIZONTAL_GAP;
            lineEndY = targetCard.posicao.y + targetVisualHeight / 2;
          } else if (isNaoPath) {
            // Saida NAO (decisao): direita + abaixo  igual ao MapaPage
            // ghostY: decision.y + decisionHeight + CARD_GAP (= decision.y + 340)
            ghostX = targetCard.posicao.x + targetVisualWidth + HORIZONTAL_GAP;
            ghostY = targetCard.posicao.y + targetVisualHeight + CARD_GAP;
            // Seta: sai do fundo do losango, desce, depois vai para a direita
            lineStartX = targetCard.posicao.x + targetVisualWidth / 2;
            lineStartY = targetCard.posicao.y + targetVisualHeight + 10; // +10 ponta do losango
            lineEndX = ghostX;
            lineEndY = ghostY + ghostAnchorY;
          } else {
            // PADRAO (SIM de decisao ou atividade a direita):
            // Calcular posicao inicial a direita, depois aplicar colisao identica ao MapaPage
            // Deciso renderiza com offset +10 no top (Card.tsx), compensar aqui
            const targetVisualY = targetCard.posicao.y + (isDecision ? 10 : 0);
            let rawGhostX = targetCard.posicao.x + targetVisualWidth + HORIZONTAL_GAP;
            let rawGhostY = targetVisualY + (targetVisualHeight - ghostHeight) / 2;

            // Colisao: deslocar para baixo ate encontrar espaco livre (igual ao MapaPage)
            let attempts = 0;
            while (checkGhostOccupied(rawGhostX, rawGhostY) && attempts < 20) {
              rawGhostY += ghostHeight + CARD_GAP;
              attempts++;
            }

            ghostX = rawGhostX;
            ghostY = rawGhostY;
            lineStartX = targetCard.posicao.x + targetVisualWidth;
            lineStartY = targetVisualY + targetVisualHeight / 2;
            lineEndX = ghostX;
            lineEndY = ghostY + ghostAnchorY;
          }

          // Cor: Verde/Cyan/Amber
          const ghostColor = validPokaYoke ? '#4ade80' : (isFactory ? '#06b6d4' : ((isBefore || isNaoPath) ? '#f59e0b' : '#22c55e'));
          const ghostOpacity = validPokaYoke ? 0.8 : 0.6;
          const ghostStrokeWidth = validPokaYoke ? 1 : 1.5;
          const ghostBg = isFactory ? 'transparent' : ((isBefore || isNaoPath) ? 'rgba(245,158,11,0.08)' : 'rgba(34,197,94,0.08)');

          // Caminho da seta:
          // - isBefore: direita do ghost ? target (leve diferenca vertical possivel)
          // - isNaoPath: desce do losango depois vai para a direita
          // - padrao: se ghost deslocou para baixo (dy>10), vai direita-depois-desce; senao linha reta
          const arrowPath = (() => {
            const dy = lineEndY - lineStartY;
            if (isBefore) {
              // ghost esta a esquerda do target � pode ter leve diferenca vertical
              const dx = lineEndX - lineStartX;
              if (Math.abs(dy) <= 10) return `M ${lineStartX} ${lineStartY} L ${lineEndX} ${lineEndY}`;
              const midX = lineStartX + dx / 2;
              return `M ${lineStartX} ${lineStartY} L ${midX} ${lineStartY} L ${midX} ${lineEndY} L ${lineEndX} ${lineEndY}`;
            }
            if (isNaoPath) {
              // Desce primeiro do losango, depois vai para a direita ate o ghost
              return `M ${lineStartX} ${lineStartY} L ${lineStartX} ${lineEndY} L ${lineEndX} ${lineEndY}`;
            }
            // Padrao: se ghost esta na mesma altura ? linha reta
            if (Math.abs(dy) <= 10) return `M ${lineStartX} ${lineStartY} L ${lineEndX} ${lineEndY}`;
            // Ghost deslocado para baixo por colisao: mid-X elbow (igual ao useEdgeCalculations)
            // Sai direita do target ? desce no ponto medio ? vai direita ate entrar no ghost pela esquerda
            const midX = (lineStartX + lineEndX) / 2;
            return `M ${lineStartX} ${lineStartY} L ${midX} ${lineStartY} L ${midX} ${lineEndY} L ${lineEndX} ${lineEndY}`;
          })();

          return (
            <>
              {/* Linha de conexao tracejada */}
              <svg className="absolute pointer-events-none overflow-visible"
                style={{ zIndex: 999, left: 0, top: 0, width: '100%', height: '100%' }}>
                <defs>
                  <marker id="ghost-arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill={ghostColor} />
                  </marker>
                </defs>
                <path d={arrowPath}
                  fill="none" stroke={ghostColor} strokeWidth={1.5} opacity={ghostOpacity}
                  markerEnd="url(#ghost-arrowhead)" />
              </svg>

              {/* Card Fantasma na posicao de destino */}
              <div className="absolute pointer-events-none ghost-shape-override"
                style={{ left: ghostX, top: ghostY, width: ghostWidth, height: ghostHeight, zIndex: 1000, opacity: 0.72 }}>
                {ghostIsDecision ? (
                  <svg width={ghostWidth} height={ghostHeight} style={{ position: 'absolute', left: 0, top: 0, overflow: 'visible' }}>
                    <polygon
                      points={`${ghostWidth/2},0 ${ghostWidth},${ghostHeight/2} ${ghostWidth/2},${ghostHeight} 0,${ghostHeight/2}`}
                      fill={ghostBg}
                      stroke={ghostColor}
                      strokeWidth="2"
                      strokeDasharray="6 3"
                    />
                  </svg>
                ) : draggingCardType === 'card_inicial' || draggingCardType === 'card_final' ? (
                  <div className="absolute inset-0 flex items-center justify-center"
                    style={{
                      border: `${ghostStrokeWidth}px dashed ${ghostColor}`,
                      backgroundColor: ghostBg,
                      borderRadius: 9999,
                    }}>
                    <div className="text-[11px] font-semibold" style={{ color: ghostColor }}>
                      {isBefore ? '\u2190 Inserir antes' : draggingCardType === 'card_inicial' ? 'Início' : 'Fim'}
                    </div>
                  </div>
                ) : ghostVsmKey && isFactory ? (
                  // Factory VSM icon ghost: shape-accurate outline via drop-shadow on the SVG alpha
                  <img
                    src={`/assets/vsm/${ghostVsmKey}.svg`}
                    alt={ghostVsmKey}
                    style={{
                      position: 'absolute',
                      left: 0, top: 0,
                      width: ghostWidth,
                      height: ghostHeight,
                      objectFit: 'contain',
                      pointerEvents: 'none',
                      opacity: 0.82,
                      // Layered drop-shadows: hard outline (0px blur) + soft glow
                      filter: [
                        `drop-shadow(0 0 0 1.5px ${ghostColor})`,
                        `drop-shadow(0 0 0 3px ${ghostColor}55)`,
                        `drop-shadow(0 0 8px ${ghostColor}77)`,
                      ].join(' '),
                    }}
                  />
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-1"
                    style={{
                      border: `${ghostStrokeWidth}px dashed ${ghostColor}`,
                      backgroundColor: ghostBg,
                      borderRadius: 12,
                    }}>
                    <div className="text-[11px] font-semibold" style={{ color: ghostColor }}>
                      {isBefore ? '\u2190 Inserir antes' : 'Nova Etapa'}
                    </div>
                  </div>
                )}
              </div>



              {/* Borda de destaque no card alvo */}
              {isDecision ? (
                // SVG polygon traces exact diamond � Card.tsx: decisao top = pos.y + 10
                <svg className="pointer-events-none absolute overflow-visible"
                  style={{ left: targetCard.posicao.x, top: targetCard.posicao.y + 10, width: 160, height: 160, zIndex: 998 }}>
                  <polygon
                    points="80,0 160,80 80,160 0,80"
                    fill={`${ghostColor}10`}
                    stroke={ghostColor}
                    strokeWidth="2.5"
                    style={{ filter: `drop-shadow(0 0 8px ${ghostColor}80)` }}
                  />
                </svg>
              ) : (
                <div className="pointer-events-none absolute"
                  style={{
                    left: targetCard.posicao.x - 3,
                    top: targetCard.posicao.y - 3,
                    width: targetVisualWidth + 6,
                    height: targetVisualHeight + 6,
                    // card uses rounded-xl (12px); outer highlight expands 3px ? 12 + 3 = 15px
                    borderRadius: isInitialFinal ? 9999 : 15,
                    border: `2.5px solid ${ghostColor}`,
                    backgroundColor: `${ghostColor}10`,
                    filter: `drop-shadow(0 0 8px ${ghostColor}80)`,
                    zIndex: 998,
                    transition: 'all 0.15s ease-out',
                  }} />
              )}

              {/* Label SIM/NAO para decisoes */}
              {isDecision && (
                <div className="absolute pointer-events-none"
                  style={{
                    left: dropPosition === 'yes' ? targetCard.posicao.x + targetVisualWidth + 4 : targetCard.posicao.x + targetVisualWidth / 2 - 20,
                    top: dropPosition === 'yes' ? targetCard.posicao.y + targetVisualHeight / 2 - 10 : targetCard.posicao.y + targetVisualHeight + 14,
                    zIndex: 1001,
                  }}>
                  <span className={`px-2 py-0.5 rounded text-xs font-bold ${dropPosition === 'yes' ? 'bg-green-500 text-white' : 'bg-amber-500 text-white'}`}>
                    {dropPosition === 'yes' ? 'SIM' : 'N\u00c3O'}
                  </span>
                </div>
              )}
            </>
          );
        })()}

        {/* ��Ļ HOVER PREVIEW: Card Fantasma mostrando onde ser+� inserido ao clicar no menu */}
        {hoverPreview && !dropTargetCardId && (() => {
          const sourceCard = activeCards.find(c => c.id === hoverPreview.sourceId);
          if (!sourceCard) return null;
          
          const sourceCardWidth = sourceCard.tipo === 'decisao' ? 160 : 240;
          const sourceCardHeight = sourceCard.tipo === 'decisao' ? 160 : 180;
          const isSourceDecision = sourceCard.tipo === 'decisao';
          
          // Dimens+�es do card fantasma baseado no tipo
          const ghostIsDecision = hoverPreview.type === 'decisao';
          const ghostWidth = ghostIsDecision ? 160 : 240;
          const ghostHeight = ghostIsDecision ? 160 : 180;
          
          // Espa�amento entre cards
          const HORIZONTAL_GAP = 80;
          
          // Posi��o do card fantasma (sempre � direita, "yes path")
          // N�o verificar ocupa��o � o ghost sempre mostra a posi��o alvo no fluxo
          // Decis�o renderiza com offset +10 no top (Card.tsx), compensar aqui
          const sourceVisualY = sourceCard.posicao.y + (isSourceDecision ? 10 : 0);
          const ghostX = sourceCard.posicao.x + sourceCardWidth + HORIZONTAL_GAP;
          const ghostY = sourceVisualY + (sourceCardHeight - ghostHeight) / 2;

          // Linha de conex�o - partir do centro visual do source
          const lineStartX = sourceCard.posicao.x + sourceCardWidth;
          const lineStartY = sourceVisualY + sourceCardHeight / 2;
          const lineEndX = ghostX;
          const lineEndY = ghostY + ghostHeight / 2;
          
          // Determinar se a linha precisa ser ortogonal (quando Y +� diferente)
          const needsOrthogonalPath = Math.abs(lineStartY - lineEndY) > 10;
          
          return (
            <>
              {/* Linha de conex+�o tracejada */}
              <svg 
                className="absolute pointer-events-none overflow-visible"
                style={{ zIndex: 999, left: 0, top: 0, width: '100%', height: '100%' }}
              >
                <defs>
                  <marker
                    id="hover-ghost-arrowhead"
                    markerWidth="10"
                    markerHeight="7"
                    refX="9"
                    refY="3.5"
                    orient="auto"
                  >
                    <polygon
                      points="0 0, 10 3.5, 0 7"
                      fill="#3b82f6"
                    />
                  </marker>
                </defs>
                {!needsOrthogonalPath ? (
                  <path
                    d={`M ${lineStartX} ${lineStartY} L ${lineEndX} ${lineEndY}`}
                    fill="none"
                    stroke="#3b82f6"
                    strokeWidth={2.5}
                    strokeDasharray="8,4"
                    markerEnd="url(#hover-ghost-arrowhead)"
                  />
                ) : (
                  <path
                    d={`M ${lineStartX} ${lineStartY} L ${lineStartX + (lineEndX - lineStartX) / 2} ${lineStartY} L ${lineStartX + (lineEndX - lineStartX) / 2} ${lineEndY} L ${lineEndX} ${lineEndY}`}
                    fill="none"
                    stroke="#3b82f6"
                    strokeWidth={2.5}
                    strokeDasharray="8,4"
                    markerEnd="url(#hover-ghost-arrowhead)"
                  />
                )}
              </svg>
              
              {/* Card Fantasma */}
              <div
                className="absolute pointer-events-none transition-all duration-150"
                style={{
                  left: ghostX,
                  top: ghostY,
                  width: ghostWidth,
                  height: ghostHeight,
                  zIndex: 1000,
                  opacity: 0.7,
                }}
              >
                {ghostIsDecision ? (
                  /* Fantasma de Decis+�o (losango) */
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div
                      className="border-3 border-dashed rounded-sm"
                      style={{
                        width: 113,
                        height: 113,
                        borderColor: '#3b82f6',
                        backgroundColor: 'rgba(59, 130, 246, 0.2)',
                        transform: 'rotate(45deg)',
                      }}
                    />
                  </div>
                ) : (
                  /* Fantasma de Atividade (ret+�ngulo) */
                  <div
                    className="absolute inset-0 rounded-xl border-3 border-dashed flex items-center justify-center"
                    style={{
                      borderColor: '#3b82f6',
                      backgroundColor: 'rgba(59, 130, 246, 0.15)',
                    }}
                  />
                )}
              </div>
            </>
          );
        })()}

        {/* Ghost post-it que segue o cursor durante modo cria��o (cursor:none esconde o cursor do sistema) */}
        {isCreatingPostIt && cursorCanvasPos && (() => {
          const postItColors: Record<string, { bg: string; border: string; label: string }> = {
            idea:    { bg: '#fef3c7', border: '#d97706', label: 'Ideia' },
            problem: { bg: '#fecaca', border: '#dc2626', label: 'Problema' },
            action:  { bg: '#bbf7d0', border: '#16a34a', label: 'Ação' },
            data:    { bg: '#dbeafe', border: '#2563eb', label: 'Dado' },
            risk:    { bg: '#fed7aa', border: '#ea580c', label: 'Risco' },
          };
          const t = creatingPostItType || 'idea';
          const c = postItColors[t] || postItColors.idea;
          const W = 68 / scale;
          const H = 60 / scale;
          const fold = 13 / scale;
          return (
            <div
              style={{
                position: 'absolute',
                left: cursorCanvasPos.x + 6 / scale,
                top: cursorCanvasPos.y + 4 / scale,
                width: W,
                height: H,
                backgroundColor: c.bg,
                border: `${1.5 / scale}px solid ${c.border}`,
                borderRadius: `${5 / scale}px`,
                boxShadow: `0 ${6/scale}px ${16/scale}px rgba(0,0,0,0.22), 0 ${2/scale}px ${4/scale}px rgba(0,0,0,0.12)`,
                pointerEvents: 'none',
                zIndex: 99999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: `${9 / scale}px`,
                fontWeight: 700,
                color: c.border,
                letterSpacing: '0.02em',
                transform: 'rotate(-3deg)',
                opacity: 0.92,
              }}
            >
              {c.label}
              {/* Orelha dobrada */}
              <div style={{
                position: 'absolute',
                top: 0, right: 0,
                width: fold, height: fold,
                background: `linear-gradient(225deg, rgba(0,0,0,0.12) 50%, transparent 50%)`,
                borderBottomLeftRadius: `${3/scale}px`,
              }} />
              {/* Linhas de texto decorativas */}
              <div style={{ position:'absolute', bottom: `${12/scale}px`, left: `${7/scale}px`, right: `${7/scale}px`, height: `${1.5/scale}px`, backgroundColor: c.border, opacity: 0.3, borderRadius: 1 }} />
              <div style={{ position:'absolute', bottom: `${7/scale}px`, left: `${7/scale}px`, right: `${14/scale}px`, height: `${1.5/scale}px`, backgroundColor: c.border, opacity: 0.3, borderRadius: 1 }} />
            </div>
          );
        })()}

        {executiveMode && executiveStageSummaries.length > 0 && (
          <div className="pointer-events-none absolute inset-0 z-[16]">
            {executiveStageSummaries.map((summary) => {
              const card = activeCards.find((item) => item.id === summary.cardId);
              if (!card) return null;

              const cardWidth = card.tipo === 'decisao' ? 160 : (card.calculatedWidth || 240);
              const cardHeight = card.tipo === 'decisao' ? 160 : 180;
              const baseX = card.posicao.x;
              const baseY = card.posicao.y;
              const isCritical = executiveBottleneckId === summary.cardId || summary.isBottleneck;
              const hasPending = summary.pendingActions > 0;

              return (
                <React.Fragment key={`executive-overlay-${summary.cardId}`}>
                  {isCritical && (
                    <div
                      className="absolute"
                      style={{
                        left: baseX - 8,
                        top: baseY - 10,
                        width: 2,
                        height: cardHeight + 20,
                        background: 'linear-gradient(180deg, rgba(239,68,68,0.75), rgba(239,68,68,0.12))',
                        boxShadow: '0 0 10px rgba(239,68,68,0.24)',
                        borderRadius: 2,
                      }}
                    />
                  )}
                  <div
                    className="absolute border border-slate-300/90 bg-white/92 px-2 py-1 text-[10px] shadow-sm backdrop-blur-sm dark:border-slate-700 dark:bg-slate-950/88"
                    style={{
                      left: baseX + 6,
                      top: baseY - 24,
                      minWidth: Math.max(120, cardWidth - 12),
                    }}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate font-semibold uppercase tracking-[0.12em] text-slate-600 dark:text-slate-300">
                        {summary.label}
                      </span>
                      {isCritical && <span className="font-semibold text-red-700 dark:text-red-300">Gargalo</span>}
                    </div>
                    <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 font-mono text-slate-700 dark:text-slate-200">
                      <span>LT acum {summary.cumulativeLeadTime.toFixed(0)}m</span>
                      <span>TP medio {summary.leadTime.toFixed(0)}m</span>
                      <span>VA {summary.valueAddedPercent.toFixed(0)}%</span>
                      <span>{hasPending ? `${summary.pendingActions} acoes` : 'Fluxo livre'}</span>
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        )}

        {/* Renderizar Post-Its com filtro baseado no viewMode */}
        {postIts.filter(p => {
          if (executiveMode) return false;
          if (p.isMinimized || p.fixedToNodeId) return false;
          
          const currentViewMode = useProjectStore.getState().viewMode;
          
          // MODO FUTURO: Ocultar post-its de problema, manter ideias
          if (currentViewMode === 'future_simulated') {
            return p.type === 'idea' || p.type === 'data';
          }
          
          // MODO ATUAL e MODO KAIZEN: Mostrar todos
          return true;
        }).map((p) => (
          <PostItCard
            key={p.id} data={p} scale={scale} isBrainstormMode={brainstormMode}
            isActive={draggingPostIt?.id === p.id} isSnapping={draggingPostIt?.id === p.id && postItSnapTargetId !== null}
            isKaizenMode={isKaizenMode}
            parentCard={null} forceEdit={editingPostItId === p.id}
            onStartDrag={handlePostItStartDrag} onDrag={handlePostItDrag} onEndDrag={handlePostItEndDrag}
            onChangeText={handlePostItTextChange} onToggleMinimize={handleTogglePostItMinimize}
            onClick={(id, e) => { setEditingPostItId(id); handlePostItClick(id, e); }}
            onOpenImpactPanel={onOpenImpactPanel}
            selected={selectedPostItIds.has(p.id)} onContextMenu={(id, x, y) => {
              if (openContextMenu) {
                openContextMenu(id, x, y);
              }
            }}
          />
        ))}
        
        {/* Conectores de P+�gina */}
        {pageConnectors.map((connector) => (
          <PageConnectorNode
            key={connector.id}
            connector={connector}
            selected={false}
            onUpdatePosition={onUpdateConnectorPosition}
            onUpdateLabel={onUpdateConnectorLabel}
            onDelete={onDeleteConnector}
            scale={scale * scaleFactor}
            pan={pan}
          />
        ))}

        {/* Menu de Contexto do Canvas/Edges - Usando Portal para renderizar fora do canvas */}
        {contextMenu && ReactDOM.createPortal(
          <>
            {/* Overlay transparente para fechar menu ao clicar fora */}
            <div 
              className="fixed inset-0" 
              style={{ zIndex: 99998 }}
              onClick={() => setContextMenu(null)}
              onContextMenu={(e) => { e.preventDefault(); setContextMenu(null); }}
            />
            <div
              className="bg-white dark:bg-slate-900 rounded-lg shadow-xl border border-gray-200 dark:border-slate-700 py-2 text-slate-800 dark:text-slate-200"
              style={(() => {
                // Poka-Yoke: inverte a direção do menu quando o clique está próximo da borda inferior/direita.
                // Estimativa conservadora das dimensões do menu (cobre canvas e edge cases).
                const MENU_W = 240;
                const MENU_H = 360;
                const PAD = 8;
                const vw = typeof window !== 'undefined' ? window.innerWidth : 1920;
                const vh = typeof window !== 'undefined' ? window.innerHeight : 1080;
                const left = contextMenu.x + MENU_W + PAD > vw ? Math.max(PAD, contextMenu.x - MENU_W) : contextMenu.x;
                const top  = contextMenu.y + MENU_H + PAD > vh ? Math.max(PAD, contextMenu.y - MENU_H) : contextMenu.y;
                return {
                  position: 'fixed' as const,
                  left,
                  top,
                  minWidth: '200px',
                  zIndex: 99999,
                };
              })()}
            onClick={(e) => e.stopPropagation()}
            onContextMenu={(e) => e.preventDefault()}
          >
            {contextMenu.type === 'edge' && selectedConnection && (() => {
              const srcCard = activeCards.find(c => c.id === selectedConnection.sourceId);
              const rawConn = srcCard?.conexoes_saida?.find(c => c.id === selectedConnection.connectionId);
              const targetCard = rawConn ? activeCards.find(c => c.id === rawConn.targetId) : undefined;
              const processedConn = processedConnections.find(conn => conn.connectionId === selectedConnection.connectionId);
              if (!srcCard || !rawConn) return null;

              if (!canEditCanvas) {
                return (
                  <div className="w-[220px] rounded-lg bg-white dark:bg-slate-900 p-3 text-slate-800 dark:text-slate-200">
                    <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400 dark:text-slate-500">Estado Atual</div>
                    <div className="mt-1 text-xs font-semibold text-slate-700 dark:text-slate-300">Mapa bloqueado para edição</div>
                    <button className="mt-3 w-full rounded border border-slate-200 dark:border-slate-700 px-2 py-1.5 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800" onClick={() => setContextMenu(null)}>
                      Fechar
                    </button>
                  </div>
                );
              }

              const updateConnection = (patch: Partial<typeof rawConn>) => {
                const idx = srcCard.conexoes_saida.findIndex(c => c.id === rawConn.id);
                if (idx === -1) return;
                const updated = [...srcCard.conexoes_saida];
                updated[idx] = { ...updated[idx], ...patch };
                handleUpdateCardData(srcCard.id, { conexoes_saida: updated });
              };

              const menuSection = (title: string, children: React.ReactNode) => (
                <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-700 last:border-b-0">
                  <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400 dark:text-slate-500">{title}</div>
                  {children}
                </div>
              );

              const typeItems: Array<{ type: NonNullable<typeof rawConn.connectionType>; label: string; icon: React.ReactNode }> = [
                { type: 'push', label: 'Push', icon: <ArrowRight size={15} /> },
                { type: 'vsm_info', label: 'Electronic', icon: <Radio size={15} /> },
                { type: 'vsm_fifo', label: 'FIFO', icon: <Layers size={15} /> },
                { type: 'vsm_transport', label: 'Transport', icon: <Truck size={15} /> },
                { type: 'vsm_pull_signal', label: 'Sinal Puxada', icon: <Package size={15} /> },
              ];
              const curType = rawConn.connectionType ?? 'material';
              const isTransportType = curType === 'vsm_transport' || curType === 'vsm_transport_delivery';
              const isInfoType = curType === 'vsm_info';
              const isKanbanType = isInfoType && Boolean(rawConn.kanbanLoopEnabled);
              const canBecomeKanban = !!targetCard && srcCard.posicao.x > targetCard.posicao.x;
              const isFifoType = curType === 'vsm_fifo';

              // V159.2: resolução para o menu de contexto — targetId como chave estável
              const ctxBoundComunicacao = isInfoType
                ? activeCards.find((c: any) =>
                    c.vsmIcon === 'comunicacao' && (
                      c.vsmBoundConnection?.connectionId === selectedConnection.connectionId ||
                      c.vsmBoundConnection?.targetId === rawConn.targetId
                    )
                  )
                : undefined;
              const curTransmission: 'Manual' | 'Eletrônico' =
                (ctxBoundComunicacao as any)?.vsmInfoTransmissionMethod ??
                rawConn.vsmInfoTransmission ??
                'Eletrônico';
              const supermarketOptions = activeCards.filter((card) => card.tipo === 'inventory' && (card as any).vsmIcon === 'supermercado');
              const pullBufferOptions = activeCards.filter((card) => card.tipo === 'inventory' && ['supermercado', 'pulmao'].includes((card as any).vsmIcon ?? ''));

              const updateKanbanLoopLeadTime = (leadTimeDays: number) => {
                const normalizedLeadTime = Math.max(0, Number(leadTimeDays) || 0);
                updateConnection({ kanbanLoopLeadTimeDays: normalizedLeadTime });
                const linkedBufferId = rawConn.kanbanSupermarketId;
                if (linkedBufferId) {
                  handleUpdateCardData(linkedBufferId, {
                    vsmFixedLeadTimeDays: normalizedLeadTime,
                    vsmBufferDays: normalizedLeadTime,
                    inventoryMode: 'coverage',
                  } as any);
                }
              };

              const updateTransmission = (method: 'Manual' | 'Eletrônico') => {
                // Atualiza rawConn
                updateConnection({ vsmInfoTransmission: method });
                // Atualiza card comunicacao bound se existir
                if (ctxBoundComunicacao) {
                  handleUpdateCardData((ctxBoundComunicacao as any).id, { vsmInfoTransmissionMethod: method } as any);
                }
                setContextMenu(null);
              };
              const dx = processedConn ? Math.abs(processedConn.targetPoint.x - processedConn.sourcePoint.x) : 0;
              const dy = processedConn ? Math.abs(processedConn.targetPoint.y - processedConn.sourcePoint.y) : 0;
              const suggestedJumpType: 'horizontal' | 'vertical' = dx >= dy ? 'vertical' : 'horizontal';
              const suggestedJumpLabel = suggestedJumpType === 'vertical' ? 'Jump Vertical' : 'Jump Horizontal';
              const suggestedJumpIcon = suggestedJumpType === 'vertical' ? <MoveVertical size={14} /> : <MoveHorizontal size={14} />;

              const addJump = (type: 'horizontal' | 'vertical') => {
                if (!canvasRef.current) return;
                const rect = canvasRef.current.getBoundingClientRect();
                const effectiveScale = scale * (contentScale / 12);
                const x = (contextMenu.x - rect.left - pan.x) / effectiveScale;
                const y = (contextMenu.y - rect.top - pan.y) / effectiveScale;
                updateConnection({ manualJumps: [...(rawConn.manualJumps || []), { x, y, type }] });
                setContextMenu(null);
              };

              const makeRadial = () => {
                updateConnection({ waypoints: undefined, manualJumps: undefined, mergeToConnection: undefined, targetHandle: undefined, forceRadial: true });
                setEditingWaypoints(false);
                setContextMenu(null);
              };

              const anchorInfoBox = () => {
                if (!canvasRef.current) return;
                const rect = canvasRef.current.getBoundingClientRect();
                const effectiveScale = scale * (contentScale / 12);
                const fallbackX = (contextMenu.x - rect.left - pan.x) / effectiveScale;
                const fallbackY = (contextMenu.y - rect.top - pan.y) / effectiveScale;
                const x = processedConn ? (processedConn.sourcePoint.x + processedConn.targetPoint.x) / 2 : fallbackX;
                const y = processedConn ? (processedConn.sourcePoint.y + processedConn.targetPoint.y) / 2 : fallbackY;
                const infoBox = {
                  id: `vsm-info-box-${rawConn.id}-${Date.now()}`,
                  tipo: 'atividade' as const,
                  vsmIcon: 'comunicacao',
                  atividade: rawConn.vsmInfoTitle || 'Caixa de Dados',
                  status: 'ativo' as const,
                  posicao: { x: Math.round(x - 59), y: Math.round(y - 20) },
                  vsmBoundConnection: { sourceId: srcCard.id, targetId: rawConn.targetId, connectionId: rawConn.id },
                  conexoes_saida: [],
                  p: 100,
                  tp: 0,
                  lt: 0,
                  tt: 0,
                  layoutType: 'classic' as const,
                };
                setCards([...activeCards, infoBox as any]);
                setContextMenu(null);
              };

              return (
                <div className="w-[256px] overflow-visible rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200">
                  {menuSection('Tipo', (
                    <div className="flex items-center gap-2">
                      {typeItems.map(({ type, label, icon }) => (
                        <button
                          key={type}
                          className={`group relative flex h-9 w-9 items-center justify-center rounded border text-xs font-bold transition ${curType === type ? 'border-cyan-500 bg-cyan-50 text-cyan-700 shadow-sm dark:border-cyan-400 dark:bg-cyan-500/15 dark:text-cyan-200' : 'border-slate-200 bg-white text-slate-600 hover:border-cyan-300 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:border-cyan-500/40 dark:hover:bg-slate-800/60'}`}
                          onClick={() => {
                            if (type === 'vsm_fifo') {
                              convertConnectionToFifoAtomically(srcCard.id, rawConn.id, { connectionType: 'vsm_fifo' });
                              return;
                            }
                            if (type === 'vsm_pull_signal') {
                              updateConnection({ connectionType: 'vsm_pull_signal' });
                              // Registrar nó alvo como Processo Puxador Master do sistema
                              const tgtId = rawConn.targetId;
                              if (tgtId) {
                                handleUpdateCardData(tgtId, { vsmPuxadorMaster: true } as any);
                              }
                              setContextMenu(null);
                              return;
                            }
                            updateConnection({ connectionType: type, ...(type === 'vsm_transport' && !rawConn.vsmTransportIcon ? { vsmTransportIcon: 'truck' as const } : {}) });
                          }}
                          aria-label={label}
                        >
                          {icon}
                          <span className="pointer-events-none absolute -top-8 left-1/2 z-[100000] hidden -translate-x-1/2 whitespace-nowrap rounded bg-slate-900 px-2 py-1 text-[10px] font-semibold text-white shadow group-hover:block">{label}</span>
                        </button>
                      ))}
                      <button
                        className={`group relative flex h-9 w-9 items-center justify-center rounded border text-xs font-bold transition ${isKanbanType ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-sm dark:border-blue-400 dark:bg-blue-500/15 dark:text-blue-200' : canBecomeKanban ? 'border-slate-200 bg-white text-slate-600 hover:border-blue-300 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:border-blue-500/40 dark:hover:bg-slate-800/60' : 'cursor-not-allowed border-slate-200 bg-slate-50 text-slate-300 dark:border-slate-800 dark:bg-slate-900/50 dark:text-slate-600'}`}
                        disabled={!canBecomeKanban}
                        onClick={() => {
                          if (!canBecomeKanban) return;
                          updateConnection({ connectionType: 'vsm_info', kanbanLoopEnabled: true, vsmInfoTransmission: 'Eletrônico' });
                        }}
                        aria-label="Pull/Kanban"
                      >
                        <Package size={15} />
                        <span className="pointer-events-none absolute -top-8 left-1/2 z-[100000] hidden -translate-x-1/2 whitespace-nowrap rounded bg-slate-900 px-2 py-1 text-[10px] font-semibold text-white shadow group-hover:block">Pull/Kanban</span>
                      </button>
                    </div>
                  ))}

                  {isKanbanType && menuSection('Loop Kanban', (
                    <div className="space-y-2 rounded border border-blue-100 bg-blue-50 p-2 dark:border-blue-800/40 dark:bg-blue-500/10">
                      <label className="text-[10px] font-bold uppercase text-blue-700 dark:text-blue-300">Supermercado / Pulmão</label>
                      <select
                        value={rawConn.kanbanSupermarketId ?? ''}
                        onChange={(event) => {
                          const linkedBufferId = event.target.value || undefined;
                          const linkedBuffer = pullBufferOptions.find(card => card.id === linkedBufferId);
                          const leadTimeDays = Number(rawConn.kanbanLoopLeadTimeDays ?? (linkedBuffer as any)?.vsmFixedLeadTimeDays ?? (linkedBuffer as any)?.vsmBufferDays ?? (linkedBuffer as any)?.vsmDias ?? 1.5) || 1.5;
                          updateConnection({ kanbanSupermarketId: linkedBufferId, kanbanLoopEnabled: true, kanbanLoopLeadTimeDays: leadTimeDays });
                          if (linkedBufferId) {
                            handleUpdateCardData(linkedBufferId, { vsmFixedLeadTimeDays: leadTimeDays, vsmBufferDays: leadTimeDays, inventoryMode: 'coverage' } as any);
                          }
                        }}
                        className="h-7 w-full rounded border border-blue-200 bg-white px-2 text-[11px] font-semibold text-slate-700 dark:border-blue-800/40 dark:bg-slate-900 dark:text-slate-200"
                      >
                        <option value="">Selecionar buffer</option>
                        {pullBufferOptions.map((card) => (
                          <option key={card.id} value={card.id}>{(card as any).atividade || (card as any).nome_fluxo || ((card as any).vsmIcon === 'pulmao' ? 'Pulmão' : 'Supermercado')}</option>
                        ))}
                      </select>
                      <label className="text-[10px] font-bold uppercase text-blue-700 dark:text-blue-300">Capacidade / LT do loop</label>
                      <div className="relative">
                        <input
                          type="number"
                          min="0"
                          step="0.1"
                          value={rawConn.kanbanLoopLeadTimeDays ?? ''}
                          onChange={(event) => updateKanbanLoopLeadTime(parseFloat(event.target.value))}
                          className="h-7 w-full rounded border border-blue-200 bg-white px-2 pr-9 text-right text-[11px] font-semibold text-slate-700 dark:border-blue-800/40 dark:bg-slate-900 dark:text-slate-200"
                          placeholder="1.5"
                        />
                        <span className="absolute right-2 top-1.5 text-[10px] font-semibold text-slate-400">dias</span>
                      </div>
                      <label className="text-[10px] font-bold uppercase text-blue-700 dark:text-blue-300">Tamanho da Bandeja (unidades)</label>
                      <div className="relative">
                        <input
                          type="number"
                          min="1"
                          step="1"
                          value={rawConn.kanbanPackSize ?? 20}
                          onChange={(event) => {
                            const v = Math.max(1, parseInt(event.target.value) || 1);
                            updateConnection({ kanbanPackSize: v });
                            // Propaga para o superCard (V193) para que os ícones de retirada/produção também exibam o número
                            if (rawConn.kanbanSupermarketId) {
                              handleUpdateCardData(rawConn.kanbanSupermarketId, { vsmKanbanPackSize: v } as any);
                            }
                          }}
                          className="h-7 w-full rounded border border-blue-200 bg-white px-2 pr-10 text-right text-[11px] font-semibold text-slate-700 dark:border-blue-800/40 dark:bg-slate-900 dark:text-slate-200"
                          placeholder="20"
                        />
                        <span className="absolute right-2 top-1.5 text-[10px] font-semibold text-slate-400">un</span>
                      </div>
                      {/* V198.0 — Takt Manual: visível somente quando globalTaktTimeSeconds indisponível */}
                      {!globalTaktTimeSeconds ? (
                        <>
                          <label className="text-[10px] font-bold uppercase text-blue-700 dark:text-blue-300">Takt Manual (segundos)</label>
                          <div className="relative">
                            <input
                              type="number"
                              min="1"
                              step="1"
                              value={rawConn.kanbanManualTaktSeconds ?? ''}
                              onChange={(event) => {
                                const v = Math.max(1, parseInt(event.target.value) || 1);
                                updateConnection({ kanbanManualTaktSeconds: v });
                              }}
                              className="h-7 w-full rounded border border-blue-200 bg-white px-2 pr-10 text-right text-[11px] font-semibold text-slate-700 dark:border-blue-800/40 dark:bg-slate-900 dark:text-slate-200"
                              placeholder="ex: 60"
                            />
                            <span className="absolute right-2 top-1.5 text-[10px] font-semibold text-slate-400">s</span>
                          </div>
                        </>
                      ) : (
                        <div className="flex items-center gap-1.5 rounded border border-blue-100 bg-blue-50 px-2 py-1 opacity-60 dark:border-blue-800/40 dark:bg-blue-500/10">
                          <span className="text-[10px] font-bold uppercase text-blue-500 dark:text-blue-300">Takt Global</span>
                          <span className="font-mono text-[11px] font-semibold text-blue-600 dark:text-blue-300">{(globalTaktTimeSeconds / 60).toFixed(1)} min</span>
                        </div>
                      )}
                      {/* Pitch KPI */}
                      {(() => {
                        const packSize = rawConn.kanbanPackSize ?? 20;
                        const activeTakt = globalTaktTimeSeconds || (rawConn.kanbanManualTaktSeconds ?? null);
                        if (!activeTakt || activeTakt <= 0) return null;
                        const pitchMin = ((activeTakt * packSize) / 60).toFixed(1);
                        return (
                          <div className="flex items-center justify-between rounded border border-emerald-200 bg-emerald-50 px-2 py-1.5 dark:border-emerald-800/40 dark:bg-emerald-500/10">
                            <span className="text-[10px] font-bold uppercase text-emerald-700 dark:text-emerald-300">Pitch</span>
                            <span className="font-mono text-[13px] font-bold tracking-tight text-emerald-600 dark:text-emerald-300">{pitchMin} min</span>
                          </div>
                        );
                      })()}
                    </div>
                  ))}

                  {isInfoType && !isKanbanType && menuSection('Transmissão', (
                    <div className="space-y-2">
                      <div className="grid grid-cols-2 gap-1">
                        {(['Manual', 'Eletrônico'] as const).map((m) => (
                          <button key={m}
                            className={`h-7 rounded text-[10px] font-bold uppercase transition-colors ${curTransmission === m ? (m === 'Manual' ? 'bg-slate-700 text-white dark:bg-slate-600' : 'bg-cyan-500 text-white dark:bg-cyan-600') : 'border border-slate-200 text-slate-500 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-800'}`}
                            onClick={() => updateTransmission(m)}>
                            {m === 'Eletrônico' ? '⚡ Eletrônico' : '✋ Manual'}
                          </button>
                        ))}
                      </div>
                      {curTransmission === 'Eletrônico' && (
                        <div className="space-y-1 rounded border border-cyan-100 bg-cyan-50 p-2 dark:border-cyan-800/40 dark:bg-cyan-500/10">
                          <label className="flex items-center gap-2 text-[10px] font-bold uppercase text-cyan-700 dark:text-cyan-300">
                            <input
                              type="checkbox"
                              checked={Boolean(rawConn.kanbanLoopEnabled)}
                              onChange={(event) => updateConnection({ kanbanLoopEnabled: event.target.checked })}
                            />
                            Loop Kanban
                          </label>
                          <select
                            value={rawConn.kanbanSupermarketId ?? ''}
                            onChange={(event) => updateConnection({ kanbanSupermarketId: event.target.value || undefined, kanbanLoopEnabled: Boolean(event.target.value) })}
                            className="h-7 w-full rounded border border-cyan-200 bg-white px-2 text-[11px] font-semibold text-slate-700 dark:border-cyan-800/40 dark:bg-slate-900 dark:text-slate-200"
                          >
                            <option value="">Supermercado posterior</option>
                            {supermarketOptions.map((card) => (
                              <option key={card.id} value={card.id}>{(card as any).atividade || (card as any).nome_fluxo || 'Supermercado'}</option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  ))}

                  {isFifoType && menuSection('FIFO', (
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="mb-1 block text-[10px] font-bold uppercase text-slate-400 dark:text-slate-500">Capacidade</label>
                        <input
                          type="number"
                          min={1}
                          value={rawConn.fifoMaxCapacity ?? 50}
                          onChange={(event) => updateConnection({ fifoMaxCapacity: Number(event.target.value) || 50 })}
                          className="h-7 w-full rounded border border-slate-200 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 px-2 text-right text-xs font-semibold"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-[10px] font-bold uppercase text-slate-400 dark:text-slate-500">WIP</label>
                        <input
                          type="number"
                          min={0}
                          value={rawConn.fifoCurrentWip ?? 0}
                          onChange={(event) => updateConnection({ fifoCurrentWip: Number(event.target.value) || 0 })}
                          className="h-7 w-full rounded border border-slate-200 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 px-2 text-right text-xs font-semibold"
                        />
                      </div>
                    </div>
                  ))}

                  {menuSection('Geometria', (
                    <div className="space-y-1">
                      <button className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-slate-100 dark:hover:bg-slate-800" onClick={() => { setEditingWaypoints(true); setContextMenu(null); }}>
                        <Spline size={14} /> Editar Pontos da Seta
                      </button>
                      <button className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-slate-100 dark:hover:bg-slate-800" onClick={makeRadial}>
                        <RotateCcw size={14} /> Tornar Reta (Radial)
                      </button>
                      <button className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-slate-100 dark:hover:bg-slate-800" onClick={() => addJump(suggestedJumpType)}>
                        {suggestedJumpIcon} {suggestedJumpLabel}
                      </button>
                      {rawConn.manualJumps && rawConn.manualJumps.length > 0 && (
                        <button className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-slate-100 dark:hover:bg-slate-800" onClick={() => { updateConnection({ manualJumps: undefined }); setContextMenu(null); }}>
                          <Eraser size={14} /> Limpar Jumps ({rawConn.manualJumps.length})
                        </button>
                      )}
                    </div>
                  ))}

                  {menuSection('Dados', (
                    <div className="space-y-2">
                      {isTransportType && (
                        <div className="space-y-2 rounded border border-slate-200 bg-slate-50 p-2 dark:border-slate-700 dark:bg-slate-800/60">
                          <div className="flex gap-1">
                            {(['truck', 'ship', 'train'] as const).map(vehicle => (
                              <button
                                key={vehicle}
                                className={`flex-1 rounded border px-2 py-1 text-xs font-semibold ${rawConn.vsmTransportIcon === vehicle || (!rawConn.vsmTransportIcon && vehicle === 'truck') ? 'border-cyan-400 bg-cyan-50 text-cyan-700 dark:border-cyan-400 dark:bg-cyan-500/15 dark:text-cyan-200' : 'border-slate-200 bg-white hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800/60'}`}
                                onClick={() => updateConnection({ vsmTransportIcon: vehicle })}
                              >
                                {vehicle === 'truck' ? 'Truck' : vehicle === 'ship' ? 'Ship' : 'Train'}
                              </button>
                            ))}
                          </div>
                          <div className="flex gap-1">
                            {['Diária', 'Semanal', 'Mensal'].map(freq => (
                              <button
                                key={freq}
                                className={`rounded border px-2 py-1 text-[11px] ${rawConn.frequencia === freq ? 'border-cyan-400 bg-cyan-50 text-cyan-700 dark:border-cyan-400 dark:bg-cyan-500/15 dark:text-cyan-200' : 'border-slate-200 bg-white hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800/60'}`}
                                onClick={() => updateConnection({ frequencia: freq })}
                              >
                                {freq}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      <button className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-slate-100 dark:hover:bg-slate-800" onClick={anchorInfoBox}>
                        <ClipboardList size={14} /> Ancorar Caixa de Dados
                      </button>
                    </div>
                  ))}

                  {menuSection('Ações', (
                    <div className="space-y-1">
                      <button
                        className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm text-red-700 hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-500/15"
                        onClick={() => {
                          const updated = srcCard.conexoes_saida.filter(conn => conn.id !== rawConn.id);
                          handleUpdateCardData(srcCard.id, { conexoes_saida: updated });
                          setSelectedConnection(null);
                          setContextMenu(null);
                        }}
                      >
                        <Trash2 size={14} /> Excluir Conexão
                      </button>
                      <button className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-slate-100 dark:hover:bg-slate-800" onClick={() => { setSelectedConnection(null); setEditingWaypoints(false); setContextMenu(null); }}>
                        <XIcon size={14} /> Cancelar
                      </button>
                    </div>
                  ))}
                </div>
              );
            })()}
            {contextMenu.type === 'canvas' && !canEditCanvas && (
              <div className="w-[220px] rounded-lg bg-white dark:bg-slate-900 p-3 text-slate-800 dark:text-slate-200">
                <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400 dark:text-slate-500">Estado Atual</div>
                <div className="mt-1 text-xs font-semibold text-slate-700 dark:text-slate-300">Use OTIMIZAÇÃO para criar ou editar itens.</div>
                <button className="mt-3 w-full rounded border border-slate-200 dark:border-slate-700 px-2 py-1.5 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800" onClick={() => setContextMenu(null)}>
                  Fechar
                </button>
              </div>
            )}
            {contextMenu.type === 'canvas' && canEditCanvas && (
              <>
                {canCreateContinuousFlowCell && (
                  <>
                    <button
                      className="w-full px-4 py-2 text-left hover:bg-blue-50 flex items-center gap-2 text-sm font-semibold text-blue-700 dark:text-blue-300 dark:hover:bg-blue-500/15"
                      onClick={createContinuousFlowCell}
                    >
                      <Package size={14} />
                      <span>Criar Célula de Fluxo Contínuo</span>
                    </button>
                    <div className="border-t border-gray-200 dark:border-slate-700 my-1"></div>
                  </>
                )}
                {/* Itens Nova Atividade/Decisão/Post-it removidos do menu de contexto do canvas (Office/VSM) */}
                <button
                  className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-slate-800 flex items-center gap-2 text-sm"
                  onClick={() => {
                    const total = selectedCardIds.size + selectedPostItIds.size;
                    if (!window.confirm(`Excluir ${total} item(s) selecionado(s)? Esta ação não pode ser desfeita.`)) {
                      setContextMenu(null);
                      return;
                    }
                    if (selectedCardIds.size > 0) {
                      selectedCardIds.forEach(id => handleDeleteTotal(id));
                      setSelectedCardIds(new Set());
                    }
                    if (selectedPostItIds.size > 0) {
                      // Delete post-its logic here if needed
                      setSelectedPostItIds(new Set());
                    }
                    setContextMenu(null);
                  }}
                  disabled={selectedCardIds.size === 0 && selectedPostItIds.size === 0}
                >
                  <Trash2 size={14} className="text-red-600 dark:text-red-300" />
                  <span>Excluir Selecionados ({selectedCardIds.size + selectedPostItIds.size})</span>
                </button>
                <div className="border-t border-gray-200 dark:border-slate-700 my-1"></div>
                {/* Alinhar à Grade — snap das posições à grade de 20px */}
                <button
                  className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-slate-800 flex items-center gap-2 text-sm"
                  onClick={() => {
                    const GRID = 20;
                    activeCards.forEach((c) => {
                      const nx = Math.round((c.posicao?.x ?? 0) / GRID) * GRID;
                      const ny = Math.round((c.posicao?.y ?? 0) / GRID) * GRID;
                      if (nx !== c.posicao?.x || ny !== c.posicao?.y) {
                        handleUpdateCardData(c.id, { posicao: { x: nx, y: ny } });
                      }
                    });
                    setContextMenu(null);
                  }}
                >
                  <LayoutGrid size={14} />
                  <span className="flex-1">Alinhar à Grade</span>
                  <kbd className="ml-2 text-[10px] font-mono text-slate-400 dark:text-slate-500">Ctrl+Shift+G</kbd>
                </button>
                {/* Centralizar visualização — reset pan/scale para origem */}
                <button
                  className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-slate-800 flex items-center gap-2 text-sm"
                  onClick={() => {
                    setPan({ x: 0, y: 0 });
                    setScale(1);
                    setContextMenu(null);
                  }}
                >
                  <Crosshair size={14} />
                  <span className="flex-1">Centralizar Visualização</span>
                  <kbd className="ml-2 text-[10px] font-mono text-slate-400 dark:text-slate-500">Space</kbd>
                </button>
                {/* Zoom Ajustar à Tela — calcula bbox dos cards e ajusta pan/scale */}
                <button
                  className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-slate-800 flex items-center gap-2 text-sm"
                  onClick={() => {
                    if (activeCards.length === 0) { setContextMenu(null); return; }
                    const rect = canvasRef.current?.getBoundingClientRect();
                    if (!rect) { setContextMenu(null); return; }
                    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
                    activeCards.forEach((c) => {
                      const x = c.posicao?.x ?? 0;
                      const y = c.posicao?.y ?? 0;
                      // largura/altura aproximada — usa fallback se não disponível
                      const w = 240;
                      const h = 160;
                      if (x < minX) minX = x;
                      if (y < minY) minY = y;
                      if (x + w > maxX) maxX = x + w;
                      if (y + h > maxY) maxY = y + h;
                    });
                    const bboxW = Math.max(1, maxX - minX);
                    const bboxH = Math.max(1, maxY - minY);
                    const PAD = 80;
                    const newScale = Math.min(
                      (rect.width  - PAD * 2) / bboxW,
                      (rect.height - PAD * 2) / bboxH,
                      1.2,
                    );
                    const safeScale = Math.max(0.1, Math.min(5, newScale));
                    const cx = (minX + maxX) / 2;
                    const cy = (minY + maxY) / 2;
                    setScale(safeScale);
                    setPan({
                      x: rect.width  / 2 - cx * safeScale,
                      y: rect.height / 2 - cy * safeScale,
                    });
                    setContextMenu(null);
                  }}
                >
                  <Maximize2 size={14} />
                  <span className="flex-1">Ajustar à Tela</span>
                </button>
                <div className="border-t border-gray-200 dark:border-slate-700 my-1"></div>
                {/* Selecionar Tudo — seleciona todos os cards ativos */}
                <button
                  className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-slate-800 flex items-center gap-2 text-sm"
                  onClick={() => {
                    setSelectedCardIds(new Set(activeCards.map((c) => c.id)));
                    setContextMenu(null);
                  }}
                  disabled={activeCards.length === 0}
                >
                  <Layers size={14} />
                  <span className="flex-1">Selecionar Tudo</span>
                  <kbd className="ml-2 text-[10px] font-mono text-slate-400 dark:text-slate-500">Ctrl+A</kbd>
                </button>
                {/* Distribuir Horizontalmente — espaça cards selecionados em intervalos iguais (≥3) */}
                <button
                  className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-slate-800 flex items-center gap-2 text-sm"
                  onClick={() => {
                    const ids = Array.from(selectedCardIds);
                    if (ids.length < 3) { setContextMenu(null); return; }
                    const sel = activeCards
                      .filter((c) => selectedCardIds.has(c.id))
                      .sort((a, b) => (a.posicao?.x ?? 0) - (b.posicao?.x ?? 0));
                    const minX = sel[0].posicao?.x ?? 0;
                    const maxX = sel[sel.length - 1].posicao?.x ?? 0;
                    const step = (maxX - minX) / (sel.length - 1);
                    sel.forEach((c, i) => {
                      if (i === 0 || i === sel.length - 1) return; // mantém extremos
                      const nx = Math.round(minX + step * i);
                      const y = c.posicao?.y ?? 0;
                      handleUpdateCardData(c.id, { posicao: { x: nx, y } });
                    });
                    setContextMenu(null);
                  }}
                  disabled={selectedCardIds.size < 3}
                >
                  <MoveHorizontal size={14} />
                  <span className="flex-1">Distribuir Horizontalmente</span>
                  <span className="ml-2 text-[10px] text-slate-400 dark:text-slate-500">{selectedCardIds.size < 3 ? '≥3' : selectedCardIds.size}</span>
                </button>
                {/* Ir para Fornecedor — pan para o card fornecedor / card_inicial */}
                <button
                  className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-slate-800 flex items-center gap-2 text-sm"
                  onClick={() => {
                    const target = activeCards.find((c: any) => {
                      const ext = String(c.extType ?? '').toUpperCase();
                      return c.id === 'ext-fornecedor' || ext === 'FORNECEDOR' || c.tipo === 'card_inicial' || c.vsmIcon === 'fornecedor';
                    });
                    const rect = canvasRef.current?.getBoundingClientRect();
                    if (target && rect) {
                      const tx = target.posicao?.x ?? 0;
                      const ty = target.posicao?.y ?? 0;
                      setPan({ x: rect.width / 2 - (tx + 120) * scale, y: rect.height / 2 - (ty + 80) * scale });
                    }
                    setContextMenu(null);
                  }}
                >
                  <Truck size={14} />
                  <span className="flex-1">Ir para Fornecedor</span>
                </button>
                {/* Ir para Cliente — pan para o card cliente / card_final */}
                <button
                  className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-slate-800 flex items-center gap-2 text-sm"
                  onClick={() => {
                    const target = activeCards.find((c: any) => {
                      const ext = String(c.extType ?? '').toUpperCase();
                      return c.id === 'ext-cliente' || ext === 'CLIENTE' || c.tipo === 'card_final' || c.vsmIcon === 'cliente';
                    });
                    const rect = canvasRef.current?.getBoundingClientRect();
                    if (target && rect) {
                      const tx = target.posicao?.x ?? 0;
                      const ty = target.posicao?.y ?? 0;
                      setPan({ x: rect.width / 2 - (tx + 120) * scale, y: rect.height / 2 - (ty + 80) * scale });
                    }
                    setContextMenu(null);
                  }}
                >
                  <ArrowRight size={14} />
                  <span className="flex-1">Ir para Cliente</span>
                </button>
                <div className="border-t border-gray-200 dark:border-slate-700 my-1"></div>
                {/* Alternar Lead Time Ladder — dispara evento ouvido pela MapaPage */}
                <button
                  className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-slate-800 flex items-center gap-2 text-sm"
                  onClick={() => {
                    window.dispatchEvent(new CustomEvent('vsm:toggleLadder'));
                    setContextMenu(null);
                  }}
                >
                  <Spline size={14} />
                  <span className="flex-1">Mostrar/Ocultar Lead Time Ladder</span>
                </button>
              </>
            )}
            </div>
          </>,
          document.body
        )}
      </div>

      {/* Modal de Compara+�+�o - M+�tricas Atual vs Futuro (Kaizen) */}
      {showComparisonModal && (() => {
        const { current, future } = getFutureMetrics();
        return (
          <ComparisonView
            isOpen={showComparisonModal}
            onClose={() => setShowComparisonModal(false)}
            current={current}
            future={future}
          />
        );
      })()}

        {/* Toolbar de Agrupamento - desativado no modo FACTORY/classic */}
      {currentLayout !== 'classic' && (
        <GroupingToolbar
          selectionCount={selectedCardIds.size}
          canGroup={canGroupCards}
          onGroup={handleGroupCards}
          onClearSelection={() => {
            setSelectedCardIds(new Set());
            onClearSelection?.();
          }}
          isMultiSelectMode={isMultiSelectMode}
          onToggleMultiSelect={setIsMultiSelectMode}
          selectedCards={selectedCardsForGrouping}
          allCards={activeCards}
          onUpdateCards={setCards}
          onSwimlanesApplied={onSwimlanesApplied}
        />
      )}

      {/* Highlight de drop target ao arrastar card sobre outro */}
      {cardDropTargetId && (() => {
        const tgt = activeCards.find(c => c.id === cardDropTargetId);
        if (!tgt) return null;
        const isBefore = cardDropSide === 'before';
        const color = isBefore ? '#f59e0b' : '#2563eb';
        const tw = tgt.tipo === 'decisao' ? 160 : (tgt.calculatedWidth || 240);
        const th = tgt.tipo === 'decisao' ? 160 : 180;
        const tgtIsInitialFinal = tgt.tipo === 'card_inicial' || tgt.tipo === 'card_final';
        const tgtIsDecision = tgt.tipo === 'decisao';
        const offset = tw + 80;

        const landX = isBefore ? tgt.posicao.x : tgt.posicao.x + offset;
        const landY = tgt.posicao.y;
        // Ghost uses atividade dimensions for all types (including decision) so the
        // inserted card preview is always a rectangular placeholder and arrowStartY
        // naturally lands at y+90 � which is also the decision diamond's visual center.
        const ghostW = tgtIsInitialFinal ? tw : 240;
        const ghostH = 180;

        const arrowStartX = isBefore ? landX + ghostW : tgt.posicao.x + tw;
        const arrowStartY = landY + ghostH / 2;
        const arrowEndX   = isBefore ? tgt.posicao.x : landX;
        // Decision card renders at pos.y+10 (Card.tsx), visual center is y+90
        const arrowEndY   = tgtIsDecision ? tgt.posicao.y + 90 : tgt.posicao.y + th / 2;
        const needsOrtho  = Math.abs(arrowStartY - arrowEndY) > 10;
        const arrowPath   = needsOrtho
          ? `M ${arrowStartX} ${arrowStartY} L ${arrowStartX + (arrowEndX - arrowStartX) / 2} ${arrowStartY} L ${arrowStartX + (arrowEndX - arrowStartX) / 2} ${arrowEndY} L ${arrowEndX} ${arrowEndY}`
          : `M ${arrowStartX} ${arrowStartY} L ${arrowEndX} ${arrowEndY}`;

        const label = isBefore ? '\u2190 inserir antes' : 'inserir depois \u2192';

        // Ghost shape por tipo (decisao usa estilo atividade � mostra o card a ser inserido, n�o o target)
        const renderGhost = () => {
          if (tgtIsInitialFinal) {
            return (
              <div className="pointer-events-none absolute transition-all duration-100"
                style={{ left: landX, top: landY, width: ghostW, height: ghostH, zIndex: 9998, opacity: 0.6 }}>
                <div className="absolute inset-0 flex items-center justify-center"
                  style={{
                    border: `2px dashed ${color}`,
                    backgroundColor: `${color}08`,
                    borderRadius: 9999,
                  }}>
                  <div className="text-[11px] font-semibold" style={{ color }}>{label}</div>
                </div>
              </div>
            );
          }
          // Atividade (retangular com cantos arredondados)
          return (
            <div className="pointer-events-none absolute transition-all duration-100"
              style={{ left: landX, top: landY, width: ghostW, height: ghostH, zIndex: 9998, opacity: 0.6 }}>
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-1"
                style={{
                  border: `2px dashed ${color}`,
                  backgroundColor: `${color}08`,
                  borderRadius: 12,
                }}>
                <div className="text-[11px] font-semibold" style={{ color }}>{label}</div>
              </div>
            </div>
          );
        };

        // Borda de destaque no target por tipo
        const renderTargetHighlight = () => {
          if (tgtIsDecision) {
            // SVG polygon traces the exact decision diamond shape
            // Card.tsx: decisao renders at top: pos.y + 10
            // Diamond points exactly hit corners of the 160�160 bbox
            return (
              <svg className="pointer-events-none absolute overflow-visible"
                style={{ left: tgt.posicao.x, top: tgt.posicao.y + 10, width: 160, height: 160, zIndex: 9996 }}>
                <polygon
                  points="80,0 160,80 80,160 0,80"
                  fill={`${color}10`}
                  stroke={color}
                  strokeWidth="2.5"
                  style={{ filter: `drop-shadow(0 0 8px ${color}80)` }}
                />
              </svg>
            );
          }
          return (
            <div className="pointer-events-none absolute"
              style={{
                left: tgt.posicao.x - 3, top: tgt.posicao.y - 3,
                width: tw + 6, height: th + 6,
                // card uses rounded-xl (12px); outer highlight expands 3px ? 12 + 3 = 15px
                borderRadius: tgtIsInitialFinal ? 9999 : 15,
                border: `2.5px solid ${color}`,
                backgroundColor: `${color}10`,
                filter: `drop-shadow(0 0 8px ${color}80)`,
                zIndex: 9996,
                transition: 'all 0.15s ease-out',
              }} />
          );
        };

        return (
          // Aplicar mesma transform do canvas-content para usar coordenadas de canvas
          <div className="absolute inset-0 origin-top-left pointer-events-none"
            style={{ transform: `translate3d(${pan.x}px, ${pan.y}px, 0) scale(${scale * scaleFactor})`, zIndex: 9990 }}>

            {/* Seta de conexao */}
            <svg className="pointer-events-none absolute overflow-visible"
              style={{ zIndex: 9997, left: 0, top: 0, width: '10000px', height: '10000px' }}>
              <defs>
                <marker id="card-drag-arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                  <polygon points="0 0, 10 3.5, 0 7" fill={color} />
                </marker>
              </defs>
              <path d={arrowPath} fill="none" stroke={color} strokeWidth={1.5}
                strokeDasharray="6 3" markerEnd="url(#card-drag-arrowhead)" opacity={0.5} />
            </svg>

            {renderGhost()}
            {renderTargetHighlight()}

            {/* Indicador lateral (apenas para cards retangulares, n�o para ovais) */}
            {!tgtIsInitialFinal && !tgtIsDecision && (
              <div className="pointer-events-none absolute"
                style={{
                  left: isBefore ? tgt.posicao.x - 5 : tgt.posicao.x + tw + 1,
                  top: tgt.posicao.y + 12,
                  width: 3, height: th - 24,
                  borderRadius: 3,
                  background: color,
                  boxShadow: `0 0 8px ${color}60`,
                  zIndex: 9999,
                  transition: 'all 0.15s ease-out',
                }} />
            )}
          </div>
        );
      })()}

      {/* Preview de Inser��o entre Cards */}
      {insertionPreview && (
        <div
          className="absolute pointer-events-none z-[200]"
          style={{
            left: insertionPreview.x * scale + pan.x,
            top: insertionPreview.y * scale + pan.y,
            transform: 'translate(-50%, -50%)'
          }}
        >
          {/* Linha indicadora vertical */}
          <div className="absolute left-1/2 -translate-x-1/2 w-1 h-24 -top-12 bg-gradient-to-b from-transparent via-blue-500 to-transparent animate-pulse" />
          
          {/* Badge com ID sugerido */}
          <div className="bg-blue-500 text-white px-3 py-2 rounded-lg shadow-xl text-sm font-bold whitespace-nowrap animate-bounce">
            ID sugerido: {insertionPreview.suggestedId}
          </div>
          
          {/* Seta indicadora */}
          <div className="absolute left-1/2 -translate-x-1/2 -bottom-2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[8px] border-l-transparent border-r-transparent border-t-blue-500" />
        </div>
      )}

    </div>
  );
};

export default React.memo(VsmCanvas);

